/* Generated by scripts/generate-dcs-tools.ts
 * *DO NOT EDIT MANUALLY*
 */
import { z } from 'zod/v3';

export const AddSharedCalendarEvent = z.object({
  eventId: z.string().uuid(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const ToolCalendarEvent = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  start: z.string(),
  end: z.string(),
  isAllDay: z.boolean(),
  timeZone: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  status: z.string(),
  isRecurring: z.boolean(),
  recurrenceLines: z.array(z.string()),
  attendees: z.array(
    z.object({
      email: z.string(),
      responseStatus: z.string(),
      isOrganizer: z.boolean(),
      isOptional: z.boolean(),
    })
  ),
  attendeeCount: z.number().int().gte(0),
  organizerEmail: z.union([z.string(), z.null()]).optional(),
  conferenceUrl: z.union([z.string(), z.null()]).optional(),
  macroCallUrl: z.union([z.string(), z.null()]).optional(),
  isReadOnly: z.boolean(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  calendarName: z.union([z.string(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  myRole: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('organizer'), z.literal('guest')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const AddToPipeline = z.object({
  pipelineId: z.string().uuid(),
  entries: z.array(
    z.object({
      recordId: z.union([z.string().uuid(), z.null()]).optional(),
      email: z.union([z.string(), z.null()]).optional(),
      set: z
        .array(
          z.object({
            field: z.string(),
            value: z.union([z.string(), z.null()]).optional(),
            values: z.union([z.array(z.string()), z.null()]).optional(),
          })
        )
        .optional(),
    })
  ),
  allowDuplicates: z.union([z.boolean(), z.null()]).optional(),
});

export const AddToPipelineResponse = z.object({
  added: z.array(
    z.object({
      entryId: z.string().uuid(),
      record: z.object({
        id: z.string().uuid(),
        name: z.union([z.string(), z.null()]).optional(),
        detail: z.union([z.string(), z.null()]).optional(),
      }),
      fields: z.record(z.any()),
    })
  ),
  alreadyPresent: z.array(
    z.object({
      entryId: z.string().uuid(),
      record: z.object({
        id: z.string().uuid(),
        name: z.union([z.string(), z.null()]).optional(),
        detail: z.union([z.string(), z.null()]).optional(),
      }),
      fields: z.record(z.any()),
    })
  ),
  version: z.union([z.number().int(), z.null()]).optional(),
  summary: z.string(),
});

export const ApplyCanvasEdits = z.object({
  document_id: z.string(),
  expected_revision: z.string(),
  code: z.string(),
  snippets: z.union([z.record(z.string()), z.null()]).optional(),
});

export const CanvasResponse = z.object({
  render: z
    .union([
      z.object({
        revision: z.string(),
        clock: z.record(z.number().int().gte(0)),
        crop: z.object({
          x: z.number(),
          y: z.number(),
          width: z.number(),
          height: z.number(),
        }),
        width: z.number().int().gte(0),
        height: z.number().int().gte(0),
        objects: z.array(
          z.object({
            id: z.string(),
            bounds: z.object({
              x: z.number(),
              y: z.number(),
              width: z.number(),
              height: z.number(),
            }),
          })
        ),
        unavailable: z.array(z.object({ id: z.string(), reason: z.string() })),
        textLayout: z
          .array(
            z.object({
              id: z.string(),
              bounds: z.object({
                x: z.number(),
                y: z.number(),
                width: z.number(),
                height: z.number(),
              }),
              overflow: z.boolean(),
            })
          )
          .optional(),
        modelMedia: z.array(
          z.object({
            type: z.string(),
            mediaType: z.string(),
            data: z.string(),
          })
        ),
      }),
      z.null(),
    ])
    .optional(),
  canvas: z.union([z.string(), z.null()]).optional(),
  operationsApplied: z.number().int().gte(0),
  summary: z.string(),
  clarification: z.union([z.string(), z.null()]).optional(),
});

export const BashCodeExecution = z.object({ input: z.string() });

export const BashCodeExecutionResponse = z.object({
  tool_use_id: z.string(),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          stdout: z.string(),
          stderr: z.string(),
          return_code: z.number().int(),
          content: z
            .union([z.array(z.object({ file_id: z.string() })), z.null()])
            .optional(),
        }),
        z.object({ type: z.literal('bash_code_execution_result') })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unavailable'),
              z.literal('execution_time_exceeded'),
              z.literal('container_expired'),
              z.literal('invalid_input'),
              z.literal('too_many_requests'),
              z.literal('file_not_found'),
              z.literal('string_not_found'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({ type: z.literal('bash_code_execution_tool_result_error') })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const BulkSetEntityPropertyOptions = z.object({
  entities: z.array(
    z.object({
      entity_type: z.enum([
        'document',
        'initiative',
        'project',
        'thread',
        'channel',
        'call',
        'user',
        'company',
        'chat',
      ]),
      entity_id: z.string(),
    })
  ),
  property_definition_id: z.string().uuid(),
  add_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  remove_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
});

export const BulkSetEntityPropertyOptionsResponse = z.object({
  results: z.array(
    z.object({
      entityType: z.string(),
      entityId: z.string(),
      status: z.string(),
      error: z.union([z.string(), z.null()]).optional(),
    })
  ),
  summary: z.string(),
});

export const CalculateSpreadsheet = z.object({
  documentId: z.string(),
  sheetId: z.union([z.string(), z.null()]).optional(),
  formulas: z.array(
    z
      .object({
        label: z.union([z.string(), z.null()]).optional(),
        formula: z.string(),
      })
      .strict()
  ),
  overrides: z
    .union([
      z.array(
        z
          .object({
            sheetId: z.string(),
            cells: z.array(
              z.object({ address: z.string(), value: z.string() }).strict()
            ),
          })
          .strict()
      ),
      z.null(),
    ])
    .optional(),
});

export const SpreadsheetResponse = z.any().superRefine((x, ctx) => {
  const schemas = [
    z.object({
      revision: z.string(),
      sheets: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          rowCount: z.number().int().gte(0),
          columnCount: z.number().int().gte(0),
          usedRange: z.union([z.string(), z.null()]).optional(),
          populatedCells: z.number().int().gte(0),
          formulaCells: z.number().int().gte(0),
          errorCells: z.number().int().gte(0),
          tables: z
            .array(
              z.object({
                name: z.string(),
                range: z.string(),
                columns: z.array(z.string()),
                totalsRow: z.boolean(),
                filteredColumns: z.array(z.string()).optional(),
                columnTypes: z
                  .array(
                    z.object({
                      column: z.string(),
                      type: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('text'),
                          z.literal('number'),
                          z.literal('percent'),
                          z.literal('currency'),
                          z.literal('date'),
                          z.literal('datetime'),
                          z.literal('time'),
                          z.literal('checkbox'),
                          z.literal('dropdown'),
                          z.literal('person'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                    })
                  )
                  .optional(),
              })
            )
            .optional(),
        })
      ),
      ranges: z.array(
        z.object({
          sheetId: z.string(),
          sheetName: z.string(),
          range: z.string(),
          cells: z.array(
            z.object({
              address: z.string(),
              source: z.string(),
              formula: z.union([z.string(), z.null()]).optional(),
              style: z
                .union([
                  z
                    .object({
                      numberFormat: z.union([z.string(), z.null()]).optional(),
                      fontName: z.union([z.string(), z.null()]).optional(),
                      borderTopStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderTopColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      borderRightStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderRightColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      borderBottomStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderBottomColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      borderLeftStyle: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal(''),
                              z.literal('thin'),
                              z.literal('medium'),
                              z.literal('thick'),
                              z.literal('double'),
                              z.literal('dotted'),
                              z.literal('dashed'),
                              z.literal('dashDot'),
                              z.literal('dashDotDot'),
                              z.literal('slantDashDot'),
                              z.literal('hair'),
                              z.literal('mediumDashed'),
                              z.literal('mediumDashDot'),
                              z.literal('mediumDashDotDot'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      borderLeftColor: z
                        .union([z.string(), z.null()])
                        .optional(),
                      bold: z.union([z.boolean(), z.null()]).optional(),
                      italic: z.union([z.boolean(), z.null()]).optional(),
                      underline: z.union([z.boolean(), z.null()]).optional(),
                      strikethrough: z
                        .union([z.boolean(), z.null()])
                        .optional(),
                      fontFamily: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('sans'),
                              z.literal('serif'),
                              z.literal('mono'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      fontSize: z
                        .union([z.number().int().gte(0).lte(255), z.null()])
                        .optional(),
                      textColor: z.union([z.string(), z.null()]).optional(),
                      fillColor: z.union([z.string(), z.null()]).optional(),
                      horizontalAlign: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('auto'),
                              z.literal('left'),
                              z.literal('center'),
                              z.literal('right'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      indent: z
                        .union([z.number().int().gte(0).lte(255), z.null()])
                        .optional(),
                      verticalAlign: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('top'),
                              z.literal('middle'),
                              z.literal('bottom'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      wrap: z.union([z.boolean(), z.null()]).optional(),
                      borderTop: z.union([z.boolean(), z.null()]).optional(),
                      borderRight: z.union([z.boolean(), z.null()]).optional(),
                      borderBottom: z.union([z.boolean(), z.null()]).optional(),
                      borderLeft: z.union([z.boolean(), z.null()]).optional(),
                      decimals: z
                        .union([z.number().int().gte(-128).lte(127), z.null()])
                        .optional(),
                      format: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('general'),
                              z.literal('number'),
                              z.literal('currency'),
                              z.literal('accounting'),
                              z.literal('percent'),
                              z.literal('date'),
                              z.literal('time'),
                              z.literal('scientific'),
                              z.literal('text'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      checkbox: z.union([z.boolean(), z.null()]).optional(),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              type: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('blank'),
                  z.literal('number'),
                  z.literal('text'),
                  z.literal('boolean'),
                  z.literal('error'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              value: z.any(),
              display: z.string(),
              error: z.union([z.string(), z.null()]).optional(),
            })
          ),
          validations: z
            .array(
              z.object({
                range: z.string(),
                type: z.string(),
                items: z.union([z.array(z.string()), z.null()]).optional(),
                source: z.union([z.string(), z.null()]).optional(),
                operator: z.union([z.string(), z.null()]).optional(),
                formulas: z.union([z.array(z.string()), z.null()]).optional(),
                dropdown: z.union([z.boolean(), z.null()]).optional(),
                rejectInvalid: z.boolean(),
                colors: z
                  .union([
                    z.array(
                      z.object({ item: z.string(), color: z.string() }).strict()
                    ),
                    z.null(),
                  ])
                  .optional(),
              })
            )
            .optional(),
          truncated: z.boolean(),
        })
      ),
      warnings: z.array(z.string()),
      action: z.literal('read'),
    }),
    z.object({
      revision: z.string(),
      results: z.array(
        z.object({
          label: z.union([z.string(), z.null()]).optional(),
          formula: z.string(),
          type: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('blank'),
              z.literal('number'),
              z.literal('text'),
              z.literal('boolean'),
              z.literal('error'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          value: z.any(),
          display: z.string(),
          error: z.union([z.string(), z.null()]).optional(),
        })
      ),
      warnings: z.array(z.string()),
      action: z.literal('calculate'),
    }),
    z.object({
      revision: z.string(),
      applied: z.boolean(),
      changes: z.array(
        z.object({
          type: z.string(),
          sheetId: z.string(),
          summary: z.string(),
          range: z.union([z.string(), z.null()]).optional(),
        })
      ),
      sheets: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          rowCount: z.number().int().gte(0),
          columnCount: z.number().int().gte(0),
          usedRange: z.union([z.string(), z.null()]).optional(),
          populatedCells: z.number().int().gte(0),
          formulaCells: z.number().int().gte(0),
          errorCells: z.number().int().gte(0),
          tables: z
            .array(
              z.object({
                name: z.string(),
                range: z.string(),
                columns: z.array(z.string()),
                totalsRow: z.boolean(),
                filteredColumns: z.array(z.string()).optional(),
                columnTypes: z
                  .array(
                    z.object({
                      column: z.string(),
                      type: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('text'),
                          z.literal('number'),
                          z.literal('percent'),
                          z.literal('currency'),
                          z.literal('date'),
                          z.literal('datetime'),
                          z.literal('time'),
                          z.literal('checkbox'),
                          z.literal('dropdown'),
                          z.literal('person'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                    })
                  )
                  .optional(),
              })
            )
            .optional(),
        })
      ),
      warnings: z.array(z.string()),
      action: z.literal('edit'),
    }),
  ];
  const errors = schemas.reduce<z.ZodError[]>(
    (errors, schema) =>
      ((result) => (result.error ? [...errors, result.error] : errors))(
        schema.safeParse(x)
      ),
    []
  );
  if (schemas.length - errors.length !== 1) {
    ctx.addIssue({
      path: ctx.path,
      code: 'invalid_union',
      unionErrors: errors,
      message: 'Invalid input: Should pass single schema',
    });
  }
});

export const ChangeBooking = z
  .object({
    bookingId: z.string().uuid(),
    change: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ action: z.literal('approve') }).strict(),
        z.object({ action: z.literal('cancel') }).strict(),
        z
          .object({
            attendance: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('unknown'),
                z.literal('attended'),
                z.literal('guestNoShow'),
                z.literal('hostNoShow'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            action: z.literal('attendance'),
          })
          .strict(),
        z
          .object({
            startsAt: z.string().datetime({ offset: true }),
            action: z.literal('reschedule'),
          })
          .strict(),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
  })
  .strict();

export const Booking = z.object({
  id: z.string().uuid(),
  eventTypeId: z.string().uuid(),
  title: z.string(),
  name: z.string(),
  email: z.string(),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  timeZone: z.string(),
  hosts: z.array(z.string()),
  status: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('pending'),
      z.literal('confirmed'),
      z.literal('cancelled'),
      z.literal('processing'),
      z.literal('failed'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  attendance: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('unknown'),
          z.literal('attended'),
          z.literal('guestNoShow'),
          z.literal('hostNoShow'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('unknown')
    )
    .optional(),
  rescheduleCount: z.number().int().gte(0).lte(65535).optional(),
  rescheduledAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  location: z.string(),
  answers: z.record(z.string()),
  questionLabels: z.record(z.string()).optional(),
});

export const Comment = z.object({
  entityType: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('initiative'),
          z.literal('crm_company'),
          z.literal('crm_contact'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  entityId: z.union([z.string(), z.null()]).optional(),
  threadId: z.union([z.string().uuid(), z.null()]).optional(),
  content: z.string(),
});

export const CommentResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('initiative'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string(),
  threadId: z.string().uuid(),
  commentId: z.string().uuid(),
});

export const CommentOnText = z.object({
  documentId: z.string().uuid(),
  content: z.string(),
  quote: z.string(),
  occurrence: z.union([z.number().int().gte(1), z.null()]).optional(),
});

export const CommentOnTextResponse = z.object({
  documentId: z.string().uuid(),
  threadId: z.string().uuid(),
  commentId: z.string().uuid(),
  markedText: z.string(),
});

export const ConfigureAgent = z.object({
  botId: z.string().uuid(),
  instructions: z.union([z.string(), z.null()]).optional(),
  harness: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('in-memory'),
          z.literal('cursor'),
          z.literal('macrod'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  harnessId: z.union([z.string().uuid(), z.null()]).optional(),
  defaultModel: z.union([z.string(), z.null()]).optional(),
  channelScope: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('all'), z.literal('selected')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  channelIds: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  mcpScope: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('owner_connections'), z.literal('selected')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  mcpServers: z
    .union([
      z.array(z.object({ appSlug: z.string(), serverName: z.string() })),
      z.null(),
    ])
    .optional(),
  autoAcceptPermissions: z.union([z.boolean(), z.null()]).optional(),
  isCoding: z.union([z.boolean(), z.null()]).optional(),
});

export const ConfigureAgentResponse = z.object({
  agent: z.object({
    bot: z.object({
      botId: z.string().uuid(),
      owner: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({ user_id: z.string(), type: z.literal('user') }),
          z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      name: z.string(),
      handle: z.string(),
      description: z.union([z.string(), z.null()]).optional(),
      avatarUrl: z.union([z.string(), z.null()]).optional(),
      hasAgent: z.boolean(),
    }),
    instructions: z.string(),
    harness: z.string(),
    harnessId: z.union([z.string().uuid(), z.null()]).optional(),
    defaultModel: z.string(),
    channelScope: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('all'), z.literal('selected')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    channelIds: z.array(z.string().uuid()),
    mcpScope: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('owner_connections'), z.literal('selected')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    mcpServers: z.array(
      z.object({ appSlug: z.string(), serverName: z.string() })
    ),
    autoAcceptPermissions: z.union([z.boolean(), z.null()]).optional(),
    isCoding: z.boolean(),
  }),
  summary: z.string(),
});

export const ConfigureBot = z.object({
  botId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  handle: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  avatarUrl: z.union([z.string(), z.null()]).optional(),
  hasAgent: z.union([z.boolean(), z.null()]).optional(),
});

export const ConfigureBotResponse = z.object({
  bot: z.object({
    botId: z.string().uuid(),
    owner: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ user_id: z.string(), type: z.literal('user') }),
        z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    name: z.string(),
    handle: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    avatarUrl: z.union([z.string(), z.null()]).optional(),
    hasAgent: z.boolean(),
  }),
  summary: z.string(),
});

export const ConnectMcpServer = z.object({
  url: z.string(),
  serverName: z.string(),
});

export const ConnectMcpServerResponse = z.object({
  url: z.string(),
  serverName: z.string(),
  alreadyConnected: z.boolean(),
  authenticated: z.boolean(),
  enabled: z.boolean(),
  summary: z.string(),
});

export const ContentSearch = z.object({
  query: z.string(),
  matchType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('partial'), z.literal('exact')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('partial')
    )
    .optional(),
  entityTypes: z
    .array(
      z.enum([
        'agent_sessions',
        'documents',
        'emails',
        'channels',
        'projects',
        'call_records',
        'calendar_events',
        'chats',
      ])
    )
    .optional(),
  agentSessionIds: z.array(z.string().uuid()).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const SearchToolResponse = z.object({
  results: z.array(
    z
      .object({
        tags: z
          .array(
            z.object({
              label: z.string(),
              scope: z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
            })
          )
          .optional(),
        summary: z.union([z.string(), z.null()]).optional(),
      })
      .and(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      project_id: z.union([z.string(), z.null()]).optional(),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                document_id: z.string().uuid(),
                document_name: z.string(),
                file_type: z.union([z.string(), z.null()]).optional(),
                sub_type: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('task'),
                        z.literal('snippet'),
                        z.literal('skill'),
                        z.literal('initiative_description'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                document_search_results: z.array(
                  z.object({
                    node_id: z.union([z.string(), z.null()]).optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    raw_content: z.union([z.string(), z.null()]).optional(),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('document') })
            ),
            z.intersection(
              z.object({
                created_at: z.string().datetime({ offset: true }),
                updated_at: z.string().datetime({ offset: true }),
                viewed_at: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                snippet: z.union([z.string(), z.null()]).optional(),
                is_read: z.boolean(),
                inbox_visible: z.boolean(),
                is_draft: z.boolean(),
                is_important: z.boolean(),
                id: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                owner_id: z.string(),
                subject: z.union([z.string(), z.null()]).optional(),
                thread_id: z.string().uuid(),
                user_id: z.string(),
                link_id: z.string().uuid(),
                email_message_search_results: z.array(
                  z.object({
                    message_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    sender: z.string(),
                    pretty_sender: z.string(),
                    recipients: z.array(z.string()),
                    cc: z.array(z.string()),
                    bcc: z.array(z.string()),
                    labels: z.array(z.string()),
                    sent_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
                participants: z.array(
                  z.object({
                    email: z.string(),
                    name: z.union([z.string(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('email') })
            ),
            z.intersection(
              z.object({
                id: z.string().uuid(),
                owner_id: z.union([z.string(), z.null()]).optional(),
                channel_type: z.string(),
                channel_id: z.string().uuid(),
                message_id: z.string().uuid(),
                thread_id: z.union([z.string().uuid(), z.null()]).optional(),
                sender_id: z.string(),
                created_at: z.string().datetime({ offset: true }),
                updated_at: z.string().datetime({ offset: true }),
                deleted_at: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                highlight: z.object({
                  name: z.union([z.string(), z.null()]).optional(),
                  content: z.array(z.string()).optional(),
                  user_id: z.union([z.string(), z.null()]).optional(),
                  sender: z.union([z.string(), z.null()]).optional(),
                  recipients: z.array(z.string()).optional(),
                  cc: z.array(z.string()).optional(),
                  bcc: z.array(z.string()).optional(),
                }),
                score: z.union([z.number(), z.null()]).optional(),
              }),
              z.object({ type: z.literal('channelMessage') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      interacted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                owner_id: z.union([z.string(), z.null()]).optional(),
                channel_type: z.string(),
                channel_id: z.string().uuid(),
                highlight: z.object({
                  name: z.union([z.string(), z.null()]).optional(),
                  content: z.array(z.string()).optional(),
                  user_id: z.union([z.string(), z.null()]).optional(),
                  sender: z.union([z.string(), z.null()]).optional(),
                  recipients: z.array(z.string()).optional(),
                  cc: z.array(z.string()).optional(),
                  bcc: z.array(z.string()).optional(),
                }),
                score: z.union([z.number(), z.null()]).optional(),
              }),
              z.object({ type: z.literal('channel') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_at: z.string().datetime({ offset: true }),
                      updated_at: z.string().datetime({ offset: true }),
                      viewed_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                      parent_project_id: z
                        .union([z.string(), z.null()])
                        .optional(),
                      deleted_at: z
                        .union([
                          z.string().datetime({ offset: true }),
                          z.null(),
                        ])
                        .optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                updated_at: z.string().datetime({ offset: true }),
                created_at: z.string().datetime({ offset: true }),
                project_search_results: z.array(
                  z.object({
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('project') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      created_by: z.string(),
                      started_at: z.string().datetime({ offset: true }),
                      ended_at: z.string().datetime({ offset: true }),
                      duration_ms: z.number().int(),
                      updated_at: z.string().datetime({ offset: true }),
                      channel_name: z.union([z.string(), z.null()]).optional(),
                      status: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('ATTENDED'),
                          z.literal('MISSED'),
                          z.literal('UNATTENDED'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      attended: z.boolean(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                owner_id: z.string(),
                call_id: z.string().uuid(),
                channel_id: z.union([z.string().uuid(), z.null()]).optional(),
                participant_ids: z.array(z.string()),
                call_search_results: z.array(
                  z.object({
                    transcript_id: z
                      .union([z.string().uuid(), z.null()])
                      .optional(),
                    speaker_id: z.union([z.string(), z.null()]).optional(),
                    sequence_num: z
                      .union([z.number().int(), z.null()])
                      .optional(),
                    started_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    ended_at: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('call') })
            ),
            z.intersection(
              z.object({
                id: z.string().uuid(),
                teamId: z.string().uuid(),
                name: z.union([z.string(), z.null()]).optional(),
                nameHighlighted: z.union([z.string(), z.null()]).optional(),
                description: z.union([z.string(), z.null()]).optional(),
                hidden: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                domains: z.array(
                  z.object({
                    id: z.string().uuid(),
                    companyId: z.string().uuid(),
                    domain: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              }),
              z.object({ type: z.literal('company') })
            ),
            z.intersection(
              z.object({
                metadata: z
                  .union([
                    z.object({
                      createdAt: z.string().datetime({ offset: true }),
                      updatedAt: z.string().datetime({ offset: true }),
                      status: z.string(),
                      time: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.object({
                            startsAt: z.string().datetime({ offset: true }),
                            endsAt: z.string().datetime({ offset: true }),
                            timeZone: z
                              .union([z.string(), z.null()])
                              .optional(),
                            kind: z.literal('timed'),
                          }),
                          z.object({
                            startDate: z.string().date(),
                            endDate: z.string().date(),
                            kind: z.literal('allDay'),
                          }),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      isRecurring: z.boolean(),
                      occurrence: z
                        .union([
                          z.object({
                            occurrenceKey: z.string(),
                            time: z.any().superRefine((x, ctx) => {
                              const schemas = [
                                z.object({
                                  startsAt: z
                                    .string()
                                    .datetime({ offset: true }),
                                  endsAt: z.string().datetime({ offset: true }),
                                  timeZone: z
                                    .union([z.string(), z.null()])
                                    .optional(),
                                  kind: z.literal('timed'),
                                }),
                                z.object({
                                  startDate: z.string().date(),
                                  endDate: z.string().date(),
                                  kind: z.literal('allDay'),
                                }),
                              ];
                              const errors = schemas.reduce<z.ZodError[]>(
                                (errors, schema) =>
                                  ((result) =>
                                    result.error
                                      ? [...errors, result.error]
                                      : errors)(schema.safeParse(x)),
                                []
                              );
                              if (schemas.length - errors.length !== 1) {
                                ctx.addIssue({
                                  path: ctx.path,
                                  code: 'invalid_union',
                                  unionErrors: errors,
                                  message:
                                    'Invalid input: Should pass single schema',
                                });
                              }
                            }),
                          }),
                          z.null(),
                        ])
                        .optional(),
                      conferenceUrl: z.union([z.string(), z.null()]).optional(),
                      isReadOnly: z.boolean(),
                      organizer: z
                        .union([
                          z.object({
                            name: z.union([z.string(), z.null()]).optional(),
                            email: z.union([z.string(), z.null()]).optional(),
                          }),
                          z.null(),
                        ])
                        .optional(),
                      calendarName: z.union([z.string(), z.null()]).optional(),
                      contributesToAvailability: z
                        .union([z.boolean(), z.null()])
                        .optional(),
                      inbox: z.union([z.string(), z.null()]).optional(),
                      role: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('organizer'),
                              z.literal('guest'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                      description: z.union([z.string(), z.null()]).optional(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                updated_at: z.string().datetime({ offset: true }),
                created_at: z.string().datetime({ offset: true }),
                calendar_event_search_results: z.array(
                  z.object({
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('calendarEvent') })
            ),
            z.intersection(
              z.object({
                id: z.string().uuid(),
                name: z.string(),
                owner_id: z.string(),
                bot_id: z.string().uuid(),
                created_at: z.string().datetime({ offset: true }),
                updated_at: z.string().datetime({ offset: true }),
                agent_session_search_results: z.array(
                  z.object({
                    goto: z
                      .union([
                        z.object({
                          message_turn: z.number().int().gte(0),
                          author: z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('user'),
                              z.literal('agent'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                        }),
                        z.null(),
                      ])
                      .optional(),
                    highlight: z.object({
                      name: z.union([z.string(), z.null()]).optional(),
                      content: z.array(z.string()).optional(),
                      user_id: z.union([z.string(), z.null()]).optional(),
                      sender: z.union([z.string(), z.null()]).optional(),
                      recipients: z.array(z.string()).optional(),
                      cc: z.array(z.string()).optional(),
                      bcc: z.array(z.string()).optional(),
                    }),
                    score: z.union([z.number(), z.null()]).optional(),
                  })
                ),
              }),
              z.object({ type: z.literal('agentSession') })
            ),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      )
  ),
});

export const CreateAgentSession = z
  .object({ agentId: z.string().uuid(), prompt: z.string() })
  .strict();

export const DispatchedCodingAgent = z.object({
  agent_session_id: z.string().uuid(),
  agent_id: z.string().uuid(),
  agent_name: z.string(),
});

export const CreateBookingLink = z
  .object({
    teamId: z.union([z.string().uuid(), z.null()]).optional(),
    draft: z
      .object({
        event: z
          .object({
            title: z.string(),
            slug: z.string(),
            description: z.string(),
            durationMinutes: z.number().int().gte(0).lte(65535),
            location: z.string(),
            macroCall: z.boolean().optional(),
            googleMeet: z.boolean(),
            enabled: z.boolean(),
            mode: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('individual'),
                z.literal('collective'),
                z.literal('roundRobin'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            hosts: z.array(z.string()),
            beforeMinutes: z.number().int().gte(0).lte(65535),
            afterMinutes: z.number().int().gte(0).lte(65535),
            noticeMinutes: z.number().int().gte(0).lte(65535),
            horizonDays: z.number().int().gte(0).lte(65535),
            intervalMinutes: z.number().int().gte(0).lte(65535),
            dailyLimit: z
              .union([z.number().int().gte(0).lte(65535), z.null()])
              .optional(),
            requiresConfirmation: z.boolean(),
            questions: z.array(
              z.object({
                id: z.string().uuid(),
                label: z.string(),
                required: z.boolean(),
              })
            ),
          })
          .strict(),
        schedule: z
          .object({
            name: z.string(),
            timeZone: z.string(),
            weekly: z.array(
              z.object({
                day: z.number().int().gte(0).lte(255),
                windows: z.array(
                  z.object({ start: z.string(), end: z.string() })
                ),
              })
            ),
            overrides: z.array(
              z.object({
                date: z.string().date(),
                windows: z.array(
                  z.object({ start: z.string(), end: z.string() })
                ),
              })
            ),
          })
          .strict(),
      })
      .strict(),
    userConfirmation: z.string(),
  })
  .strict();

export const BookingLinkResult = z.object({
  profileId: z.string().uuid(),
  eventTypeId: z.string().uuid(),
  revision: z.number().int(),
  draft: z
    .object({
      event: z
        .object({
          title: z.string(),
          slug: z.string(),
          description: z.string(),
          durationMinutes: z.number().int().gte(0).lte(65535),
          location: z.string(),
          macroCall: z.boolean().optional(),
          googleMeet: z.boolean(),
          enabled: z.boolean(),
          mode: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('individual'),
              z.literal('collective'),
              z.literal('roundRobin'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          hosts: z.array(z.string()),
          beforeMinutes: z.number().int().gte(0).lte(65535),
          afterMinutes: z.number().int().gte(0).lte(65535),
          noticeMinutes: z.number().int().gte(0).lte(65535),
          horizonDays: z.number().int().gte(0).lte(65535),
          intervalMinutes: z.number().int().gte(0).lte(65535),
          dailyLimit: z
            .union([z.number().int().gte(0).lte(65535), z.null()])
            .optional(),
          requiresConfirmation: z.boolean(),
          questions: z.array(
            z.object({
              id: z.string().uuid(),
              label: z.string(),
              required: z.boolean(),
            })
          ),
        })
        .strict(),
      schedule: z
        .object({
          name: z.string(),
          timeZone: z.string(),
          weekly: z.array(
            z.object({
              day: z.number().int().gte(0).lte(255),
              windows: z.array(
                z.object({ start: z.string(), end: z.string() })
              ),
            })
          ),
          overrides: z.array(
            z.object({
              date: z.string().date(),
              windows: z.array(
                z.object({ start: z.string(), end: z.string() })
              ),
            })
          ),
        })
        .strict(),
    })
    .strict(),
  url: z.string(),
});

export const CreateBot = z.object({
  teamId: z.union([z.string().uuid(), z.null()]).optional(),
  name: z.string(),
  handle: z.string(),
  description: z.union([z.string(), z.null()]).optional(),
  avatarUrl: z.union([z.string(), z.null()]).optional(),
  channelId: z.union([z.string().uuid(), z.null()]).optional(),
  credentialLabel: z.union([z.string(), z.null()]).optional(),
  credentialExpiresAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  hasAgent: z.union([z.boolean(), z.null()]).optional(),
});

export const CreateBotResponse = z.object({
  bot: z.object({
    botId: z.string().uuid(),
    owner: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ user_id: z.string(), type: z.literal('user') }),
        z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    name: z.string(),
    handle: z.string(),
    description: z.union([z.string(), z.null()]).optional(),
    avatarUrl: z.union([z.string(), z.null()]).optional(),
    hasAgent: z.boolean(),
  }),
  channelSetup: z
    .union([
      z.object({
        channelId: z.string().uuid(),
        webhook: z.object({
          channelId: z.string().uuid(),
          channelName: z.union([z.string(), z.null()]).optional(),
          webhookUrl: z.string(),
        }),
        credentialHeader: z.string(),
        credentialScopeHeader: z.string(),
        credentialScope: z.string(),
        credentialLabel: z.union([z.string(), z.null()]).optional(),
        credentialExpiresAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  summary: z.string(),
});

export const CreateCalendarEvent = z.object({
  title: z.string(),
  time: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        startsAt: z.string().datetime({ offset: true }),
        endsAt: z.string().datetime({ offset: true }),
        timeZone: z.union([z.string(), z.null()]).optional(),
        kind: z.literal('timed'),
      }),
      z.object({
        startDate: z.string().date(),
        endDate: z.string().date(),
        kind: z.literal('allDay'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  description: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  attendees: z
    .array(z.object({ email: z.string(), isOptional: z.boolean().optional() }))
    .optional(),
  recurrenceLines: z.array(z.string()).optional(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  reminders: z
    .union([
      z.object({
        useDefault: z.boolean(),
        overrides: z
          .array(
            z.object({ method: z.string(), minutes: z.number().int().gte(0) })
          )
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  addMacroCall: z.boolean().optional(),
  addConference: z.boolean().optional(),
  eventType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('default'), z.literal('out_of_office')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('default')
    )
    .optional(),
  outOfOffice: z
    .union([
      z.object({
        autoDeclineMode: z
          .union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('decline_none'),
                z.literal('decline_all'),
                z.literal('decline_new_only'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
          .optional(),
        declineMessage: z.union([z.string(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
  guestPolicy: z
    .union([
      z.object({
        canModify: z.boolean(),
        canInviteOthers: z.boolean(),
        canSeeOtherGuests: z.boolean(),
      }),
      z.null(),
    ])
    .optional(),
});

export const UserToolResponseForToolCalendarEvent = z
  .any()
  .superRefine((x, ctx) => {
    const schemas = [
      z.literal('PendingUserExecution'),
      z.literal('Rejected'),
      z
        .object({
          UserAction: z.object({
            eventId: z.string().uuid(),
            title: z.string(),
            start: z.string(),
            end: z.string(),
            isAllDay: z.boolean(),
            timeZone: z.union([z.string(), z.null()]).optional(),
            location: z.union([z.string(), z.null()]).optional(),
            description: z.union([z.string(), z.null()]).optional(),
            status: z.string(),
            isRecurring: z.boolean(),
            recurrenceLines: z.array(z.string()),
            attendees: z.array(
              z.object({
                email: z.string(),
                responseStatus: z.string(),
                isOrganizer: z.boolean(),
                isOptional: z.boolean(),
              })
            ),
            attendeeCount: z.number().int().gte(0),
            organizerEmail: z.union([z.string(), z.null()]).optional(),
            conferenceUrl: z.union([z.string(), z.null()]).optional(),
            macroCallUrl: z.union([z.string(), z.null()]).optional(),
            isReadOnly: z.boolean(),
            calendarId: z.union([z.string().uuid(), z.null()]).optional(),
            calendarName: z.union([z.string(), z.null()]).optional(),
            inbox: z.union([z.string(), z.null()]).optional(),
            myRole: z
              .union([
                z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('organizer'), z.literal('guest')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
                z.null(),
              ])
              .optional(),
          }),
        })
        .strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  });

export const CreateChannel = z.object({
  name: z.string(),
  channelType: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('private'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()).optional(),
});

export const CreateChannelResponse = z.object({
  channelId: z.string().uuid(),
  name: z.string(),
  channelType: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('private'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()),
  summary: z.string(),
});

export const CreateCompany = z.object({
  name: z.string(),
  domain: z.union([z.string(), z.null()]).optional(),
});

export const CreateCompanyResponse = z.object({
  companyId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  domains: z.array(z.string()),
});

export const CreateConfirmedCalendarEvent = z.object({
  title: z.string(),
  time: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        startsAt: z.string().datetime({ offset: true }),
        endsAt: z.string().datetime({ offset: true }),
        timeZone: z.union([z.string(), z.null()]).optional(),
        kind: z.literal('timed'),
      }),
      z.object({
        startDate: z.string().date(),
        endDate: z.string().date(),
        kind: z.literal('allDay'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  description: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  attendees: z
    .array(z.object({ email: z.string(), isOptional: z.boolean().optional() }))
    .optional(),
  recurrenceLines: z.array(z.string()).optional(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  reminders: z
    .union([
      z.object({
        useDefault: z.boolean(),
        overrides: z
          .array(
            z.object({ method: z.string(), minutes: z.number().int().gte(0) })
          )
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  addMacroCall: z.boolean().optional(),
  addConference: z.boolean().optional(),
  eventType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('default'), z.literal('out_of_office')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('default')
    )
    .optional(),
  outOfOffice: z
    .union([
      z.object({
        autoDeclineMode: z
          .union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('decline_none'),
                z.literal('decline_all'),
                z.literal('decline_new_only'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
          .optional(),
        declineMessage: z.union([z.string(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
  guestPolicy: z
    .union([
      z.object({
        canModify: z.boolean(),
        canInviteOthers: z.boolean(),
        canSeeOtherGuests: z.boolean(),
      }),
      z.null(),
    ])
    .optional(),
  userConfirmation: z.string(),
});

export const CreateContact = z.object({
  companyId: z.union([z.string().uuid(), z.null()]).optional(),
  name: z.string(),
  email: z.union([z.string(), z.null()]).optional(),
});

export const CreateContactResponse = z.object({
  contactId: z.string().uuid(),
  companyId: z.union([z.string().uuid(), z.null()]).optional(),
  email: z.union([z.string(), z.null()]).optional(),
  name: z.union([z.string(), z.null()]).optional(),
});

export const CreateDocument = z.object({
  documentName: z.string(),
  fileContent: z.string(),
  fileExtension: z.string(),
  isTask: z.boolean().optional(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateDocumentResponse = z.object({
  documentId: z.string().uuid(),
});

export const CreateForm = z.object({
  name: z.string(),
  source: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        isHiddenInDrive: z.boolean().optional(),
        anonymous: z.boolean().optional(),
        kind: z.literal('new'),
      }),
      z.object({
        databaseId: z.string().uuid(),
        tableId: z.string().uuid(),
        kind: z.literal('table'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  draft: z
    .object({
      description: z.string().optional(),
      confirmationMessage: z.string().optional(),
      sections: z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z
              .object({
                key: z.string(),
                title: z.string().optional(),
                description: z.string().optional(),
                questions: z.array(
                  z
                    .object({
                      key: z.string(),
                      column: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z
                            .object({
                              name: z.string(),
                              type: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.object({ type: z.literal('text') }),
                                  z.object({ type: z.literal('number') }),
                                  z.object({ type: z.literal('boolean') }),
                                  z.object({ type: z.literal('date') }),
                                  z.object({ type: z.literal('date_range') }),
                                  z.object({ type: z.literal('link') }),
                                  z.object({
                                    multi: z.boolean(),
                                    type: z.literal('select'),
                                  }),
                                  z.object({
                                    multi: z.boolean(),
                                    type: z.literal('select_number'),
                                  }),
                                  z.object({ type: z.literal('tag') }),
                                  z.object({
                                    target: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('USER'),
                                        z.literal('DOCUMENT'),
                                        z.literal('TASK'),
                                        z.literal('COMPANY'),
                                        z.literal('CONTACT'),
                                        z.literal('CALL_RECORD'),
                                        z.literal('CHANNEL'),
                                        z.literal('CHAT'),
                                        z.literal('PROJECT'),
                                        z.literal('THREAD'),
                                        z.literal('CALENDAR_EVENT'),
                                        z.literal('INITIATIVE'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    multi: z.boolean(),
                                    type: z.literal('entity'),
                                  }),
                                  z.object({
                                    database: z.string().uuid(),
                                    table: z.string().uuid(),
                                    type: z.literal('relation'),
                                  }),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              options: z
                                .array(
                                  z
                                    .object({
                                      key: z.string(),
                                      label: z.string(),
                                    })
                                    .strict()
                                )
                                .optional(),
                              kind: z.literal('new'),
                            })
                            .strict(),
                          z
                            .object({
                              columnId: z.string().uuid(),
                              kind: z.literal('existing'),
                            })
                            .strict(),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      helpText: z.string().optional(),
                      required: z.boolean().optional(),
                      widget: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('short'),
                              z.literal('paragraph'),
                              z.literal('datetime'),
                              z.literal('date'),
                              z.literal('url'),
                              z.literal('file'),
                              z.literal('choice'),
                              z.literal('dropdown'),
                              z.literal('checkboxes'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                    })
                    .strict()
                ),
                kind: z.literal('questions'),
              })
              .strict(),
            z
              .object({
                key: z.string(),
                title: z.string().optional(),
                description: z.string().optional(),
                rules: z
                  .object({
                    conjunction: z.any().superRefine((x, ctx) => {
                      const schemas = [z.literal('and'), z.literal('or')];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    conditions: z.array(
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z
                            .object({
                              question: z.union([
                                z.object({ key: z.string() }).strict(),
                                z.object({ id: z.string().uuid() }).strict(),
                              ]),
                              test: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z
                                    .object({
                                      test: z.any().superRefine((x, ctx) => {
                                        const schemas = [
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('isEmpty'),
                                                  z.literal('isNotEmpty'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            kind: z.literal('presence'),
                                          }),
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('is'),
                                                  z.literal('isNot'),
                                                  z.literal('contains'),
                                                  z.literal('doesNotContain'),
                                                  z.literal('startsWith'),
                                                  z.literal('endsWith'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            value: z.string(),
                                            kind: z.literal('text'),
                                          }),
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('is'),
                                                  z.literal('isNot'),
                                                  z.literal('greaterThan'),
                                                  z.literal(
                                                    'greaterThanOrEqual'
                                                  ),
                                                  z.literal('lessThan'),
                                                  z.literal('lessThanOrEqual'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            value: z.number(),
                                            kind: z.literal('number'),
                                          }),
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('before'),
                                                  z.literal('after'),
                                                  z.literal('onOrBefore'),
                                                  z.literal('onOrAfter'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            value: z
                                              .string()
                                              .datetime({ offset: true }),
                                            kind: z.literal('date'),
                                          }),
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('contains'),
                                                  z.literal('doesNotContain'),
                                                  z.literal('endsBefore'),
                                                  z.literal('endsOnOrAfter'),
                                                  z.literal('startsAfter'),
                                                  z.literal('startsOnOrBefore'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            value: z
                                              .string()
                                              .datetime({ offset: true }),
                                            kind: z.literal('dateRange'),
                                          }),
                                          z.object({
                                            checked: z.boolean(),
                                            kind: z.literal('checkbox'),
                                          }),
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('isAnyOf'),
                                                  z.literal('isNoneOf'),
                                                  z.literal('hasAny'),
                                                  z.literal('hasAll'),
                                                  z.literal('hasNone'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            options: z.array(z.string().uuid()),
                                            kind: z.literal('options'),
                                          }),
                                          z.object({
                                            operator: z
                                              .any()
                                              .superRefine((x, ctx) => {
                                                const schemas = [
                                                  z.literal('isAnyOf'),
                                                  z.literal('isNoneOf'),
                                                  z.literal('hasAny'),
                                                  z.literal('hasAll'),
                                                  z.literal('hasNone'),
                                                ];
                                                const errors = schemas.reduce<
                                                  z.ZodError[]
                                                >(
                                                  (errors, schema) =>
                                                    ((result) =>
                                                      result.error
                                                        ? [
                                                            ...errors,
                                                            result.error,
                                                          ]
                                                        : errors)(
                                                      schema.safeParse(x)
                                                    ),
                                                  []
                                                );
                                                if (
                                                  schemas.length -
                                                    errors.length !==
                                                  1
                                                ) {
                                                  ctx.addIssue({
                                                    path: ctx.path,
                                                    code: 'invalid_union',
                                                    unionErrors: errors,
                                                    message:
                                                      'Invalid input: Should pass single schema',
                                                  });
                                                }
                                              }),
                                            entities: z.array(z.string()),
                                            kind: z.literal('entities'),
                                          }),
                                        ];
                                        const errors = schemas.reduce<
                                          z.ZodError[]
                                        >(
                                          (errors, schema) =>
                                            ((result) =>
                                              result.error
                                                ? [...errors, result.error]
                                                : errors)(schema.safeParse(x)),
                                          []
                                        );
                                        if (
                                          schemas.length - errors.length !==
                                          1
                                        ) {
                                          ctx.addIssue({
                                            path: ctx.path,
                                            code: 'invalid_union',
                                            unionErrors: errors,
                                            message:
                                              'Invalid input: Should pass single schema',
                                          });
                                        }
                                      }),
                                      kind: z.literal('value'),
                                    })
                                    .strict(),
                                  z
                                    .object({
                                      operator: z
                                        .any()
                                        .superRefine((x, ctx) => {
                                          const schemas = [
                                            z.literal('isAnyOf'),
                                            z.literal('isNoneOf'),
                                            z.literal('hasAny'),
                                            z.literal('hasAll'),
                                            z.literal('hasNone'),
                                          ];
                                          const errors = schemas.reduce<
                                            z.ZodError[]
                                          >(
                                            (errors, schema) =>
                                              ((result) =>
                                                result.error
                                                  ? [...errors, result.error]
                                                  : errors)(
                                                schema.safeParse(x)
                                              ),
                                            []
                                          );
                                          if (
                                            schemas.length - errors.length !==
                                            1
                                          ) {
                                            ctx.addIssue({
                                              path: ctx.path,
                                              code: 'invalid_union',
                                              unionErrors: errors,
                                              message:
                                                'Invalid input: Should pass single schema',
                                            });
                                          }
                                        }),
                                      options: z.array(
                                        z.union([
                                          z
                                            .object({ key: z.string() })
                                            .strict(),
                                          z
                                            .object({ id: z.string().uuid() })
                                            .strict(),
                                        ])
                                      ),
                                      kind: z.literal('options'),
                                    })
                                    .strict(),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              kind: z.literal('condition'),
                            })
                            .strict(),
                          z
                            .object({
                              conjunction: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('and'),
                                  z.literal('or'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              conditions: z.array(z.any()),
                              kind: z.literal('group'),
                            })
                            .strict(),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      })
                    ),
                  })
                  .strict(),
                message: z.string(),
                kind: z.literal('gate'),
              })
              .strict(),
            z
              .object({
                key: z.string(),
                title: z.string().optional(),
                description: z.string().optional(),
                target: z.object({
                  profileId: z.string().uuid(),
                  eventTypeId: z.string().uuid(),
                }),
                qualification: z.any().superRefine((x, ctx) => {
                  const schemas = [
                    z.literal('advisory'),
                    z.literal('required'),
                  ];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
                kind: z.literal('booking'),
              })
              .strict(),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
    })
    .strict(),
});

export const MutationResult = z.object({
  state: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('completed'),
      z.literal('savedPendingProjection'),
      z.literal('partiallyApplied'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  formId: z.string().uuid(),
  saved: z
    .union([
      z.object({
        form: z.object({
          id: z.string().uuid(),
          name: z.string(),
          description: z.string(),
          ownerId: z.string(),
          databaseId: z.string().uuid(),
          tableId: z.string().uuid(),
          submittedColumnId: z.union([z.string().uuid(), z.null()]).optional(),
          respondentColumnId: z.union([z.string().uuid(), z.null()]).optional(),
          audience: z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('members'), z.literal('public')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          tallyVisible: z.boolean(),
          responsesVisible: z.boolean(),
          anonymous: z.boolean(),
          status: z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('open'), z.literal('closed')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          closesAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          confirmationMessage: z.string(),
          branding: z.object({
            logoFileId: z.union([z.string().uuid(), z.null()]).optional(),
            coverFileId: z.union([z.string().uuid(), z.null()]).optional(),
            accentColor: z.union([z.string(), z.null()]).optional(),
          }),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          projectId: z.union([z.string(), z.null()]).optional(),
          isHiddenInDrive: z.boolean(),
        }),
        layout: z.object({
          sections: z.array(
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  questions: z.array(
                    z.object({
                      id: z.string().uuid(),
                      column: z.string().uuid(),
                      helpText: z.string(),
                      required: z.boolean(),
                      widget: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('short'),
                              z.literal('paragraph'),
                              z.literal('datetime'),
                              z.literal('date'),
                              z.literal('url'),
                              z.literal('file'),
                              z.literal('choice'),
                              z.literal('dropdown'),
                              z.literal('checkboxes'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                    })
                  ),
                  kind: z.literal('questions'),
                }),
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  rules: z.object({
                    conjunction: z.any().superRefine((x, ctx) => {
                      const schemas = [z.literal('and'), z.literal('or')];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    conditions: z.array(
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.intersection(
                            z.object({
                              column: z.string().uuid(),
                              test: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isEmpty'),
                                        z.literal('isNotEmpty'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    kind: z.literal('presence'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('is'),
                                        z.literal('isNot'),
                                        z.literal('contains'),
                                        z.literal('doesNotContain'),
                                        z.literal('startsWith'),
                                        z.literal('endsWith'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z.string(),
                                    kind: z.literal('text'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('is'),
                                        z.literal('isNot'),
                                        z.literal('greaterThan'),
                                        z.literal('greaterThanOrEqual'),
                                        z.literal('lessThan'),
                                        z.literal('lessThanOrEqual'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z.number(),
                                    kind: z.literal('number'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('before'),
                                        z.literal('after'),
                                        z.literal('onOrBefore'),
                                        z.literal('onOrAfter'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z
                                      .string()
                                      .datetime({ offset: true }),
                                    kind: z.literal('date'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('contains'),
                                        z.literal('doesNotContain'),
                                        z.literal('endsBefore'),
                                        z.literal('endsOnOrAfter'),
                                        z.literal('startsAfter'),
                                        z.literal('startsOnOrBefore'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z
                                      .string()
                                      .datetime({ offset: true }),
                                    kind: z.literal('dateRange'),
                                  }),
                                  z.object({
                                    checked: z.boolean(),
                                    kind: z.literal('checkbox'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isAnyOf'),
                                        z.literal('isNoneOf'),
                                        z.literal('hasAny'),
                                        z.literal('hasAll'),
                                        z.literal('hasNone'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    options: z.array(z.string().uuid()),
                                    kind: z.literal('options'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isAnyOf'),
                                        z.literal('isNoneOf'),
                                        z.literal('hasAny'),
                                        z.literal('hasAll'),
                                        z.literal('hasNone'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    entities: z.array(z.string()),
                                    kind: z.literal('entities'),
                                  }),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                            }),
                            z.object({ kind: z.literal('condition') })
                          ),
                          z.intersection(
                            z.object({
                              conjunction: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('and'),
                                  z.literal('or'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              conditions: z.any(),
                            }),
                            z.object({ kind: z.literal('group') })
                          ),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      })
                    ),
                  }),
                  message: z.string(),
                  kind: z.literal('gate'),
                }),
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  target: z.object({
                    profileId: z.string().uuid(),
                    eventTypeId: z.string().uuid(),
                  }),
                  kind: z.literal('booking'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            })
          ),
        }),
        columns: z.array(
          z.object({
            id: z.string().uuid(),
            name: z.string(),
            kind: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({ type: z.literal('text') }),
                z.object({ type: z.literal('number') }),
                z.object({ type: z.literal('boolean') }),
                z.object({ type: z.literal('date') }),
                z.object({ type: z.literal('date_range') }),
                z.object({ type: z.literal('link') }),
                z.object({ multi: z.boolean(), type: z.literal('select') }),
                z.object({
                  multi: z.boolean(),
                  type: z.literal('select_number'),
                }),
                z.object({ type: z.literal('tag') }),
                z.object({
                  target: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('USER'),
                      z.literal('DOCUMENT'),
                      z.literal('TASK'),
                      z.literal('COMPANY'),
                      z.literal('CONTACT'),
                      z.literal('CALL_RECORD'),
                      z.literal('CHANNEL'),
                      z.literal('CHAT'),
                      z.literal('PROJECT'),
                      z.literal('THREAD'),
                      z.literal('CALENDAR_EVENT'),
                      z.literal('INITIATIVE'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  multi: z.boolean(),
                  type: z.literal('entity'),
                }),
                z.object({
                  database: z.string().uuid(),
                  table: z.string().uuid(),
                  type: z.literal('relation'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            options: z.array(
              z.object({
                id: z.string().uuid(),
                label: z.string(),
                color: z.union([z.string(), z.null()]).optional(),
              })
            ),
          })
        ),
        projected: z.boolean(),
        editorUrl: z.string(),
        respondentUrl: z.string(),
        acceptingResponses: z.boolean(),
        capabilities: z.object({
          presentationLabels: z.boolean(),
          conditionalColumnCleanup: z.boolean(),
          safeLinkedTypeChanges: z.boolean(),
          requiredBookingQualification: z.boolean(),
        }),
      }),
      z.null(),
    ])
    .optional(),
  keys: z.object({
    sections: z.record(z.string().uuid()),
    questions: z.record(z.string().uuid()),
    columns: z.record(z.string().uuid()),
    options: z.record(z.record(z.string().uuid())),
  }),
  diagnostics: z.array(
    z.object({
      code: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('TextTooLong'),
          z.literal('TooManySections'),
          z.literal('DuplicateSectionKey'),
          z.literal('DuplicateQuestionKey'),
          z.literal('TooManyQuestions'),
          z.literal('UnknownColumn'),
          z.literal('DuplicateDisplayLabel'),
          z.literal('TooManyOptions'),
          z.literal('UnexpectedOptions'),
          z.literal('DuplicateOptionKey'),
          z.literal('DuplicateOptionLabel'),
          z.literal('InvalidNumericOption'),
          z.literal('ReferencePickerUnavailable'),
          z.literal('UnsupportedWidget'),
          z.literal('RepeatedColumn'),
          z.literal('QualificationEnforcementUnavailable'),
          z.literal('BookingMustBeLast'),
          z.literal('EmptyScreeningGroup'),
          z.literal('ScreeningTooDeep'),
          z.literal('TooManyConditions'),
          z.literal('GateReferencesLaterQuestion'),
          z.literal('UnknownOption'),
          z.literal('InvalidGateRule'),
          z.literal('ConcurrentFieldChange'),
          z.literal('InvalidEdit'),
          z.literal('Forbidden'),
          z.literal('Unavailable'),
          z.literal('InvalidDraft'),
          z.literal('InvalidAccess'),
          z.literal('InvalidName'),
          z.literal('FormNotFound'),
          z.literal('TableAlreadyHasForm'),
          z.literal('BookingTargetUnavailable'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      path: z.string(),
      message: z.string(),
    })
  ),
});

export const CreateImportEntity = z.object({
  source: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('linear'),
      z.literal('jira'),
      z.literal('notion'),
      z.literal('slack'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  foreignId: z.string(),
  status: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('staged'), z.literal('imported')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  metadata: z.any(),
  entityId: z.union([z.string(), z.null()]).optional(),
});

export const CreateImportEntityResponse = z.object({
  outcome: z.string(),
  entity: z.object({
    id: z.string().uuid(),
    source: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('linear'),
        z.literal('jira'),
        z.literal('notion'),
        z.literal('slack'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    foreignId: z.string(),
    status: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('staged'),
        z.literal('importing'),
        z.literal('imported'),
        z.literal('discarded'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    label: z.string(),
    entityId: z.union([z.string(), z.null()]).optional(),
    entityType: z.union([z.string(), z.null()]).optional(),
    importedByTeammate: z.boolean(),
  }),
  message: z.string(),
});

export const CreateInitiative = z.object({
  name: z.string(),
  description: z.union([z.string(), z.null()]).optional(),
  memberIds: z.union([z.array(z.string()), z.null()]).optional(),
  shareWithTeam: z.union([z.boolean(), z.null()]).optional(),
});

export const ProjectDetails = z.object({
  initiativeId: z.string().uuid(),
  name: z.string(),
  ownerId: z.string(),
  memberIds: z.array(z.string()),
  taskIds: z.array(z.string()),
  taskCount: z.number().int().gte(0),
  tasksTruncated: z.boolean(),
  access: z.string(),
  teamAccess: z.union([z.string(), z.null()]).optional(),
  linkScope: z.union([z.string(), z.null()]).optional(),
  linkAccess: z.union([z.string(), z.null()]).optional(),
  channelShares: z.array(
    z.object({ channelId: z.string(), access: z.string() })
  ),
});

export const CreatePipeline = z.object({
  name: z.string(),
  tracks: z.enum(['company', 'contact']),
  sharedWithTeam: z.union([z.boolean(), z.null()]).optional(),
  stages: z.array(z.string()).optional(),
  fields: z
    .array(
      z.object({
        name: z.string(),
        type: z.enum([
          'text',
          'number',
          'checkbox',
          'date',
          'link',
          'select',
          'multi_select',
          'person',
          'people',
        ]),
        options: z.array(z.string()).optional(),
      })
    )
    .optional(),
});

export const CreatePipelineResponse = z.object({
  pipeline: z.object({
    id: z.string().uuid(),
    name: z.string(),
    tracks: z.enum(['company', 'contact']),
    access: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('view'),
        z.literal('edit'),
        z.literal('owner'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    sharedWithTeam: z.boolean(),
    fields: z.array(
      z.object({
        name: z.string(),
        type: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('company'),
            z.literal('contact'),
            z.literal('text'),
            z.literal('number'),
            z.literal('checkbox'),
            z.literal('date'),
            z.literal('date_range'),
            z.literal('link'),
            z.literal('select'),
            z.literal('tags'),
            z.literal('person'),
            z.literal('item'),
            z.literal('relation'),
            z.literal('formula'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        multiple: z.boolean(),
        options: z.array(z.string()),
        required: z.boolean(),
        readOnly: z.boolean(),
      })
    ),
    version: z.number().int(),
  }),
  summary: z.string(),
});

export const CreateProject = z.object({
  projectName: z.string(),
  parentProjectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateProjectResponse = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
});

export const CreateRoutine = z.object({
  configuration: z.object({
    name: z.string(),
    instructions: z.string(),
    target: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ model: z.string(), type: z.literal('model') }),
        z.object({ agentId: z.string().uuid(), type: z.literal('agent') }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    schedule: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({
          at: z.string().datetime({ offset: true }),
          type: z.literal('once'),
        }),
        z.object({
          expression: z.string(),
          timezone: z.string(),
          type: z.literal('cron'),
        }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    requiredApps: z
      .array(z.object({ appSlug: z.string(), name: z.string() }))
      .optional(),
  }),
});

export const RoutineInfo = z.object({
  id: z.string().uuid(),
  name: z.string(),
  enabled: z.boolean(),
  trigger: z.any(),
  task: z.any(),
  nextRunAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const CreateSkill = z.object({
  skillName: z.string(),
  markdown: z.string(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateSkillResponse = z.object({ documentId: z.string().uuid() });

export const CreateTag = z.object({
  label: z.string(),
  color: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('red'),
      z.literal('tomato'),
      z.literal('orange'),
      z.literal('amber'),
      z.literal('yellow'),
      z.literal('green'),
      z.literal('teal'),
      z.literal('blue'),
      z.literal('indigo'),
      z.literal('purple'),
      z.literal('pink'),
      z.literal('gray'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  scope: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('personal'), z.literal('team')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('personal')
    )
    .optional(),
});

export const CreateTagResponse = z.object({
  id: z.string().uuid(),
  label: z.string(),
  color: z.union([z.string(), z.null()]).optional(),
  propertyDefinitionId: z.string().uuid(),
  scope: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('personal'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const Decide = z.intersection(
  z.object({ input: z.any(), questions: z.record(z.string()) }).strict(),
  z.any()
);

export const DecisionResponse = z.object({
  probabilities: z.record(z.number()),
});

export const DeleteBot = z.object({ botId: z.string().uuid() });

export const DeleteBotResponse = z.object({
  botId: z.string().uuid(),
  deleted: z.boolean(),
  summary: z.string(),
});

export const DeleteCalendarEvent = z.object({
  eventId: z.string().uuid(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  scope: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [
        z.literal('all'),
        z.literal('this_event'),
        z.literal('this_and_following'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  recurrenceId: z.union([z.string(), z.null()]).optional(),
});

export const DeleteCalendarEventResponse = z.object({
  eventId: z.string().uuid(),
  summary: z.string(),
});

export const DeleteCallRecord = z.object({ callId: z.string().uuid() });

export const DeleteCallRecordResponse = z.object({ callId: z.string().uuid() });

export const DeleteChannelMessage = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
});

export const DeleteChannelMessageResponse = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  summary: z.string(),
});

export const DeleteComment = z.object({ commentId: z.string().uuid() });

export const DeleteCommentResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('initiative'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string(),
  commentId: z.string().uuid(),
  deletedThread: z.boolean(),
});

export const DeleteDatabaseView = z.object({
  databaseId: z.string().uuid(),
  viewId: z.string().uuid(),
});

export const DeletedDatabaseView = z.object({
  databaseId: z.string().uuid(),
  tableId: z.string().uuid(),
  viewId: z.string().uuid(),
  name: z.string(),
});

export const DeleteImportEntity = z.object({ id: z.string().uuid() });

export const DeleteImportEntityResponse = z.object({
  discarded: z.boolean(),
  message: z.string(),
});

export const DeleteInitiative = z.object({ initiativeId: z.string().uuid() });

export const ProjectOperationComplete = z.object({ success: z.boolean() });

export const DeleteRoutine = z.object({ routineId: z.string().uuid() });

export const DeletedRoutine = z.object({ routineId: z.string().uuid() });

export const DeleteTag = z.object({
  id: z.string().uuid(),
  property_definition_id: z.string().uuid(),
});

export const DeleteTagResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const DescribeDatabase = z.object({
  databaseId: z.string().uuid(),
  includeEditingMetadata: z.boolean().optional(),
});

export const ToolDatabaseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  grant: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('view'),
      z.literal('comment'),
      z.literal('edit'),
      z.literal('owner'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  tables: z.array(
    z.object({
      id: z.string().uuid(),
      sqlName: z.string(),
      version: z.number().int(),
      name: z.string(),
      writable: z.boolean(),
      columns: z.array(
        z.object({
          id: z.string().uuid(),
          sqlName: z.string(),
          name: z.string(),
          dataType: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('text'),
              z.literal('number'),
              z.literal('boolean'),
              z.literal('date'),
              z.literal('date_range'),
              z.literal('link'),
              z.literal('select'),
              z.literal('select_number'),
              z.literal('tag'),
              z.literal('entity'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          specificEntityType: z.union([z.string(), z.null()]).optional(),
          isMultiSelect: z.boolean(),
          options: z
            .array(z.object({ id: z.string().uuid(), label: z.string() }))
            .optional(),
          writable: z.boolean(),
          relation: z
            .union([
              z.object({
                databaseId: z.string().uuid(),
                tableId: z.string().uuid(),
              }),
              z.null(),
            ])
            .optional(),
          formula: z.union([z.string(), z.null()]).optional(),
          safeTypes: z.array(z.string()).optional(),
          checkedTypes: z.array(z.string()).optional(),
        })
      ),
      views: z.array(z.any()).optional(),
    })
  ),
});

export const DiscoverConnectors = z.object({
  request: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({ query: z.string(), operation: z.literal('search') }),
      z.object({ app_slug: z.string(), operation: z.literal('inspect') }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const DiscoverConnectorsResponse = z.object({
  result: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        apps: z.array(
          z.object({
            app_slug: z.string(),
            name: z.string(),
            description: z.union([z.string(), z.null()]).optional(),
          })
        ),
        more_results: z.boolean(),
        operation: z.literal('search'),
      }),
      z.object({
        app: z.object({
          app_slug: z.string(),
          name: z.string(),
          description: z.union([z.string(), z.null()]).optional(),
        }),
        connected: z.boolean(),
        tools: z.array(z.object({ name: z.string(), description: z.string() })),
        tools_truncated: z.boolean(),
        operation: z.literal('inspect'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  connect_markup: z.union([z.string(), z.null()]).optional(),
});

export const DispatchCodingAgent = z
  .object({ agent_id: z.string().uuid(), prompt: z.string() })
  .strict();

export const DuplicateItem = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('database'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
});

export const DuplicateItemResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('database'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  sourceId: z.string().uuid(),
  copyId: z.string(),
  message: z.string(),
});

export const EditBookingLink = z
  .object({
    teamId: z.union([z.string().uuid(), z.null()]).optional(),
    eventTypeId: z.string().uuid(),
    expectedRevision: z.number().int(),
    draft: z
      .object({
        event: z
          .object({
            title: z.string(),
            slug: z.string(),
            description: z.string(),
            durationMinutes: z.number().int().gte(0).lte(65535),
            location: z.string(),
            macroCall: z.boolean().optional(),
            googleMeet: z.boolean(),
            enabled: z.boolean(),
            mode: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('individual'),
                z.literal('collective'),
                z.literal('roundRobin'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            hosts: z.array(z.string()),
            beforeMinutes: z.number().int().gte(0).lte(65535),
            afterMinutes: z.number().int().gte(0).lte(65535),
            noticeMinutes: z.number().int().gte(0).lte(65535),
            horizonDays: z.number().int().gte(0).lte(65535),
            intervalMinutes: z.number().int().gte(0).lte(65535),
            dailyLimit: z
              .union([z.number().int().gte(0).lte(65535), z.null()])
              .optional(),
            requiresConfirmation: z.boolean(),
            questions: z.array(
              z.object({
                id: z.string().uuid(),
                label: z.string(),
                required: z.boolean(),
              })
            ),
          })
          .strict(),
        schedule: z
          .object({
            name: z.string(),
            timeZone: z.string(),
            weekly: z.array(
              z.object({
                day: z.number().int().gte(0).lte(255),
                windows: z.array(
                  z.object({ start: z.string(), end: z.string() })
                ),
              })
            ),
            overrides: z.array(
              z.object({
                date: z.string().date(),
                windows: z.array(
                  z.object({ start: z.string(), end: z.string() })
                ),
              })
            ),
          })
          .strict(),
      })
      .strict(),
    userConfirmation: z.string(),
  })
  .strict();

export const EditCanvas = z.object({
  document_id: z.string(),
  instructions: z.string(),
});

export const EditChannelMessage = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  content: z.string(),
});

export const EditChannelMessageResponse = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  summary: z.string(),
});

export const EditComment = z.object({
  commentId: z.string().uuid(),
  content: z.string(),
});

export const EditCommentResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('initiative'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string(),
  commentId: z.string().uuid(),
});

export const EditDocument = z.object({
  document_id: z.string(),
  instructions: z.string(),
  fast: z.boolean().optional(),
});

export const EditDocumentResponse = z.object({
  summary: z.string(),
  clarification: z.union([z.string(), z.null()]).optional(),
});

export const EditForm = z.object({
  formId: z.string().uuid(),
  changes: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z
          .object({
            questionId: z.string().uuid(),
            helpText: z.union([z.string(), z.null()]).optional(),
            required: z.union([z.boolean(), z.null()]).optional(),
            widget: z
              .union([
                z.any().superRefine((x, ctx) => {
                  const schemas = [
                    z.object({ kind: z.literal('default') }).strict(),
                    z
                      .object({
                        widget: z.any().superRefine((x, ctx) => {
                          const schemas = [
                            z.literal('short'),
                            z.literal('paragraph'),
                            z.literal('datetime'),
                            z.literal('date'),
                            z.literal('url'),
                            z.literal('file'),
                            z.literal('choice'),
                            z.literal('dropdown'),
                            z.literal('checkboxes'),
                          ];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                        kind: z.literal('set'),
                      })
                      .strict(),
                  ];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
                z.null(),
              ])
              .optional(),
            kind: z.literal('setQuestion'),
          })
          .strict(),
        z
          .object({
            sectionId: z.string().uuid(),
            title: z.union([z.string(), z.null()]).optional(),
            description: z.union([z.string(), z.null()]).optional(),
            message: z.union([z.string(), z.null()]).optional(),
            kind: z.literal('setSectionText'),
          })
          .strict(),
        z
          .object({
            sectionId: z.string().uuid(),
            rules: z.object({
              conjunction: z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('and'), z.literal('or')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              conditions: z.array(
                z.any().superRefine((x, ctx) => {
                  const schemas = [
                    z.intersection(
                      z.object({
                        column: z.string().uuid(),
                        test: z.any().superRefine((x, ctx) => {
                          const schemas = [
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('isEmpty'),
                                  z.literal('isNotEmpty'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              kind: z.literal('presence'),
                            }),
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('is'),
                                  z.literal('isNot'),
                                  z.literal('contains'),
                                  z.literal('doesNotContain'),
                                  z.literal('startsWith'),
                                  z.literal('endsWith'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              value: z.string(),
                              kind: z.literal('text'),
                            }),
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('is'),
                                  z.literal('isNot'),
                                  z.literal('greaterThan'),
                                  z.literal('greaterThanOrEqual'),
                                  z.literal('lessThan'),
                                  z.literal('lessThanOrEqual'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              value: z.number(),
                              kind: z.literal('number'),
                            }),
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('before'),
                                  z.literal('after'),
                                  z.literal('onOrBefore'),
                                  z.literal('onOrAfter'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              value: z.string().datetime({ offset: true }),
                              kind: z.literal('date'),
                            }),
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('contains'),
                                  z.literal('doesNotContain'),
                                  z.literal('endsBefore'),
                                  z.literal('endsOnOrAfter'),
                                  z.literal('startsAfter'),
                                  z.literal('startsOnOrBefore'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              value: z.string().datetime({ offset: true }),
                              kind: z.literal('dateRange'),
                            }),
                            z.object({
                              checked: z.boolean(),
                              kind: z.literal('checkbox'),
                            }),
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('isAnyOf'),
                                  z.literal('isNoneOf'),
                                  z.literal('hasAny'),
                                  z.literal('hasAll'),
                                  z.literal('hasNone'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              options: z.array(z.string().uuid()),
                              kind: z.literal('options'),
                            }),
                            z.object({
                              operator: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('isAnyOf'),
                                  z.literal('isNoneOf'),
                                  z.literal('hasAny'),
                                  z.literal('hasAll'),
                                  z.literal('hasNone'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              entities: z.array(z.string()),
                              kind: z.literal('entities'),
                            }),
                          ];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                      }),
                      z.object({ kind: z.literal('condition') })
                    ),
                    z.intersection(
                      z.object({
                        conjunction: z.any().superRefine((x, ctx) => {
                          const schemas = [z.literal('and'), z.literal('or')];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                        conditions: z.any(),
                      }),
                      z.object({ kind: z.literal('group') })
                    ),
                  ];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                })
              ),
            }),
            kind: z.literal('setGateRules'),
          })
          .strict(),
        z
          .object({
            sectionId: z.string().uuid(),
            target: z.object({
              profileId: z.string().uuid(),
              eventTypeId: z.string().uuid(),
            }),
            qualification: z.any().superRefine((x, ctx) => {
              const schemas = [z.literal('advisory'), z.literal('required')];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            kind: z.literal('setBookingTarget'),
          })
          .strict(),
        z
          .object({
            section: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  questions: z.array(
                    z.object({
                      id: z.string().uuid(),
                      column: z.string().uuid(),
                      helpText: z.string(),
                      required: z.boolean(),
                      widget: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('short'),
                              z.literal('paragraph'),
                              z.literal('datetime'),
                              z.literal('date'),
                              z.literal('url'),
                              z.literal('file'),
                              z.literal('choice'),
                              z.literal('dropdown'),
                              z.literal('checkboxes'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                    })
                  ),
                  kind: z.literal('questions'),
                }),
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  rules: z.object({
                    conjunction: z.any().superRefine((x, ctx) => {
                      const schemas = [z.literal('and'), z.literal('or')];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    conditions: z.array(
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.intersection(
                            z.object({
                              column: z.string().uuid(),
                              test: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isEmpty'),
                                        z.literal('isNotEmpty'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    kind: z.literal('presence'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('is'),
                                        z.literal('isNot'),
                                        z.literal('contains'),
                                        z.literal('doesNotContain'),
                                        z.literal('startsWith'),
                                        z.literal('endsWith'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z.string(),
                                    kind: z.literal('text'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('is'),
                                        z.literal('isNot'),
                                        z.literal('greaterThan'),
                                        z.literal('greaterThanOrEqual'),
                                        z.literal('lessThan'),
                                        z.literal('lessThanOrEqual'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z.number(),
                                    kind: z.literal('number'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('before'),
                                        z.literal('after'),
                                        z.literal('onOrBefore'),
                                        z.literal('onOrAfter'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z
                                      .string()
                                      .datetime({ offset: true }),
                                    kind: z.literal('date'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('contains'),
                                        z.literal('doesNotContain'),
                                        z.literal('endsBefore'),
                                        z.literal('endsOnOrAfter'),
                                        z.literal('startsAfter'),
                                        z.literal('startsOnOrBefore'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z
                                      .string()
                                      .datetime({ offset: true }),
                                    kind: z.literal('dateRange'),
                                  }),
                                  z.object({
                                    checked: z.boolean(),
                                    kind: z.literal('checkbox'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isAnyOf'),
                                        z.literal('isNoneOf'),
                                        z.literal('hasAny'),
                                        z.literal('hasAll'),
                                        z.literal('hasNone'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    options: z.array(z.string().uuid()),
                                    kind: z.literal('options'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isAnyOf'),
                                        z.literal('isNoneOf'),
                                        z.literal('hasAny'),
                                        z.literal('hasAll'),
                                        z.literal('hasNone'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    entities: z.array(z.string()),
                                    kind: z.literal('entities'),
                                  }),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                            }),
                            z.object({ kind: z.literal('condition') })
                          ),
                          z.intersection(
                            z.object({
                              conjunction: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('and'),
                                  z.literal('or'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              conditions: z.any(),
                            }),
                            z.object({ kind: z.literal('group') })
                          ),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      })
                    ),
                  }),
                  message: z.string(),
                  kind: z.literal('gate'),
                }),
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  target: z.object({
                    profileId: z.string().uuid(),
                    eventTypeId: z.string().uuid(),
                  }),
                  kind: z.literal('booking'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            after: z.union([z.string().uuid(), z.null()]).optional(),
            kind: z.literal('addSection'),
          })
          .strict(),
        z
          .object({
            sectionId: z.string().uuid(),
            after: z.union([z.string().uuid(), z.null()]).optional(),
            kind: z.literal('moveSection'),
          })
          .strict(),
        z
          .object({
            sectionId: z.string().uuid(),
            kind: z.literal('removeSection'),
          })
          .strict(),
        z
          .object({
            sectionId: z.string().uuid(),
            question: z.object({
              id: z.string().uuid(),
              column: z.string().uuid(),
              helpText: z.string(),
              required: z.boolean(),
              widget: z
                .union([
                  z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('short'),
                      z.literal('paragraph'),
                      z.literal('datetime'),
                      z.literal('date'),
                      z.literal('url'),
                      z.literal('file'),
                      z.literal('choice'),
                      z.literal('dropdown'),
                      z.literal('checkboxes'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  z.null(),
                ])
                .optional(),
            }),
            after: z.union([z.string().uuid(), z.null()]).optional(),
            kind: z.literal('addQuestion'),
          })
          .strict(),
        z
          .object({
            questionId: z.string().uuid(),
            sectionId: z.string().uuid(),
            after: z.union([z.string().uuid(), z.null()]).optional(),
            kind: z.literal('moveQuestion'),
          })
          .strict(),
        z
          .object({
            questionId: z.string().uuid(),
            kind: z.literal('removeQuestion'),
          })
          .strict(),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
  newColumns: z
    .array(
      z
        .object({
          id: z.string().uuid(),
          name: z.string(),
          kind: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.object({ type: z.literal('text') }),
              z.object({ type: z.literal('number') }),
              z.object({ type: z.literal('boolean') }),
              z.object({ type: z.literal('date') }),
              z.object({ type: z.literal('date_range') }),
              z.object({ type: z.literal('link') }),
              z.object({ multi: z.boolean(), type: z.literal('select') }),
              z.object({
                multi: z.boolean(),
                type: z.literal('select_number'),
              }),
              z.object({ type: z.literal('tag') }),
              z.object({
                target: z.any().superRefine((x, ctx) => {
                  const schemas = [
                    z.literal('USER'),
                    z.literal('DOCUMENT'),
                    z.literal('TASK'),
                    z.literal('COMPANY'),
                    z.literal('CONTACT'),
                    z.literal('CALL_RECORD'),
                    z.literal('CHANNEL'),
                    z.literal('CHAT'),
                    z.literal('PROJECT'),
                    z.literal('THREAD'),
                    z.literal('CALENDAR_EVENT'),
                    z.literal('INITIATIVE'),
                  ];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
                multi: z.boolean(),
                type: z.literal('entity'),
              }),
              z.object({
                database: z.string().uuid(),
                table: z.string().uuid(),
                type: z.literal('relation'),
              }),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          options: z
            .array(
              z.object({
                id: z.string().uuid(),
                label: z.string(),
                color: z.union([z.string(), z.null()]).optional(),
              })
            )
            .optional(),
        })
        .strict()
    )
    .optional(),
  description: z.union([z.string(), z.null()]).optional(),
  confirmationMessage: z.union([z.string(), z.null()]).optional(),
});

export const EditPipeline = z.object({
  pipelineId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  sharedWithTeam: z.union([z.boolean(), z.null()]).optional(),
  addFields: z
    .array(
      z.object({
        name: z.string(),
        type: z.enum([
          'text',
          'number',
          'checkbox',
          'date',
          'link',
          'select',
          'multi_select',
          'person',
          'people',
        ]),
        options: z.array(z.string()).optional(),
      })
    )
    .optional(),
  renameFields: z
    .array(z.object({ from: z.string(), to: z.string() }))
    .optional(),
  removeFields: z.array(z.string()).optional(),
  addOptions: z
    .array(z.object({ field: z.string(), labels: z.array(z.string()) }))
    .optional(),
  renameOptions: z
    .array(z.object({ field: z.string(), from: z.string(), to: z.string() }))
    .optional(),
  removeOptions: z
    .array(z.object({ field: z.string(), label: z.string() }))
    .optional(),
});

export const EditPipelineResponse = z.object({
  pipeline: z.object({
    id: z.string().uuid(),
    name: z.string(),
    tracks: z.enum(['company', 'contact']),
    access: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('view'),
        z.literal('edit'),
        z.literal('owner'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    sharedWithTeam: z.boolean(),
    fields: z.array(
      z.object({
        name: z.string(),
        type: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('company'),
            z.literal('contact'),
            z.literal('text'),
            z.literal('number'),
            z.literal('checkbox'),
            z.literal('date'),
            z.literal('date_range'),
            z.literal('link'),
            z.literal('select'),
            z.literal('tags'),
            z.literal('person'),
            z.literal('item'),
            z.literal('relation'),
            z.literal('formula'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        multiple: z.boolean(),
        options: z.array(z.string()),
        required: z.boolean(),
        readOnly: z.boolean(),
      })
    ),
    version: z.number().int(),
  }),
  summary: z.string(),
});

export const EditPresentation = z.object({
  documentId: z.string(),
  operations: z
    .array(
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              text: z.string(),
              op: z.literal('setText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              at: z
                .object({
                  paragraph: z.number().int().gte(0),
                  offset: z.number().int().gte(0),
                })
                .strict(),
              text: z.string(),
              op: z.literal('insertText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              start: z
                .object({
                  paragraph: z.number().int().gte(0),
                  offset: z.number().int().gte(0),
                })
                .strict(),
              end: z
                .object({
                  paragraph: z.number().int().gte(0),
                  offset: z.number().int().gte(0),
                })
                .strict(),
              op: z.literal('deleteText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              start: z
                .union([
                  z
                    .object({
                      paragraph: z.number().int().gte(0),
                      offset: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              end: z
                .union([
                  z
                    .object({
                      paragraph: z.number().int().gte(0),
                      offset: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              props: z
                .object({
                  bold: z.union([z.boolean(), z.null()]).optional(),
                  italic: z.union([z.boolean(), z.null()]).optional(),
                  underline: z.union([z.boolean(), z.null()]).optional(),
                  strike: z.union([z.boolean(), z.null()]).optional(),
                  size: z.union([z.number(), z.null()]).optional(),
                  color: z.union([z.string(), z.null()]).optional(),
                  font: z.union([z.string(), z.null()]).optional(),
                  highlight: z.union([z.string(), z.null()]).optional(),
                  baseline: z.union([z.number(), z.null()]).optional(),
                  spacing: z.union([z.number(), z.null()]).optional(),
                  link: z.union([z.string(), z.null()]).optional(),
                  linkTip: z.union([z.string(), z.null()]).optional(),
                  shadow: z
                    .union([
                      z.union([
                        z.string(),
                        z
                          .object({
                            preset: z.union([z.string(), z.null()]).optional(),
                            color: z.union([z.string(), z.null()]).optional(),
                            transparency: z
                              .union([z.number(), z.null()])
                              .optional(),
                            sizePct: z.union([z.number(), z.null()]).optional(),
                            blurPt: z.union([z.number(), z.null()]).optional(),
                            distancePt: z
                              .union([z.number(), z.null()])
                              .optional(),
                            angleDeg: z
                              .union([z.number(), z.null()])
                              .optional(),
                          })
                          .strict(),
                      ]),
                      z.null(),
                    ])
                    .optional(),
                  glow: z
                    .union([
                      z.union([
                        z.string(),
                        z
                          .object({
                            color: z.union([z.string(), z.null()]).optional(),
                            sizePt: z.union([z.number(), z.null()]).optional(),
                            transparency: z
                              .union([z.number(), z.null()])
                              .optional(),
                          })
                          .strict(),
                      ]),
                      z.null(),
                    ])
                    .optional(),
                })
                .strict(),
              op: z.literal('formatText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              from: z.union([z.number().int().gte(0), z.null()]).optional(),
              to: z.union([z.number().int().gte(0), z.null()]).optional(),
              props: z
                .object({
                  align: z.union([z.string(), z.null()]).optional(),
                  level: z
                    .union([z.number().int().gte(0).lte(255), z.null()])
                    .optional(),
                  bullet: z
                    .union([
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.object({ kind: z.literal('none') }).strict(),
                          z.object({ kind: z.literal('inherit') }).strict(),
                          z
                            .object({
                              char: z.string(),
                              kind: z.literal('char'),
                            })
                            .strict(),
                          z
                            .object({
                              scheme: z.string(),
                              start: z.number().int().gte(0).optional(),
                              kind: z.literal('number'),
                            })
                            .strict(),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      z.null(),
                    ])
                    .optional(),
                  lineSpacing: z.union([z.number(), z.null()]).optional(),
                  spaceBefore: z.union([z.number(), z.null()]).optional(),
                  spaceAfter: z.union([z.number(), z.null()]).optional(),
                  marginLeft: z.union([z.number(), z.null()]).optional(),
                  indent: z.union([z.number(), z.null()]).optional(),
                })
                .strict(),
              op: z.literal('formatParagraphs'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              props: z
                .object({
                  anchor: z.union([z.string(), z.null()]).optional(),
                  wrap: z.union([z.boolean(), z.null()]).optional(),
                  autofit: z.union([z.string(), z.null()]).optional(),
                  insets: z
                    .union([z.array(z.number()).min(4).max(4), z.null()])
                    .optional(),
                  columns: z
                    .union([z.number().int().gte(0), z.null()])
                    .optional(),
                  direction: z
                    .union([
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('horz'),
                          z.literal('vert'),
                          z.literal('vert270'),
                          z.literal('wordArtVert'),
                          z.literal('eaVert'),
                          z.literal('mongolianVert'),
                          z.literal('wordArtVertRtl'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      z.null(),
                    ])
                    .optional(),
                })
                .strict(),
              op: z.literal('formatBody'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              x: z.union([z.number(), z.null()]).optional(),
              y: z.union([z.number(), z.null()]).optional(),
              w: z.union([z.number(), z.null()]).optional(),
              h: z.union([z.number(), z.null()]).optional(),
              rotation: z.union([z.number(), z.null()]).optional(),
              flipH: z.union([z.boolean(), z.null()]).optional(),
              flipV: z.union([z.boolean(), z.null()]).optional(),
              op: z.literal('setTransform'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              fill: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.object({ kind: z.literal('none') }).strict(),
                  z
                    .object({
                      color: z.string(),
                      alpha: z.union([z.number(), z.null()]).optional(),
                      kind: z.literal('solid'),
                    })
                    .strict(),
                  z
                    .object({
                      colors: z.array(z.string()),
                      angle: z.number().optional(),
                      kind: z.literal('gradient'),
                    })
                    .strict(),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              op: z.literal('setFill'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              line: z
                .object({
                  none: z.boolean().optional(),
                  color: z.union([z.string(), z.null()]).optional(),
                  width: z.union([z.number(), z.null()]).optional(),
                  dash: z.union([z.string(), z.null()]).optional(),
                  tail: z.union([z.string(), z.null()]).optional(),
                  head: z.union([z.string(), z.null()]).optional(),
                })
                .strict(),
              op: z.literal('setLine'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              preset: z.string(),
              op: z.literal('setGeometry'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z
                    .object({
                      text: z.string().optional(),
                      kind: z.literal('textBox'),
                    })
                    .strict(),
                  z
                    .object({
                      preset: z.string(),
                      text: z.string().optional(),
                      kind: z.literal('shape'),
                    })
                    .strict(),
                  z
                    .object({
                      arrow: z.boolean().optional(),
                      kind: z.literal('line'),
                    })
                    .strict(),
                  z
                    .object({
                      data: z.string(),
                      description: z.string().optional(),
                      kind: z.literal('image'),
                    })
                    .strict(),
                  z
                    .object({
                      data: z.string(),
                      contentType: z.string(),
                      poster: z.string(),
                      description: z.string().optional(),
                      kind: z.literal('video'),
                    })
                    .strict(),
                  z
                    .object({
                      data: z.string(),
                      contentType: z.string(),
                      poster: z.string(),
                      description: z.string().optional(),
                      kind: z.literal('audio'),
                    })
                    .strict(),
                  z
                    .object({
                      cells: z.array(z.array(z.string())),
                      kind: z.literal('table'),
                    })
                    .strict(),
                  z
                    .object({
                      chartType: z.string(),
                      grouping: z.union([z.string(), z.null()]).optional(),
                      categories: z.array(z.string()),
                      series: z.array(
                        z
                          .object({
                            name: z.string(),
                            values: z.array(z.union([z.number(), z.null()])),
                          })
                          .strict()
                      ),
                      title: z.union([z.string(), z.null()]).optional(),
                      kind: z.literal('chart'),
                    })
                    .strict(),
                  z
                    .object({
                      layout: z.string(),
                      items: z
                        .union([
                          z.array(
                            z
                              .object({
                                text: z.string(),
                                level: z
                                  .number()
                                  .int()
                                  .gte(0)
                                  .lte(255)
                                  .optional(),
                              })
                              .strict()
                          ),
                          z.null(),
                        ])
                        .optional(),
                      colors: z.union([z.string(), z.null()]).optional(),
                      style: z.union([z.string(), z.null()]).optional(),
                      kind: z.literal('smartArt'),
                    })
                    .strict(),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              x: z.number(),
              y: z.number(),
              w: z.number(),
              h: z.number(),
              op: z.literal('addShape'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              op: z.literal('deleteShape'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              dx: z.number().optional(),
              dy: z.number().optional(),
              op: z.literal('duplicateShape'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              to: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('front'),
                  z.literal('back'),
                  z.literal('forward'),
                  z.literal('backward'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              op: z.literal('reorderShape'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              data: z.string(),
              op: z.literal('replaceImage'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              row: z.number().int().gte(0),
              col: z.number().int().gte(0),
              text: z.string(),
              op: z.literal('setCellText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              at: z.number().int().gte(0),
              op: z.literal('insertTableRow'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              row: z.number().int().gte(0),
              op: z.literal('deleteTableRow'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              at: z.number().int().gte(0),
              op: z.literal('insertTableColumn'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              col: z.number().int().gte(0),
              op: z.literal('deleteTableColumn'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              from: z
                .object({
                  row: z.number().int().gte(0),
                  col: z.number().int().gte(0),
                })
                .strict(),
              to: z
                .object({
                  row: z.number().int().gte(0),
                  col: z.number().int().gte(0),
                })
                .strict(),
              op: z.literal('mergeCells'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .object({
                  row: z.number().int().gte(0),
                  col: z.number().int().gte(0),
                })
                .strict(),
              op: z.literal('splitCell'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              from: z
                .object({
                  row: z.number().int().gte(0),
                  col: z.number().int().gte(0),
                })
                .strict(),
              to: z
                .object({
                  row: z.number().int().gte(0),
                  col: z.number().int().gte(0),
                })
                .strict(),
              fill: z
                .union([
                  z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.object({ kind: z.literal('none') }).strict(),
                      z
                        .object({
                          color: z.string(),
                          alpha: z.union([z.number(), z.null()]).optional(),
                          kind: z.literal('solid'),
                        })
                        .strict(),
                      z
                        .object({
                          colors: z.array(z.string()),
                          angle: z.number().optional(),
                          kind: z.literal('gradient'),
                        })
                        .strict(),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  z.null(),
                ])
                .optional(),
              borders: z
                .union([
                  z
                    .object({
                      edges: z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('all'),
                          z.literal('outside'),
                          z.literal('inside'),
                          z.literal('top'),
                          z.literal('bottom'),
                          z.literal('left'),
                          z.literal('right'),
                          z.literal('insideHorizontal'),
                          z.literal('insideVertical'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      line: z
                        .object({
                          none: z.boolean().optional(),
                          color: z.union([z.string(), z.null()]).optional(),
                          width: z.union([z.number(), z.null()]).optional(),
                          dash: z.union([z.string(), z.null()]).optional(),
                        })
                        .strict(),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              anchor: z.union([z.string(), z.null()]).optional(),
              margins: z
                .union([z.array(z.number()).min(4).max(4), z.null()])
                .optional(),
              op: z.literal('formatCells'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              style: z.union([z.string(), z.null()]).optional(),
              firstRow: z.union([z.boolean(), z.null()]).optional(),
              lastRow: z.union([z.boolean(), z.null()]).optional(),
              firstCol: z.union([z.boolean(), z.null()]).optional(),
              lastCol: z.union([z.boolean(), z.null()]).optional(),
              bandRow: z.union([z.boolean(), z.null()]).optional(),
              bandCol: z.union([z.boolean(), z.null()]).optional(),
              op: z.literal('setTableStyle'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              columnWidths: z.union([z.array(z.number()), z.null()]).optional(),
              rowHeights: z.union([z.array(z.number()), z.null()]).optional(),
              op: z.literal('setTableGrid'),
            })
            .strict(),
          z
            .object({
              layout: z.union([z.string(), z.null()]).optional(),
              after: z.union([z.number().int().gte(0), z.null()]).optional(),
              title: z.union([z.string(), z.null()]).optional(),
              body: z.union([z.string(), z.null()]).optional(),
              op: z.literal('addSlide'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              op: z.literal('duplicateSlide'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              op: z.literal('deleteSlide'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              to: z.number().int().gte(0),
              op: z.literal('moveSlide'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              hidden: z.boolean(),
              op: z.literal('setSlideHidden'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              text: z.string(),
              op: z.literal('setNotes'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              fill: z
                .union([
                  z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.object({ kind: z.literal('none') }).strict(),
                      z
                        .object({
                          color: z.string(),
                          alpha: z.union([z.number(), z.null()]).optional(),
                          kind: z.literal('solid'),
                        })
                        .strict(),
                      z
                        .object({
                          colors: z.array(z.string()),
                          angle: z.number().optional(),
                          kind: z.literal('gradient'),
                        })
                        .strict(),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  z.null(),
                ])
                .optional(),
              op: z.literal('setBackground'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              categories: z.array(z.string()),
              series: z.array(
                z
                  .object({
                    name: z.string(),
                    values: z.array(z.union([z.number(), z.null()])),
                  })
                  .strict()
              ),
              op: z.literal('setChartData'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              kind: z.string(),
              grouping: z.union([z.string(), z.null()]).optional(),
              op: z.literal('setChartType'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              title: z.union([z.string(), z.null()]).optional(),
              legend: z.union([z.string(), z.null()]).optional(),
              dataLabels: z.union([z.boolean(), z.null()]).optional(),
              seriesColors: z
                .union([
                  z.array(
                    z
                      .object({
                        series: z.number().int().gte(0),
                        color: z.string(),
                      })
                      .strict()
                  ),
                  z.null(),
                ])
                .optional(),
              op: z.literal('formatChart'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapes: z.array(z.number().int().gte(0)),
              op: z.literal('groupShapes'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              op: z.literal('ungroupShape'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              payload: z.string(),
              dx: z.number().optional(),
              dy: z.number().optional(),
              op: z.literal('pasteShapes'),
            })
            .strict(),
          z
            .object({
              after: z.union([z.number().int().gte(0), z.null()]).optional(),
              payload: z.string(),
              op: z.literal('pasteSlides'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              layout: z.string(),
              op: z.literal('setSlideLayout'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              kind: z.string(),
              durationMs: z
                .union([z.number().int().gte(0), z.null()])
                .optional(),
              direction: z.union([z.string(), z.null()]).optional(),
              advanceOnClick: z.union([z.boolean(), z.null()]).optional(),
              advanceAfterMs: z
                .union([z.number().int().gte(0), z.null()])
                .optional(),
              applyToAll: z.boolean().optional(),
              op: z.literal('setTransition'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              animations: z.array(
                z
                  .object({
                    shapeId: z.number().int().gte(0),
                    class: z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('entrance'),
                        z.literal('emphasis'),
                        z.literal('exit'),
                        z.literal('path'),
                        z.literal('media'),
                        z.literal('other'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    effect: z.string(),
                    start: z
                      .union([
                        z.any().superRefine((x, ctx) => {
                          const schemas = [
                            z.literal('onClick'),
                            z.literal('withPrevious'),
                            z.literal('afterPrevious'),
                          ];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                        z.null(),
                      ])
                      .optional(),
                    durationMs: z
                      .union([z.number().int().gte(0), z.null()])
                      .optional(),
                    delayMs: z
                      .union([z.number().int().gte(0), z.null()])
                      .optional(),
                    direction: z.union([z.string(), z.null()]).optional(),
                    paragraph: z
                      .union([z.number().int().gte(0), z.null()])
                      .optional(),
                    repeat: z
                      .union([
                        z.union([
                          z.number(),
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('untilNextClick'),
                              z.literal('untilEndOfSlide'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                        ]),
                        z.null(),
                      ])
                      .optional(),
                    path: z.union([z.string(), z.null()]).optional(),
                  })
                  .strict()
              ),
              op: z.literal('setAnimations'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              animation: z
                .object({
                  shapeId: z.number().int().gte(0),
                  class: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('entrance'),
                      z.literal('emphasis'),
                      z.literal('exit'),
                      z.literal('path'),
                      z.literal('media'),
                      z.literal('other'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  effect: z.string(),
                  start: z
                    .union([
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.literal('onClick'),
                          z.literal('withPrevious'),
                          z.literal('afterPrevious'),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      }),
                      z.null(),
                    ])
                    .optional(),
                  durationMs: z
                    .union([z.number().int().gte(0), z.null()])
                    .optional(),
                  delayMs: z
                    .union([z.number().int().gte(0), z.null()])
                    .optional(),
                  direction: z.union([z.string(), z.null()]).optional(),
                  paragraph: z
                    .union([z.number().int().gte(0), z.null()])
                    .optional(),
                  repeat: z
                    .union([
                      z.union([
                        z.number(),
                        z.any().superRefine((x, ctx) => {
                          const schemas = [
                            z.literal('untilNextClick'),
                            z.literal('untilEndOfSlide'),
                          ];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                      ]),
                      z.null(),
                    ])
                    .optional(),
                  path: z.union([z.string(), z.null()]).optional(),
                })
                .strict(),
              index: z.union([z.number().int().gte(0), z.null()]).optional(),
              op: z.literal('addAnimation'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapeIds: z
                .union([z.array(z.number().int().gte(0)), z.null()])
                .optional(),
              indexes: z
                .union([z.array(z.number().int().gte(0)), z.null()])
                .optional(),
              op: z.literal('removeAnimations'),
            })
            .strict(),
          z
            .object({
              find: z.string(),
              replace: z.string(),
              matchCase: z.boolean().optional(),
              wholeWord: z.boolean().optional(),
              slide: z.union([z.number().int().gte(0), z.null()]).optional(),
              op: z.literal('replaceText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              text: z.string(),
              op: z.literal('setAltText'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              name: z.string(),
              op: z.literal('setShapeName'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapes: z.array(z.number().int().gte(0)),
              link: z.string(),
              tip: z.union([z.string(), z.null()]).optional(),
              op: z.literal('setShapeLink'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              hidden: z.boolean(),
              op: z.literal('setShapeHidden'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapes: z.array(z.number().int().gte(0)),
              fromSlide: z.number().int().gte(0),
              fromShape: z.number().int().gte(0),
              op: z.literal('pasteFormat'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              left: z.union([z.number(), z.null()]).optional(),
              top: z.union([z.number(), z.null()]).optional(),
              right: z.union([z.number(), z.null()]).optional(),
              bottom: z.union([z.number(), z.null()]).optional(),
              mode: z
                .union([
                  z.any().superRefine((x, ctx) => {
                    const schemas = [z.literal('fill'), z.literal('fit')];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  z.null(),
                ])
                .optional(),
              op: z.literal('cropPicture'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapes: z.array(z.number().int().gte(0)),
              brightness: z.union([z.number(), z.null()]).optional(),
              contrast: z.union([z.number(), z.null()]).optional(),
              recolor: z.union([z.string(), z.null()]).optional(),
              transparency: z.union([z.number(), z.null()]).optional(),
              reset: z.boolean().optional(),
              op: z.literal('formatPicture'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapes: z.array(z.number().int().gte(0)),
              shadow: z
                .union([
                  z.union([
                    z.string(),
                    z
                      .object({
                        preset: z.union([z.string(), z.null()]).optional(),
                        color: z.union([z.string(), z.null()]).optional(),
                        transparency: z
                          .union([z.number(), z.null()])
                          .optional(),
                        sizePct: z.union([z.number(), z.null()]).optional(),
                        blurPt: z.union([z.number(), z.null()]).optional(),
                        distancePt: z.union([z.number(), z.null()]).optional(),
                        angleDeg: z.union([z.number(), z.null()]).optional(),
                      })
                      .strict(),
                  ]),
                  z.null(),
                ])
                .optional(),
              glow: z
                .union([
                  z.union([
                    z.string(),
                    z
                      .object({
                        color: z.union([z.string(), z.null()]).optional(),
                        sizePt: z.union([z.number(), z.null()]).optional(),
                        transparency: z
                          .union([z.number(), z.null()])
                          .optional(),
                      })
                      .strict(),
                  ]),
                  z.null(),
                ])
                .optional(),
              softEdge: z
                .union([
                  z.union([
                    z.string(),
                    z
                      .object({
                        sizePt: z.union([z.number(), z.null()]).optional(),
                      })
                      .strict(),
                  ]),
                  z.null(),
                ])
                .optional(),
              reflection: z
                .union([
                  z.union([
                    z.string(),
                    z
                      .object({
                        preset: z.union([z.string(), z.null()]).optional(),
                        transparency: z
                          .union([z.number(), z.null()])
                          .optional(),
                        sizePct: z.union([z.number(), z.null()]).optional(),
                        distancePt: z.union([z.number(), z.null()]).optional(),
                        blurPt: z.union([z.number(), z.null()]).optional(),
                      })
                      .strict(),
                  ]),
                  z.null(),
                ])
                .optional(),
              op: z.literal('setShapeEffects'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              paths: z.array(
                z
                  .object({
                    commands: z.array(
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z
                            .object({
                              x: z.number(),
                              y: z.number(),
                              cmd: z.literal('moveTo'),
                            })
                            .strict(),
                          z
                            .object({
                              x: z.number(),
                              y: z.number(),
                              cmd: z.literal('lineTo'),
                            })
                            .strict(),
                          z
                            .object({
                              x1: z.number(),
                              y1: z.number(),
                              x2: z.number(),
                              y2: z.number(),
                              x: z.number(),
                              y: z.number(),
                              cmd: z.literal('cubicBezTo'),
                            })
                            .strict(),
                          z
                            .object({
                              x1: z.number(),
                              y1: z.number(),
                              x: z.number(),
                              y: z.number(),
                              cmd: z.literal('quadBezTo'),
                            })
                            .strict(),
                          z
                            .object({
                              wR: z.number(),
                              hR: z.number(),
                              stAng: z.number(),
                              swAng: z.number(),
                              cmd: z.literal('arcTo'),
                            })
                            .strict(),
                          z.object({ cmd: z.literal('close') }).strict(),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      })
                    ),
                    fill: z
                      .union([
                        z.any().superRefine((x, ctx) => {
                          const schemas = [
                            z.literal('norm'),
                            z.literal('none'),
                            z.literal('lighten'),
                            z.literal('lightenLess'),
                            z.literal('darken'),
                            z.literal('darkenLess'),
                          ];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                        z.null(),
                      ])
                      .optional(),
                    stroke: z.union([z.boolean(), z.null()]).optional(),
                  })
                  .strict()
              ),
              fit: z.union([z.boolean(), z.null()]).optional(),
              op: z.literal('setCustomGeometry'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shapes: z.array(z.number().int().gte(0)),
              mode: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('union'),
                  z.literal('combine'),
                  z.literal('fragment'),
                  z.literal('intersect'),
                  z.literal('subtract'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              op: z.literal('mergeShapes'),
            })
            .strict(),
          z
            .object({
              colors: z.array(
                z.object({ slot: z.string(), color: z.string() }).strict()
              ),
              name: z.union([z.string(), z.null()]).optional(),
              op: z.literal('setThemeColors'),
            })
            .strict(),
          z
            .object({
              major: z.union([z.string(), z.null()]).optional(),
              minor: z.union([z.string(), z.null()]).optional(),
              name: z.union([z.string(), z.null()]).optional(),
              op: z.literal('setThemeFonts'),
            })
            .strict(),
          z
            .object({
              slides: z
                .union([z.array(z.number().int().gte(0)), z.null()])
                .optional(),
              slideNumber: z.union([z.boolean(), z.null()]).optional(),
              date: z.union([z.boolean(), z.null()]).optional(),
              dateText: z.union([z.string(), z.null()]).optional(),
              dateFormat: z.union([z.string(), z.null()]).optional(),
              footer: z.union([z.boolean(), z.null()]).optional(),
              footerText: z.union([z.string(), z.null()]).optional(),
              notOnTitle: z.boolean().optional(),
              op: z.literal('setHeaderFooter'),
            })
            .strict(),
          z
            .object({
              width: z.number(),
              height: z.number(),
              scale: z
                .union([
                  z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('none'),
                      z.literal('fit'),
                      z.literal('maximize'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  z.null(),
                ])
                .optional(),
              op: z.literal('setSlideSize'),
            })
            .strict(),
          z
            .object({
              name: z.string(),
              beforeSlide: z.number().int().gte(0),
              op: z.literal('addSection'),
            })
            .strict(),
          z
            .object({
              id: z.string(),
              name: z.string(),
              op: z.literal('renameSection'),
            })
            .strict(),
          z
            .object({
              id: z.string(),
              deleteSlides: z.boolean().optional(),
              op: z.literal('removeSection'),
            })
            .strict(),
          z
            .object({
              id: z.string(),
              toIndex: z.number().int().gte(0),
              op: z.literal('moveSection'),
            })
            .strict(),
          z
            .object({
              guides: z.array(
                z
                  .object({
                    orient: z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('horizontal'),
                        z.literal('vertical'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    position: z.number(),
                    color: z.union([z.string(), z.null()]).optional(),
                    id: z.union([z.number().int().gte(0), z.null()]).optional(),
                  })
                  .strict()
              ),
              op: z.literal('setGuides'),
            })
            .strict(),
          z
            .object({
              master: z.union([z.number().int().gte(0), z.null()]).optional(),
              after: z.union([z.number().int().gte(0), z.null()]).optional(),
              duplicate: z
                .union([z.number().int().gte(0), z.null()])
                .optional(),
              name: z.union([z.string(), z.null()]).optional(),
              op: z.literal('addLayout'),
            })
            .strict(),
          z
            .object({
              layout: z.number().int().gte(0),
              name: z.string(),
              op: z.literal('renameLayout'),
            })
            .strict(),
          z
            .object({
              layout: z.number().int().gte(0),
              op: z.literal('deleteLayout'),
            })
            .strict(),
          z
            .object({
              layout: z.number().int().gte(0),
              kind: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('content'),
                  z.literal('text'),
                  z.literal('picture'),
                  z.literal('chart'),
                  z.literal('table'),
                  z.literal('smartArt'),
                  z.literal('media'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              x: z.number(),
              y: z.number(),
              w: z.number(),
              h: z.number(),
              vertical: z.boolean().optional(),
              op: z.literal('insertPlaceholder'),
            })
            .strict(),
          z
            .object({
              layout: z.number().int().gte(0),
              title: z.union([z.boolean(), z.null()]).optional(),
              footers: z.union([z.boolean(), z.null()]).optional(),
              hideBackgroundGraphics: z
                .union([z.boolean(), z.null()])
                .optional(),
              op: z.literal('setLayoutOptions'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              style: z.number().int().gte(0).lte(255),
              op: z.literal('setBackgroundStyle'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              text: z.string(),
              author: z.string(),
              initials: z.union([z.string(), z.null()]).optional(),
              x: z.union([z.number(), z.null()]).optional(),
              y: z.union([z.number(), z.null()]).optional(),
              shape: z.union([z.number().int().gte(0), z.null()]).optional(),
              op: z.literal('addComment'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              comment: z.string(),
              text: z.string(),
              author: z.string(),
              initials: z.union([z.string(), z.null()]).optional(),
              op: z.literal('replyComment'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              comment: z.string(),
              text: z.string(),
              op: z.literal('editComment'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              comment: z.string(),
              resolved: z.boolean(),
              op: z.literal('resolveComment'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              comment: z.string(),
              op: z.literal('deleteComment'),
            })
            .strict(),
          z
            .object({
              slide: z.union([z.number().int().gte(0), z.null()]).optional(),
              op: z.literal('deleteAllComments'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.union([z.number().int().gte(0), z.null()]).optional(),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              at: z
                .union([
                  z
                    .object({
                      paragraph: z.number().int().gte(0),
                      offset: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              latex: z.string(),
              display: z.union([z.boolean(), z.null()]).optional(),
              op: z.literal('insertEquation'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              cell: z
                .union([
                  z
                    .object({
                      row: z.number().int().gte(0),
                      col: z.number().int().gte(0),
                    })
                    .strict(),
                  z.null(),
                ])
                .optional(),
              paragraph: z.number().int().gte(0),
              index: z.number().int().gte(0),
              latex: z.string(),
              display: z.union([z.boolean(), z.null()]).optional(),
              op: z.literal('setEquation'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              edit: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z
                    .object({
                      node: z.string(),
                      text: z.string(),
                      action: z.literal('setText'),
                    })
                    .strict(),
                  z
                    .object({
                      node: z.union([z.string(), z.null()]).optional(),
                      position: z
                        .intersection(
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('after'),
                              z.literal('before'),
                              z.literal('above'),
                              z.literal('below'),
                              z.literal('assistant'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.any().default('after')
                        )
                        .optional(),
                      text: z.string().optional(),
                      action: z.literal('addNode'),
                    })
                    .strict(),
                  z
                    .object({
                      node: z.string(),
                      action: z.literal('deleteNode'),
                    })
                    .strict(),
                  z
                    .object({ node: z.string(), action: z.literal('promote') })
                    .strict(),
                  z
                    .object({ node: z.string(), action: z.literal('demote') })
                    .strict(),
                  z
                    .object({ node: z.string(), action: z.literal('moveUp') })
                    .strict(),
                  z
                    .object({ node: z.string(), action: z.literal('moveDown') })
                    .strict(),
                  z
                    .object({
                      items: z.array(
                        z
                          .object({
                            text: z.string(),
                            level: z.number().int().gte(0).lte(255).optional(),
                          })
                          .strict()
                      ),
                      action: z.literal('setNodes'),
                    })
                    .strict(),
                  z
                    .object({
                      layout: z.string(),
                      action: z.literal('setLayout'),
                    })
                    .strict(),
                  z
                    .object({
                      colors: z.string(),
                      action: z.literal('setColors'),
                    })
                    .strict(),
                  z
                    .object({
                      style: z.string(),
                      action: z.literal('setStyle'),
                    })
                    .strict(),
                  z.object({ action: z.literal('reset') }).strict(),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              op: z.literal('editSmartArt'),
            })
            .strict(),
          z
            .object({
              slide: z.number().int().gte(0),
              shape: z.number().int().gte(0),
              to: z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('shapes'), z.literal('text')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              op: z.literal('convertSmartArt'),
            })
            .strict(),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      })
    )
    .optional(),
  saveAs: z
    .union([
      z.object({
        name: z.string(),
        projectId: z.union([z.string().uuid(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
});

export const PresentationEditOutcome = z.object({
  documentId: z.string(),
  created: z.array(
    z.object({
      slide: z.number().int().gte(0),
      shape: z.union([z.number().int().gte(0), z.null()]).optional(),
      section: z.union([z.string(), z.null()]).optional(),
    })
  ),
  structureChanged: z.boolean(),
  changedSlides: z.string(),
});

export const EditSpreadsheet = z.object({
  documentId: z.string(),
  expectedRevision: z.string(),
  operations: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z
          .object({
            sheetId: z.string(),
            cells: z.array(
              z.object({ address: z.string(), value: z.string() }).strict()
            ),
            type: z.literal('set_cells'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            style: z
              .object({
                numberFormat: z.union([z.string(), z.null()]).optional(),
                fontName: z.union([z.string(), z.null()]).optional(),
                borderTopStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderTopColor: z.union([z.string(), z.null()]).optional(),
                borderRightStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderRightColor: z.union([z.string(), z.null()]).optional(),
                borderBottomStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderBottomColor: z.union([z.string(), z.null()]).optional(),
                borderLeftStyle: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal(''),
                        z.literal('thin'),
                        z.literal('medium'),
                        z.literal('thick'),
                        z.literal('double'),
                        z.literal('dotted'),
                        z.literal('dashed'),
                        z.literal('dashDot'),
                        z.literal('dashDotDot'),
                        z.literal('slantDashDot'),
                        z.literal('hair'),
                        z.literal('mediumDashed'),
                        z.literal('mediumDashDot'),
                        z.literal('mediumDashDotDot'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                borderLeftColor: z.union([z.string(), z.null()]).optional(),
                bold: z.union([z.boolean(), z.null()]).optional(),
                italic: z.union([z.boolean(), z.null()]).optional(),
                underline: z.union([z.boolean(), z.null()]).optional(),
                strikethrough: z.union([z.boolean(), z.null()]).optional(),
                fontFamily: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('sans'),
                        z.literal('serif'),
                        z.literal('mono'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                fontSize: z
                  .union([z.number().int().gte(0).lte(255), z.null()])
                  .optional(),
                textColor: z.union([z.string(), z.null()]).optional(),
                fillColor: z.union([z.string(), z.null()]).optional(),
                horizontalAlign: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('auto'),
                        z.literal('left'),
                        z.literal('center'),
                        z.literal('right'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                indent: z
                  .union([z.number().int().gte(0).lte(255), z.null()])
                  .optional(),
                verticalAlign: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('top'),
                        z.literal('middle'),
                        z.literal('bottom'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                wrap: z.union([z.boolean(), z.null()]).optional(),
                borderTop: z.union([z.boolean(), z.null()]).optional(),
                borderRight: z.union([z.boolean(), z.null()]).optional(),
                borderBottom: z.union([z.boolean(), z.null()]).optional(),
                borderLeft: z.union([z.boolean(), z.null()]).optional(),
                decimals: z
                  .union([z.number().int().gte(-128).lte(127), z.null()])
                  .optional(),
                format: z
                  .union([
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('general'),
                        z.literal('number'),
                        z.literal('currency'),
                        z.literal('accounting'),
                        z.literal('percent'),
                        z.literal('date'),
                        z.literal('time'),
                        z.literal('scientific'),
                        z.literal('text'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    z.null(),
                  ])
                  .optional(),
                checkbox: z.union([z.boolean(), z.null()]).optional(),
              })
              .strict(),
            type: z.literal('format_cells'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            clearFormatting: z.union([z.boolean(), z.null()]).optional(),
            type: z.literal('clear_cells'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            sourceRange: z.string(),
            targetRange: z.string(),
            type: z.literal('fill_cells'),
          })
          .strict(),
        z.object({ name: z.string(), type: z.literal('add_sheet') }).strict(),
        z
          .object({
            sheetId: z.string(),
            name: z.string(),
            type: z.literal('rename_sheet'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            name: z.union([z.string(), z.null()]).optional(),
            type: z.literal('duplicate_sheet'),
          })
          .strict(),
        z
          .object({ sheetId: z.string(), type: z.literal('delete_sheet') })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            count: z.number().int().gte(0).lte(65535),
            type: z.literal('append_rows'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            columns: z.array(
              z
                .object({
                  column: z.string(),
                  width: z.number().int().gte(0).lte(65535),
                })
                .strict()
            ),
            type: z.literal('resize_columns'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            items: z.union([z.array(z.string()), z.null()]).optional(),
            source: z.union([z.string(), z.null()]).optional(),
            rejectInvalid: z.union([z.boolean(), z.null()]).optional(),
            colors: z
              .union([
                z.array(
                  z.object({ item: z.string(), color: z.string() }).strict()
                ),
                z.null(),
              ])
              .optional(),
            type: z.literal('set_dropdown'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            type: z.literal('clear_validation'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            type: z.literal('insert_checkboxes'),
          })
          .strict(),
        z
          .object({
            sheetId: z.string(),
            range: z.string(),
            name: z.union([z.string(), z.null()]).optional(),
            totalsRow: z.union([z.boolean(), z.null()]).optional(),
            columnTypes: z
              .union([
                z.array(
                  z.object({
                    column: z.string(),
                    type: z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.literal('text'),
                        z.literal('number'),
                        z.literal('percent'),
                        z.literal('currency'),
                        z.literal('date'),
                        z.literal('datetime'),
                        z.literal('time'),
                        z.literal('checkbox'),
                        z.literal('dropdown'),
                        z.literal('person'),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                  })
                ),
                z.null(),
              ])
              .optional(),
            type: z.literal('create_table'),
          })
          .strict(),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
});

export const EditTag = z.object({
  id: z.string().uuid(),
  property_definition_id: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  color: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('red'),
          z.literal('tomato'),
          z.literal('orange'),
          z.literal('amber'),
          z.literal('yellow'),
          z.literal('green'),
          z.literal('teal'),
          z.literal('blue'),
          z.literal('indigo'),
          z.literal('purple'),
          z.literal('pink'),
          z.literal('gray'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const EditTagResponse = z.object({
  id: z.string().uuid(),
  label: z.string(),
  color: z.union([z.string(), z.null()]).optional(),
  propertyDefinitionId: z.string().uuid(),
  summary: z.string(),
});

export const EditWordDocument = z.object({
  documentId: z.string(),
  operations: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z
          .object({
            paragraph: z.string(),
            find: z.string(),
            replace: z.string(),
            occurrence: z.union([z.number().int().gte(0), z.null()]).optional(),
            type: z.literal('replaceText'),
          })
          .strict(),
        z
          .object({
            paragraph: z.string(),
            text: z.string(),
            type: z.literal('setText'),
          })
          .strict(),
        z
          .object({
            paragraph: z.string(),
            find: z.union([z.string(), z.null()]).optional(),
            occurrence: z.union([z.number().int().gte(0), z.null()]).optional(),
            bold: z.union([z.boolean(), z.null()]).optional(),
            italic: z.union([z.boolean(), z.null()]).optional(),
            underline: z.union([z.boolean(), z.null()]).optional(),
            strikethrough: z.union([z.boolean(), z.null()]).optional(),
            type: z.literal('formatText'),
          })
          .strict(),
        z
          .object({
            after: z.union([z.string(), z.null()]).optional(),
            before: z.union([z.string(), z.null()]).optional(),
            text: z.string(),
            style: z.union([z.string(), z.null()]).optional(),
            type: z.literal('insertParagraph'),
          })
          .strict(),
        z.object({ id: z.string(), type: z.literal('delete') }).strict(),
        z
          .object({
            paragraph: z.string(),
            style: z.string(),
            type: z.literal('setStyle'),
          })
          .strict(),
        z
          .object({
            paragraph: z.string(),
            find: z.union([z.string(), z.null()]).optional(),
            occurrence: z.union([z.number().int().gte(0), z.null()]).optional(),
            text: z.string(),
            type: z.literal('addComment'),
          })
          .strict(),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
  trackChanges: z.union([z.boolean(), z.null()]).optional(),
  author: z.union([z.string(), z.null()]).optional(),
});

export const WordDocumentResponse = z.object({ content: z.string() });

export const ExportPipelineCsv = z.object({ pipelineId: z.string().uuid() });

export const ExportPipelineCsvResponse = z.object({
  documentId: z.string().uuid(),
  fileName: z.string(),
  rows: z.number().int().gte(0),
  complete: z.boolean(),
  summary: z.string(),
});

export const FindPipelineEntries = z.object({
  pipelineId: z.union([z.string().uuid(), z.null()]).optional(),
  filters: z
    .array(
      z.object({
        field: z.string(),
        value: z.union([z.string(), z.null()]).optional(),
        values: z.union([z.array(z.string()), z.null()]).optional(),
      })
    )
    .optional(),
  recordIds: z.array(z.string().uuid()).optional(),
  search: z.union([z.string(), z.null()]).optional(),
  sortBy: z.union([z.string(), z.null()]).optional(),
  descending: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const FindPipelineEntriesResponse = z.object({
  pipelines: z.array(
    z.object({
      pipelineId: z.string().uuid(),
      pipelineName: z.string(),
      version: z.union([z.number().int(), z.null()]).optional(),
      entries: z.array(
        z.object({
          entryId: z.string().uuid(),
          record: z.object({
            id: z.string().uuid(),
            name: z.union([z.string(), z.null()]).optional(),
            detail: z.union([z.string(), z.null()]).optional(),
          }),
          fields: z.record(z.any()),
        })
      ),
    })
  ),
  matched: z.number().int().gte(0),
  complete: z.boolean(),
  counts: z.array(
    z.object({
      field: z.string(),
      counts: z.array(
        z.object({ label: z.string(), count: z.number().int().gte(0) })
      ),
      empty: z.number().int().gte(0),
    })
  ),
  totals: z.array(z.object({ field: z.string(), sum: z.number() })),
  summary: z.string(),
});

export const GenerateImage = z.object({
  prompt: z.string(),
  aspectRatio: z
    .union([
      z.enum(['square', 'landscape', 'portrait', 'widescreen', 'tall']),
      z.null(),
    ])
    .optional(),
  referenceImages: z
    .union([
      z
        .array(
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.object({ id: z.string().uuid(), type: z.literal('document') }),
              z.object({
                id: z.string().uuid(),
                type: z.literal('staticFile'),
              }),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          })
        )
        .max(3),
      z.null(),
    ])
    .optional(),
});

export const GenerateImageResponse = z.object({
  staticFileId: z.string(),
  url: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().gte(0),
  width: z.union([z.number().int().gte(0), z.null()]).optional(),
  height: z.union([z.number().int().gte(0), z.null()]).optional(),
  markdown: z.union([z.string(), z.null()]).optional(),
  note: z.union([z.string(), z.null()]).optional(),
});

export const GenerateObject = z.intersection(
  z
    .object({
      prompt: z.string(),
      system: z.union([z.string(), z.null()]).optional(),
      model: z.union([z.string(), z.null()]).optional(),
      schema: z.any(),
    })
    .strict(),
  z.any()
);

export const GenerateObjectResponse = z.object({ object: z.any() });

export const GetBookingInsights = z
  .object({
    teamId: z.union([z.string().uuid(), z.null()]).optional(),
    query: z
      .object({
        from: z.string().date(),
        to: z.string().date(),
        timeZone: z.string(),
        eventTypeId: z.union([z.string().uuid(), z.null()]).optional(),
        hostId: z.union([z.string(), z.null()]).optional(),
      })
      .strict(),
  })
  .strict();

export const BookingInsights = z.object({
  asOf: z.string().datetime({ offset: true }),
  previousFrom: z.string().date(),
  previousTo: z.string().date(),
  current: z.object({
    counts: z.object({
      total: z.number().int().gte(0),
      completed: z.number().int().gte(0),
      rescheduled: z.number().int().gte(0),
      cancelled: z.number().int().gte(0),
      guestNoShow: z.number().int().gte(0),
      hostNoShow: z.number().int().gte(0),
    }),
    meetingMinutes: z.number(),
    averageMinutes: z.number(),
    days: z.record(
      z.object({
        total: z.number().int().gte(0),
        completed: z.number().int().gte(0),
        rescheduled: z.number().int().gte(0),
        cancelled: z.number().int().gte(0),
        guestNoShow: z.number().int().gte(0),
        hostNoShow: z.number().int().gte(0),
      })
    ),
    hours: z.array(z.number().int().gte(0)),
    eventCounts: z.record(z.number().int().gte(0)),
    hostCounts: z.record(z.number().int().gte(0)),
  }),
  previous: z.object({
    counts: z.object({
      total: z.number().int().gte(0),
      completed: z.number().int().gte(0),
      rescheduled: z.number().int().gte(0),
      cancelled: z.number().int().gte(0),
      guestNoShow: z.number().int().gte(0),
      hostNoShow: z.number().int().gte(0),
    }),
    meetingMinutes: z.number(),
    averageMinutes: z.number(),
    days: z.record(
      z.object({
        total: z.number().int().gte(0),
        completed: z.number().int().gte(0),
        rescheduled: z.number().int().gte(0),
        cancelled: z.number().int().gte(0),
        guestNoShow: z.number().int().gte(0),
        hostNoShow: z.number().int().gte(0),
      })
    ),
    hours: z.array(z.number().int().gte(0)),
    eventCounts: z.record(z.number().int().gte(0)),
    hostCounts: z.record(z.number().int().gte(0)),
  }),
});

export const GetBookingRescheduleSlots = z
  .object({ bookingId: z.string().uuid(), date: z.string().date() })
  .strict();

export const BookingSlotsResult = z.object({
  slots: z.array(
    z.object({
      startsAt: z.string().datetime({ offset: true }),
      endsAt: z.string().datetime({ offset: true }),
    })
  ),
});

export const GetBotWebhooks = z.object({ botId: z.string().uuid() });

export const GetBotWebhooksResponse = z.object({
  botId: z.string().uuid(),
  credentialHeader: z.string(),
  credentialScopeHeader: z.string(),
  credentialScope: z.string(),
  webhooks: z.array(
    z.object({
      channelId: z.string().uuid(),
      channelName: z.union([z.string(), z.null()]).optional(),
      webhookUrl: z.string(),
    })
  ),
  summary: z.string(),
});

export const GetCompany = z.object({ company_id: z.string().uuid() });

export const GetCompanyResponse = z.object({
  id: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  domains: z.array(z.string()),
  hidden: z.boolean(),
  emailSync: z.boolean(),
  firstInteraction: z.string().datetime({ offset: true }),
  lastInteraction: z.string().datetime({ offset: true }),
  stage: z
    .union([
      z.object({ optionId: z.string().uuid(), label: z.string() }),
      z.null(),
    ])
    .optional(),
  ownerUserId: z.union([z.string(), z.null()]).optional(),
  revenue: z.union([z.number(), z.null()]).optional(),
  contacts: z.array(
    z.object({
      id: z.string().uuid(),
      email: z.union([z.string(), z.null()]).optional(),
      name: z.union([z.string(), z.null()]).optional(),
      lastInteraction: z.string().datetime({ offset: true }),
    })
  ),
  properties: z.array(
    z.object({
      propertyDefinitionId: z.string().uuid(),
      displayName: z.string(),
      dataType: z.string(),
      isMultiSelect: z.boolean(),
      isSystem: z.boolean(),
      currentValue: z.any().optional(),
      options: z.array(
        z.object({ id: z.string().uuid(), displayValue: z.string() })
      ),
    })
  ),
  summary: z.string(),
});

export const GetEntityProperties = z.object({
  entity_id: z.string(),
  entity_type: z.enum([
    'document',
    'initiative',
    'project',
    'thread',
    'channel',
    'call',
    'user',
    'company',
    'chat',
  ]),
});

export const GetEntityPropertiesResponse = z.object({
  properties: z.array(
    z.object({
      propertyDefinitionId: z.string().uuid(),
      displayName: z.string(),
      dataType: z.string(),
      isMultiSelect: z.boolean(),
      isSystem: z.boolean(),
      currentValue: z.any().optional(),
      currentValueLabels: z.union([z.array(z.string()), z.null()]).optional(),
      scope: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('personal'), z.literal('team')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
      options: z
        .array(
          z.object({
            id: z.string().uuid(),
            displayOrder: z.number().int(),
            displayValue: z.string(),
          })
        )
        .optional(),
    })
  ),
  summary: z.string(),
});

export const GetTeamAvailability = z.object({
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
  userIds: z.union([z.array(z.string()), z.null()]).optional(),
});

export const TeamAvailability = z.object({
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
  members: z.array(
    z.object({
      userId: z.string(),
      busy: z.array(
        z.object({
          start: z.string().datetime({ offset: true }),
          end: z.string().datetime({ offset: true }),
        })
      ),
      coverage: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('complete'), z.literal('unknown')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      unknownReasons: z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('hidden'),
            z.literal('unavailable'),
            z.literal('truncated'),
            z.literal('missing_time_zone'),
            z.literal('invalid_time_zone'),
            z.literal('invalid_interval'),
            z.literal('conflicting_copies'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
    })
  ),
  freeWindows: z
    .union([
      z.array(
        z.object({
          start: z.string().datetime({ offset: true }),
          end: z.string().datetime({ offset: true }),
        })
      ),
      z.null(),
    ])
    .optional(),
  unknownUserIds: z.array(z.string()),
  complete: z.boolean(),
  summary: z.string(),
});

export const GetThread = z.object({
  threadId: z.string().uuid(),
  limit: z.union([z.number().int(), z.null()]).optional(),
});

export const GetThreadResponse = z.object({
  threadId: z.string().uuid(),
  inbox: z.union([z.string(), z.null()]).optional(),
  isRead: z.boolean(),
  labels: z.array(z.string()),
  messages: z.array(
    z.object({
      id: z.string().uuid(),
      subject: z.union([z.string(), z.null()]).optional(),
      from: z
        .union([
          z.object({
            email: z.string(),
            name: z.union([z.string(), z.null()]).optional(),
          }),
          z.null(),
        ])
        .optional(),
      to: z.array(
        z.object({
          email: z.string(),
          name: z.union([z.string(), z.null()]).optional(),
        })
      ),
      cc: z.array(
        z.object({
          email: z.string(),
          name: z.union([z.string(), z.null()]).optional(),
        })
      ),
      bodyParsed: z.union([z.string(), z.null()]).optional(),
      date: z.union([z.string(), z.null()]).optional(),
      labels: z.array(z.string()),
    })
  ),
  summary: z.string(),
});

export const GetUserActivity = z.object({
  userId: z.union([z.string(), z.null()]).optional(),
  start: z.string().date(),
  end: z.string().date(),
});

export const ActivityReports = z.object({
  reports: z.array(
    z.object({
      granularity: z.enum(['day', 'week', 'month', 'summary']),
      periodStart: z.string().date(),
      periodEnd: z.string().date(),
      content: z.string(),
    })
  ),
});

export const ImportCompaniesCsv = z.object({
  csv: z.union([z.string(), z.null()]).optional(),
  documentId: z.union([z.string().uuid(), z.null()]).optional(),
  pipelineId: z.union([z.string().uuid(), z.null()]).optional(),
  newPipelineName: z.union([z.string(), z.null()]).optional(),
  shareNewPipeline: z.union([z.boolean(), z.null()]).optional(),
});

export const ImportCompaniesCsvResponse = z.object({
  created: z.number().int().gte(0),
  existing: z.number().int().gte(0),
  skippedRows: z.number().int().gte(0),
  failedCount: z.number().int().gte(0),
  failed: z.array(z.object({ key: z.string(), reason: z.string() })),
  pipeline: z
    .union([
      z.object({
        pipelineId: z.string().uuid(),
        pipelineName: z.string(),
        added: z.number().int().gte(0),
        alreadyPresent: z.number().int().gte(0),
        createdRecords: z.number().int().gte(0),
        unresolvedCount: z.number().int().gte(0),
        unresolved: z.array(z.object({ key: z.string(), reason: z.string() })),
        invalidCount: z.number().int().gte(0),
        invalid: z.array(z.object({ key: z.string(), reason: z.string() })),
        skippedRows: z.number().int().gte(0),
        filledFields: z.array(z.string()),
        ignoredColumns: z.array(z.string()),
        addedOptions: z.array(
          z.object({ field: z.string(), labels: z.array(z.string()) })
        ),
      }),
      z.null(),
    ])
    .optional(),
  summary: z.string(),
});

export const ImportFromJira = z.object({
  action: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('plan'),
      z.literal('status'),
      z.literal('cancel'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  sharing: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('team'),
          z.literal('personal'),
          z.literal('ai'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const TrackerImportResponse = z.object({
  report: z.object({
    warnings: z.array(z.string()).optional(),
    issues: z.number().int().gte(0).optional(),
    projects: z.number().int().gte(0).optional(),
    status: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('running'),
            z.literal('ready'),
            z.literal('importing'),
            z.literal('completed'),
            z.literal('failed'),
            z.literal('dismissed'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    error: z.union([z.string(), z.null()]).optional(),
    scanned: z
      .union([
        z.object({
          pages: z.number().int().gte(0),
          dataSources: z.number().int().gte(0),
        }),
        z.null(),
      ])
      .optional(),
    pages: z.number().int().gte(0),
    dataSources: z.number().int().gte(0),
    deciding: z.number().int().gte(0).optional(),
    onTeam: z.boolean().optional(),
    copy: z.number().int().gte(0),
    tasks: z.number().int().gte(0),
    skip: z.number().int().gte(0),
    hand: z.number().int().gte(0),
    documents: z.number().int().gte(0),
    databases: z.number().int().gte(0),
    databaseRows: z.number().int().gte(0),
    taskRows: z.number().int().gte(0),
    imported: z.number().int().gte(0),
    waiting: z.number().int().gte(0),
    writing: z.number().int().gte(0),
    waitingOnTeammates: z.number().int().gte(0).optional(),
    sharedWithTeam: z.number().int().gte(0).optional(),
    alreadyInMacro: z.number().int().gte(0).optional(),
    alreadyImportedBy: z
      .array(z.object({ by: z.string(), count: z.number().int().gte(0) }))
      .optional(),
    reviewId: z.union([z.string().uuid(), z.null()]).optional(),
    handItems: z
      .array(
        z.object({
          importId: z.string().uuid(),
          title: z.string(),
          kind: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('issue'),
              z.literal('project'),
              z.literal('document'),
              z.literal('page'),
              z.literal('database'),
              z.literal('data_source'),
              z.literal('row'),
              z.literal('jira_issue'),
              z.literal('jira_project'),
              z.literal('jira_epic'),
              z.literal('channel'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          url: z.union([z.string(), z.null()]).optional(),
          reason: z.union([z.string(), z.null()]).optional(),
        })
      )
      .optional(),
    failures: z
      .array(
        z.object({
          importId: z.string().uuid(),
          title: z.string(),
          kind: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('issue'),
              z.literal('project'),
              z.literal('document'),
              z.literal('page'),
              z.literal('database'),
              z.literal('data_source'),
              z.literal('row'),
              z.literal('jira_issue'),
              z.literal('jira_project'),
              z.literal('jira_epic'),
              z.literal('channel'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          url: z.union([z.string(), z.null()]).optional(),
          reason: z.union([z.string(), z.null()]).optional(),
        })
      )
      .optional(),
  }),
  message: z.string(),
});

export const ImportFromLinear = z.object({
  action: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('plan'),
      z.literal('status'),
      z.literal('cancel'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  sharing: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('team'),
          z.literal('personal'),
          z.literal('ai'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ImportFromNotion = z.object({
  action: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('plan'),
      z.literal('status'),
      z.literal('cancel'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  roots: z.array(z.string()).optional(),
  sharing: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('team'),
          z.literal('personal'),
          z.literal('ai'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ImportFromNotionResponse = z.object({
  report: z.object({
    warnings: z.array(z.string()).optional(),
    issues: z.number().int().gte(0).optional(),
    projects: z.number().int().gte(0).optional(),
    status: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('running'),
            z.literal('ready'),
            z.literal('importing'),
            z.literal('completed'),
            z.literal('failed'),
            z.literal('dismissed'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    error: z.union([z.string(), z.null()]).optional(),
    scanned: z
      .union([
        z.object({
          pages: z.number().int().gte(0),
          dataSources: z.number().int().gte(0),
        }),
        z.null(),
      ])
      .optional(),
    pages: z.number().int().gte(0),
    dataSources: z.number().int().gte(0),
    deciding: z.number().int().gte(0).optional(),
    onTeam: z.boolean().optional(),
    copy: z.number().int().gte(0),
    tasks: z.number().int().gte(0),
    skip: z.number().int().gte(0),
    hand: z.number().int().gte(0),
    documents: z.number().int().gte(0),
    databases: z.number().int().gte(0),
    databaseRows: z.number().int().gte(0),
    taskRows: z.number().int().gte(0),
    imported: z.number().int().gte(0),
    waiting: z.number().int().gte(0),
    writing: z.number().int().gte(0),
    waitingOnTeammates: z.number().int().gte(0).optional(),
    sharedWithTeam: z.number().int().gte(0).optional(),
    alreadyInMacro: z.number().int().gte(0).optional(),
    alreadyImportedBy: z
      .array(z.object({ by: z.string(), count: z.number().int().gte(0) }))
      .optional(),
    reviewId: z.union([z.string().uuid(), z.null()]).optional(),
    handItems: z
      .array(
        z.object({
          importId: z.string().uuid(),
          title: z.string(),
          kind: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('issue'),
              z.literal('project'),
              z.literal('document'),
              z.literal('page'),
              z.literal('database'),
              z.literal('data_source'),
              z.literal('row'),
              z.literal('jira_issue'),
              z.literal('jira_project'),
              z.literal('jira_epic'),
              z.literal('channel'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          url: z.union([z.string(), z.null()]).optional(),
          reason: z.union([z.string(), z.null()]).optional(),
        })
      )
      .optional(),
    failures: z
      .array(
        z.object({
          importId: z.string().uuid(),
          title: z.string(),
          kind: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('issue'),
              z.literal('project'),
              z.literal('document'),
              z.literal('page'),
              z.literal('database'),
              z.literal('data_source'),
              z.literal('row'),
              z.literal('jira_issue'),
              z.literal('jira_project'),
              z.literal('jira_epic'),
              z.literal('channel'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          url: z.union([z.string(), z.null()]).optional(),
          reason: z.union([z.string(), z.null()]).optional(),
        })
      )
      .optional(),
  }),
  message: z.string(),
});

export const ImportNotionPage = z.object({ pageUrl: z.string() });

export const ImportNotionPageResponse = z.object({
  outcome: z.string(),
  entity: z.object({
    id: z.string().uuid(),
    source: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('linear'),
        z.literal('jira'),
        z.literal('notion'),
        z.literal('slack'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    foreignId: z.string(),
    status: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.literal('staged'),
        z.literal('importing'),
        z.literal('imported'),
        z.literal('discarded'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    label: z.string(),
    entityId: z.union([z.string(), z.null()]).optional(),
    entityType: z.union([z.string(), z.null()]).optional(),
    importedByTeammate: z.boolean(),
  }),
  message: z.string(),
});

export const ImportPipelineCsv = z.object({
  pipelineId: z.string().uuid(),
  csv: z.union([z.string(), z.null()]).optional(),
  documentId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const ImportPipelineCsvResponse = z.object({
  pipelineId: z.string().uuid(),
  pipelineName: z.string(),
  added: z.number().int().gte(0),
  alreadyPresent: z.number().int().gte(0),
  createdRecords: z.number().int().gte(0),
  unresolvedCount: z.number().int().gte(0),
  unresolved: z.array(z.object({ key: z.string(), reason: z.string() })),
  invalidCount: z.number().int().gte(0),
  invalid: z.array(z.object({ key: z.string(), reason: z.string() })),
  skippedRows: z.number().int().gte(0),
  filledFields: z.array(z.string()),
  ignoredColumns: z.array(z.string()),
  addedOptions: z.array(
    z.object({ field: z.string(), labels: z.array(z.string()) })
  ),
  summary: z.string(),
});

export const IssueBotCredential = z.object({
  botId: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  expiresAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const IssueBotCredentialResponse = z.object({
  botId: z.string().uuid(),
  label: z.union([z.string(), z.null()]).optional(),
  expiresAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  summary: z.string(),
});

export const ListAgentUpdates = z.record(z.never());

export const AgentUpdates = z.object({
  items: z.array(
    z.object({
      sessionId: z.string().uuid(),
      sessionName: z.string(),
      harness: z.string(),
      pullRequestUrl: z.union([z.string(), z.null()]).optional(),
      kind: z.enum(['permission', 'elicitation', 'tool_approval', 'stalled']),
      summary: z.string(),
      since: z.string().datetime({ offset: true }),
      options: z.array(z.string()),
    })
  ),
  truncated: z.boolean(),
  truncationNotice: z.union([z.string(), z.null()]).optional(),
});

export const ListAgents = z.record(z.any());

export const ListAgentsResponse = z.object({
  agents: z.array(
    z.object({
      bot: z.object({
        botId: z.string().uuid(),
        owner: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.object({ user_id: z.string(), type: z.literal('user') }),
            z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        name: z.string(),
        handle: z.string(),
        description: z.union([z.string(), z.null()]).optional(),
        avatarUrl: z.union([z.string(), z.null()]).optional(),
        hasAgent: z.boolean(),
      }),
      instructions: z.string(),
      harness: z.string(),
      harnessId: z.union([z.string().uuid(), z.null()]).optional(),
      defaultModel: z.string(),
      channelScope: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('all'), z.literal('selected')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      channelIds: z.array(z.string().uuid()),
      mcpScope: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('owner_connections'), z.literal('selected')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      mcpServers: z.array(
        z.object({ appSlug: z.string(), serverName: z.string() })
      ),
      autoAcceptPermissions: z.union([z.boolean(), z.null()]).optional(),
      isCoding: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListBookingLinks = z
  .object({
    teamId: z.union([z.string().uuid(), z.null()]).optional(),
    query: z.union([z.string(), z.null()]).optional(),
  })
  .strict();

export const ListBookingLinksResult = z.object({
  teamIds: z.array(z.string().uuid()),
  userId: z.string(),
  profileId: z.string().uuid(),
  revision: z.number().int(),
  schedules: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      timeZone: z.string(),
      weekly: z.array(
        z.object({
          day: z.number().int().gte(0).lte(255),
          windows: z.array(z.object({ start: z.string(), end: z.string() })),
        })
      ),
      overrides: z.array(
        z.object({
          date: z.string().date(),
          windows: z.array(z.object({ start: z.string(), end: z.string() })),
        })
      ),
    })
  ),
  links: z.array(
    z.object({
      profileId: z.string().uuid(),
      eventTypeId: z.string().uuid(),
      revision: z.number().int(),
      draft: z
        .object({
          event: z
            .object({
              title: z.string(),
              slug: z.string(),
              description: z.string(),
              durationMinutes: z.number().int().gte(0).lte(65535),
              location: z.string(),
              macroCall: z.boolean().optional(),
              googleMeet: z.boolean(),
              enabled: z.boolean(),
              mode: z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('individual'),
                  z.literal('collective'),
                  z.literal('roundRobin'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              hosts: z.array(z.string()),
              beforeMinutes: z.number().int().gte(0).lte(65535),
              afterMinutes: z.number().int().gte(0).lte(65535),
              noticeMinutes: z.number().int().gte(0).lte(65535),
              horizonDays: z.number().int().gte(0).lte(65535),
              intervalMinutes: z.number().int().gte(0).lte(65535),
              dailyLimit: z
                .union([z.number().int().gte(0).lte(65535), z.null()])
                .optional(),
              requiresConfirmation: z.boolean(),
              questions: z.array(
                z.object({
                  id: z.string().uuid(),
                  label: z.string(),
                  required: z.boolean(),
                })
              ),
            })
            .strict(),
          schedule: z
            .object({
              name: z.string(),
              timeZone: z.string(),
              weekly: z.array(
                z.object({
                  day: z.number().int().gte(0).lte(255),
                  windows: z.array(
                    z.object({ start: z.string(), end: z.string() })
                  ),
                })
              ),
              overrides: z.array(
                z.object({
                  date: z.string().date(),
                  windows: z.array(
                    z.object({ start: z.string(), end: z.string() })
                  ),
                })
              ),
            })
            .strict(),
        })
        .strict(),
      url: z.string(),
    })
  ),
});

export const ListBookings = z
  .object({
    teamId: z.union([z.string().uuid(), z.null()]).optional(),
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
  })
  .strict();

export const BookingsResult = z.object({
  bookings: z.array(
    z.object({
      id: z.string().uuid(),
      eventTypeId: z.string().uuid(),
      title: z.string(),
      name: z.string(),
      email: z.string(),
      startsAt: z.string().datetime({ offset: true }),
      endsAt: z.string().datetime({ offset: true }),
      timeZone: z.string(),
      hosts: z.array(z.string()),
      status: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('pending'),
          z.literal('confirmed'),
          z.literal('cancelled'),
          z.literal('processing'),
          z.literal('failed'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      attendance: z
        .intersection(
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unknown'),
              z.literal('attended'),
              z.literal('guestNoShow'),
              z.literal('hostNoShow'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.any().default('unknown')
        )
        .optional(),
      rescheduleCount: z.number().int().gte(0).lte(65535).optional(),
      rescheduledAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      location: z.string(),
      answers: z.record(z.string()),
      questionLabels: z.record(z.string()).optional(),
    })
  ),
});

export const ListBots = z.record(z.any());

export const ListBotsResponse = z.object({
  bots: z.array(
    z.object({
      botId: z.string().uuid(),
      owner: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({ user_id: z.string(), type: z.literal('user') }),
          z.object({ team_id: z.string().uuid(), type: z.literal('team') }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      name: z.string(),
      handle: z.string(),
      description: z.union([z.string(), z.null()]).optional(),
      avatarUrl: z.union([z.string(), z.null()]).optional(),
      hasAgent: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListCalendarEvents = z.object({
  start: z.string().datetime({ offset: true }),
  end: z.string().datetime({ offset: true }),
});

export const ListCalendarEventsResponse = z.object({
  events: z.array(
    z.object({
      eventId: z.string().uuid(),
      title: z.string(),
      start: z.string(),
      end: z.string(),
      isAllDay: z.boolean(),
      timeZone: z.union([z.string(), z.null()]).optional(),
      location: z.union([z.string(), z.null()]).optional(),
      description: z.union([z.string(), z.null()]).optional(),
      status: z.string(),
      eventType: z.union([z.string(), z.null()]).optional(),
      isRecurring: z.boolean(),
      recurrenceId: z.union([z.string(), z.null()]).optional(),
      attendees: z.array(
        z.object({
          email: z.string(),
          responseStatus: z.string(),
          isOrganizer: z.boolean(),
          isOptional: z.boolean(),
        })
      ),
      attendeeCount: z.number().int().gte(0),
      myResponse: z.union([z.string(), z.null()]).optional(),
      organizerEmail: z.union([z.string(), z.null()]).optional(),
      conferenceUrl: z.union([z.string(), z.null()]).optional(),
      isReadOnly: z.boolean(),
      viewerCan: z.object({
        editDetails: z.boolean(),
        inviteGuests: z.boolean(),
        removeGuests: z.boolean(),
        setGuestPolicy: z.boolean(),
        rsvp: z.boolean(),
        organize: z.boolean(),
      }),
      calendarId: z.union([z.string().uuid(), z.null()]).optional(),
      calendarName: z.union([z.string(), z.null()]).optional(),
      contributesToAvailability: z.union([z.boolean(), z.null()]).optional(),
      inbox: z.union([z.string(), z.null()]).optional(),
      myRole: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('organizer'), z.literal('guest')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
      copies: z
        .array(
          z.object({
            calendarId: z.string().uuid(),
            calendarName: z.union([z.string(), z.null()]).optional(),
            inbox: z.union([z.string(), z.null()]).optional(),
            title: z.string(),
            isReadOnly: z.boolean(),
          })
        )
        .optional(),
    })
  ),
  truncated: z.boolean(),
  syncStatus: z.string(),
  summary: z.string(),
});

export const ListCalendars = z.record(z.any());

export const ListCalendarsToolResponse = z.object({
  calendars: z.array(
    z.object({
      provider: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('google'), z.literal('outlook')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      capabilities: z.object({
        conferenceProvider: z
          .union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('microsoft_teams'),
                z.literal('google_meet'),
                z.literal('other'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
          .optional(),
        removeConference: z.boolean(),
        autoDecline: z.boolean(),
        emailReminders: z.boolean(),
        customRecurrence: z.boolean(),
        resetRsvp: z.boolean(),
        guestPolicy: z.boolean(),
      }),
      calendarId: z.string().uuid(),
      name: z.string(),
      emailAddress: z.string(),
      isPrimary: z.boolean(),
      isWritable: z.boolean(),
      contributesToAvailability: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListCodingAgents = z.record(z.never());

export const ListCodingAgentsResponse = z.object({
  agents: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      description: z.union([z.string(), z.null()]).optional(),
      instructions: z.string(),
      harness: z.string(),
      model: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ListCompanies = z.object({
  search: z.union([z.string(), z.null()]).optional(),
  stage: z.union([z.string(), z.null()]).optional(),
  owner_user_id: z.union([z.string(), z.null()]).optional(),
  include_hidden: z.union([z.boolean(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ListCompaniesResponse = z.object({
  companies: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.union([z.string(), z.null()]).optional(),
      domains: z.array(z.string()),
      hidden: z.boolean(),
      lastInteraction: z.string().datetime({ offset: true }),
      stage: z
        .union([
          z.object({ optionId: z.string().uuid(), label: z.string() }),
          z.null(),
        ])
        .optional(),
      ownerUserId: z.union([z.string(), z.null()]).optional(),
      revenue: z.union([z.number(), z.null()]).optional(),
    })
  ),
  summary: z.string(),
});

export const ListDatabases = z.record(z.any());

export const ListDatabasesResponse = z.object({
  databases: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      grant: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('view'),
          z.literal('comment'),
          z.literal('edit'),
          z.literal('owner'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      tables: z.array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          sqlName: z.string(),
        })
      ),
    })
  ),
  summary: z.string(),
});

export const ListEntities = z.object({
  includeTypes: z
    .union([
      z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('calendar_event'),
            z.literal('document'),
            z.literal('agent_session'),
            z.literal('project'),
            z.literal('email'),
            z.literal('channel'),
            z.literal('channel_thread'),
            z.literal('call'),
            z.literal('foreign_entity'),
            z.literal('ai_chat'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
      z.null(),
    ])
    .optional(),
  favoritesOnly: z.boolean().optional(),
  sortBy: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [
        z.literal('recently_viewed'),
        z.literal('recently_updated'),
        z.literal('recently_created'),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  df: z.any().optional(),
  pf: z.any().optional(),
  asf: z.any().optional(),
  emailPreset: z.union([z.literal('signal'), z.null()]).optional(),
  ef: z.any().optional(),
  chanf: z.any().optional(),
  cthf: z.any().optional(),
  callf: z.any().optional(),
  fef: z.any().optional(),
  propf: z.any().optional(),
  emailView: z.union([z.string(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ListEntitiesResponse = z.object({
  items: z.array(
    z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({
          id: z.string().uuid(),
          title: z.string(),
          status: z.string(),
          location: z.union([z.string(), z.null()]).optional(),
          conferenceUrl: z.union([z.string(), z.null()]).optional(),
          conferenceProvider: z.union([z.string(), z.null()]).optional(),
          time: z.any(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('calendarEvent'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          fileType: z.union([z.string(), z.null()]).optional(),
          subType: z.union([z.string(), z.null()]).optional(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('document'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          ownerId: z.string(),
          botId: z.string().uuid(),
          archivedBy: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('user'),
                  z.literal('pull_request_merged'),
                  z.literal('runtime_closed'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
          status: z.string(),
          turnState: z.union([z.string(), z.null()]).optional(),
          repoUrl: z.union([z.string(), z.null()]).optional(),
          type: z.literal('agentSession'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('project'),
        }),
        z.object({
          id: z.string().uuid(),
          subject: z.union([z.string(), z.null()]).optional(),
          snippet: z.union([z.string(), z.null()]).optional(),
          senderName: z.union([z.string(), z.null()]).optional(),
          senderEmail: z.union([z.string(), z.null()]).optional(),
          inboxVisible: z.boolean(),
          isRead: z.boolean(),
          isDraft: z.boolean(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('email'),
        }),
        z.object({
          id: z.string().uuid(),
          name: z.union([z.string(), z.null()]).optional(),
          type: z.literal('channel'),
        }),
        z.object({
          id: z.string().uuid(),
          channelId: z.string().uuid(),
          type: z.literal('channelThread'),
        }),
        z.object({
          id: z.string().uuid(),
          createdBy: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
          type: z.literal('call'),
        }),
        z.object({
          id: z.string().uuid(),
          foreignEntityId: z.string(),
          foreignEntitySource: z.string(),
          metadata: z.any(),
          type: z.literal('foreignEntity'),
        }),
        z.object({
          type: z.literal('aiChat'),
          id: z.string().uuid(),
          name: z.string(),
          tags: z
            .array(
              z.object({
                label: z.string(),
                scope: z.any().superRefine((x, ctx) => {
                  const schemas = [z.literal('personal'), z.literal('team')];
                  const errors = schemas.reduce<z.ZodError[]>(
                    (errors, schema) =>
                      ((result) =>
                        result.error ? [...errors, result.error] : errors)(
                        schema.safeParse(x)
                      ),
                    []
                  );
                  if (schemas.length - errors.length !== 1) {
                    ctx.addIssue({
                      path: ctx.path,
                      code: 'invalid_union',
                      unionErrors: errors,
                      message: 'Invalid input: Should pass single schema',
                    });
                  }
                }),
              })
            )
            .optional(),
        }),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
  ),
  summary: z.string(),
});

export const ListForms = z.object({
  query: z.union([z.string(), z.null()]).optional(),
  status: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('open'), z.literal('closed')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  access: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('view'),
          z.literal('edit'),
          z.literal('owner'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  databaseId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const ListResult = z.object({
  forms: z.array(
    z.object({
      form: z.object({
        id: z.string().uuid(),
        name: z.string(),
        description: z.string(),
        ownerId: z.string(),
        databaseId: z.string().uuid(),
        tableId: z.string().uuid(),
        submittedColumnId: z.union([z.string().uuid(), z.null()]).optional(),
        respondentColumnId: z.union([z.string().uuid(), z.null()]).optional(),
        audience: z.any().superRefine((x, ctx) => {
          const schemas = [z.literal('members'), z.literal('public')];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        tallyVisible: z.boolean(),
        responsesVisible: z.boolean(),
        anonymous: z.boolean(),
        status: z.any().superRefine((x, ctx) => {
          const schemas = [z.literal('open'), z.literal('closed')];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        closesAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        confirmationMessage: z.string(),
        branding: z.object({
          logoFileId: z.union([z.string().uuid(), z.null()]).optional(),
          coverFileId: z.union([z.string().uuid(), z.null()]).optional(),
          accentColor: z.union([z.string(), z.null()]).optional(),
        }),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        projectId: z.union([z.string(), z.null()]).optional(),
        isHiddenInDrive: z.boolean(),
      }),
      access: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('view'),
          z.literal('edit'),
          z.literal('owner'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      editorUrl: z.string(),
      respondentUrl: z.string(),
    })
  ),
  total: z.number().int().gte(0),
  truncated: z.boolean(),
});

export const ListImportEntities = z.object({
  source: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('linear'),
          z.literal('jira'),
          z.literal('notion'),
          z.literal('slack'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  status: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('staged'),
          z.literal('importing'),
          z.literal('imported'),
          z.literal('discarded'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ListImportEntitiesResponse = z.object({
  entities: z.array(
    z.object({
      id: z.string().uuid(),
      source: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('linear'),
          z.literal('jira'),
          z.literal('notion'),
          z.literal('slack'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      foreignId: z.string(),
      status: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('staged'),
          z.literal('importing'),
          z.literal('imported'),
          z.literal('discarded'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      label: z.string(),
      entityId: z.union([z.string(), z.null()]).optional(),
      entityType: z.union([z.string(), z.null()]).optional(),
      importedByTeammate: z.boolean(),
    })
  ),
});

export const ListInboxes = z.record(z.any());

export const ListInboxesResponse = z.object({
  inboxes: z.array(
    z.object({
      emailAddress: z.string(),
      isPrimary: z.boolean(),
      isDelegated: z.boolean(),
    })
  ),
  summary: z.string(),
});

export const ListInitiatives = z.object({
  query: z.union([z.string(), z.null()]).optional(),
  status: z.union([z.string().uuid(), z.null()]).optional(),
  priority: z.union([z.string().uuid(), z.null()]).optional(),
  assignee: z.union([z.string(), z.null()]).optional(),
  dueAfter: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  dueBefore: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  cursor: z.union([z.string(), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ProjectListResult = z.object({
  projects: z.array(
    z.object({
      initiativeId: z.string().uuid(),
      name: z.string(),
      access: z.string(),
      properties: z.object({
        status: z.union([z.string().uuid(), z.null()]).optional(),
        priority: z.union([z.string().uuid(), z.null()]).optional(),
        assignees: z.array(z.string()),
        dueDate: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        completed: z.boolean(),
      }),
      taskCount: z.number().int().gte(0),
      completedTaskCount: z.number().int().gte(0),
    })
  ),
  truncated: z.boolean(),
  nextCursor: z.union([z.string(), z.null()]).optional(),
});

export const ListLabels = z.object({
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
});

export const ListLabelsResponse = z.object({
  labels: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), type: z.string() })
  ),
  summary: z.string(),
});

export const ListNotifications = z.object({
  limit: z.union([z.number().int().gte(0), z.null()]).optional(),
  states: z
    .union([
      z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('unseen'),
            z.literal('seen'),
            z.literal('done'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
      z.null(),
    ])
    .optional(),
  includeTypes: z
    .union([
      z.array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('email'),
            z.literal('message'),
            z.literal('channel'),
            z.literal('document'),
            z.literal('project'),
            z.literal('call'),
            z.literal('task'),
            z.literal('github'),
            z.literal('reminder'),
            z.literal('calendar'),
            z.literal('agent'),
            z.literal('chat'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      ),
      z.null(),
    ])
    .optional(),
  entities: z
    .union([
      z.array(
        z.object({
          entityType: z.enum([
            'agent_session',
            'user',
            'channel',
            'channel_message',
            'document',
            'project',
            'email_thread',
            'calendar_event',
            'team',
            'call',
            'foreign_entity',
            'static_file',
            'crm_company',
            'crm_contact',
            'reminder',
            'skill',
            'scheduled_action',
            'chat',
          ]),
          id: z.string(),
        })
      ),
      z.null(),
    ])
    .optional(),
});

export const ListNotificationsResponse = z.object({
  notifications: z.array(
    z.object({
      id: z.string().uuid(),
      eventType: z.string(),
      entityType: z.string(),
      entityId: z.string(),
      state: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('unseen'),
          z.literal('seen'),
          z.literal('done'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      createdAt: z.string(),
      metadata: z.any(),
      senderId: z.union([z.string(), z.null()]).optional(),
    })
  ),
  hasMore: z.boolean(),
});

export const ListPipelines = z.record(z.any());

export const ListPipelinesResponse = z.object({
  pipelines: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      tracks: z.enum(['company', 'contact']),
      access: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('view'),
          z.literal('edit'),
          z.literal('owner'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      sharedWithTeam: z.boolean(),
      fields: z.array(
        z.object({
          name: z.string(),
          type: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('company'),
              z.literal('contact'),
              z.literal('text'),
              z.literal('number'),
              z.literal('checkbox'),
              z.literal('date'),
              z.literal('date_range'),
              z.literal('link'),
              z.literal('select'),
              z.literal('tags'),
              z.literal('person'),
              z.literal('item'),
              z.literal('relation'),
              z.literal('formula'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          multiple: z.boolean(),
          options: z.array(z.string()),
          required: z.boolean(),
          readOnly: z.boolean(),
        })
      ),
      version: z.number().int(),
    })
  ),
  summary: z.string(),
});

export const ListRoutines = z.object({
  query: z.union([z.string(), z.null()]).optional(),
  enabled: z.union([z.boolean(), z.null()]).optional(),
});

export const RoutineList = z.object({
  routines: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      enabled: z.boolean(),
      trigger: z.any(),
      task: z.any(),
      nextRunAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
  total: z.number().int().gte(0),
});

export const ListSkills = z.record(z.any());

export const ListSkillsResponse = z.object({
  results: z.array(
    z.object({
      documentId: z.string().uuid(),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ListTags = z.record(z.any());

export const ListTagsResponse = z.object({
  tagSets: z.array(
    z.object({
      scope: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('personal'), z.literal('team')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      propertyDefinitionId: z.string().uuid(),
      tags: z.array(
        z.object({
          id: z.string().uuid(),
          label: z.string(),
          color: z.union([z.string(), z.null()]).optional(),
        })
      ),
    })
  ),
  summary: z.string(),
});

export const ListTeamMembers = z.record(z.any());

export const ListTeamMembersResponse = z.object({
  members: z.array(z.object({ userId: z.string(), role: z.string() })),
  invited: z.array(z.object({ email: z.string(), role: z.string() })),
});

export const LoadTools = z.object({ names: z.array(z.string()) });

export const LoadToolsResponse = z.object({
  loaded: z.array(z.object({ name: z.string(), description: z.string() })),
  not_found: z.array(z.string()),
});

export const ManageAgentSession = z
  .object({
    sessionId: z.string().uuid(),
    action: z.any().superRefine((x, ctx) => {
      const schemas = [
        z.object({ name: z.string(), type: z.literal('rename') }).strict(),
        z
          .object({ archived: z.boolean(), type: z.literal('archive') })
          .strict(),
        z.object({ type: z.literal('delete') }).strict(),
        z.object({ prompt: z.string(), type: z.literal('prompt') }).strict(),
        z.object({ type: z.literal('stop') }).strict(),
      ];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
  })
  .strict();

export const SessionActionResult = z.object({
  sessionId: z.string().uuid(),
  message: z.string(),
  actionId: z.union([z.string().uuid(), z.null()]).optional(),
  status: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('sent'), z.literal('queued')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ManageBotChannelAccess = z.object({
  botId: z.string().uuid(),
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('grant'), z.literal('revoke')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const ManageBotChannelAccessResponse = z.object({
  botId: z.string().uuid(),
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('grant'), z.literal('revoke')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const ManageChannelParticipants = z.object({
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('add'), z.literal('remove')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()),
});

export const ManageChannelParticipantsResponse = z.object({
  channelId: z.string().uuid(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('add'), z.literal('remove')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  participants: z.array(z.string()),
  summary: z.string(),
});

export const MarkNotificationsDone = z.object({
  notificationIds: z.array(z.string().uuid()),
  done: z.boolean(),
});

export const MarkNotificationsResponse = z.object({
  success: z.boolean(),
  count: z.number().int().gte(0),
});

export const MarkNotificationsSeen = z.object({
  notificationIds: z.array(z.string().uuid()),
});

export const MoveToProject = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('email'),
      z.literal('project'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const MoveToProjectResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const MoveToTrash = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('project'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
});

export const MoveToTrashResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('project'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  message: z.string(),
});

export const MuteNotifications = z.object({
  entityType: z.enum([
    'agent_session',
    'user',
    'channel',
    'channel_message',
    'document',
    'project',
    'email_thread',
    'calendar_event',
    'team',
    'call',
    'foreign_entity',
    'static_file',
    'crm_company',
    'crm_contact',
    'reminder',
    'skill',
    'scheduled_action',
    'chat',
  ]),
  entityId: z.string(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('mute'),
      z.literal('snooze'),
      z.literal('unmute'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  snoozeUntil: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const MuteNotificationsResponse = z.object({
  entityId: z.string(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('mute'),
      z.literal('snooze'),
      z.literal('unmute'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  snoozeUntil: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const NameSearch = z.object({
  name: z.string(),
  matchType: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('partial'), z.literal('exact')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('partial')
    )
    .optional(),
  entityTypes: z
    .array(
      z.enum([
        'agent_sessions',
        'documents',
        'emails',
        'channels',
        'projects',
        'call_records',
        'calendar_events',
        'chats',
      ])
    )
    .optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  tags: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          scope: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [z.literal('personal'), z.literal('team')];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
        })
      ),
      z.null(),
    ])
    .optional(),
  tagsMatch: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('any'), z.literal('all')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const OpenDirectMessage = z.object({
  participants: z.array(z.string()),
});

export const OpenDirectMessageResponse = z.object({
  channelId: z.string().uuid(),
  created: z.boolean(),
  participants: z.array(z.string()),
  summary: z.string(),
});

export const QueryDatabase = z.object({
  sql: z.string(),
  databaseId: z.union([z.string().uuid(), z.null()]).optional(),
  baseVersions: z
    .union([
      z.array(
        z.object({ tableId: z.string().uuid(), version: z.number().int() })
      ),
      z.null(),
    ])
    .optional(),
  display: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('table'),
          z.literal('scalar'),
          z.literal('bar'),
          z.literal('line'),
          z.literal('area'),
          z.literal('scatter'),
          z.literal('pie'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const QueryDatabaseResponse = z.object({
  results: z.array(
    z.object({
      sourceTable: z.union([z.string().uuid(), z.null()]).optional(),
      columns: z.array(
        z.object({
          name: z.string(),
          kind: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('text'),
              z.literal('number'),
              z.literal('boolean'),
              z.literal('date'),
              z.literal('dateRange'),
              z.literal('select'),
              z.literal('entity'),
              z.literal('row'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          options: z
            .array(z.object({ id: z.string().uuid(), label: z.string() }))
            .optional(),
          target: z
            .union([
              z.any().superRefine((x, ctx) => {
                const schemas = [
                  z.literal('USER'),
                  z.literal('DOCUMENT'),
                  z.literal('TASK'),
                  z.literal('COMPANY'),
                  z.literal('CONTACT'),
                  z.literal('CALL_RECORD'),
                  z.literal('CHANNEL'),
                  z.literal('CHAT'),
                  z.literal('PROJECT'),
                  z.literal('THREAD'),
                  z.literal('CALENDAR_EVENT'),
                  z.literal('INITIATIVE'),
                  z.literal('DATABASE_ROW'),
                ];
                const errors = schemas.reduce<z.ZodError[]>(
                  (errors, schema) =>
                    ((result) =>
                      result.error ? [...errors, result.error] : errors)(
                      schema.safeParse(x)
                    ),
                  []
                );
                if (schemas.length - errors.length !== 1) {
                  ctx.addIssue({
                    path: ctx.path,
                    code: 'invalid_union',
                    unionErrors: errors,
                    message: 'Invalid input: Should pass single schema',
                  });
                }
              }),
              z.null(),
            ])
            .optional(),
          relatedTable: z.union([z.string().uuid(), z.null()]).optional(),
        })
      ),
      rows: z.array(
        z.array(
          z.union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({ type: z.literal('text'), value: z.string() }),
                z.object({ type: z.literal('number'), value: z.number() }),
                z.object({ type: z.literal('bool'), value: z.boolean() }),
                z.object({
                  type: z.literal('date'),
                  value: z.string().datetime({ offset: true }),
                }),
                z.object({
                  type: z.literal('dateRange'),
                  value: z.object({
                    start: z.string().datetime({ offset: true }),
                    end: z.string().datetime({ offset: true }),
                  }),
                }),
                z.object({
                  type: z.literal('options'),
                  value: z.array(z.string().uuid()),
                }),
                z.object({
                  type: z.literal('entities'),
                  value: z.array(z.string()),
                }),
                z.object({ type: z.literal('row'), value: z.string().uuid() }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
        )
      ),
      rowIds: z.array(z.string().uuid()),
    })
  ),
  changesApplied: z.number().int().gte(0),
  insertedRowIds: z.array(z.string().uuid()).optional(),
  newVersions: z.record(z.number().int()).optional(),
  readVersions: z.array(
    z.object({ tableId: z.string().uuid(), version: z.number().int() })
  ),
  truncatedTables: z.array(z.string()).optional(),
  statement: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        databaseId: z.string().uuid(),
        summary: z.string(),
        kind: z.literal('schema'),
      }),
      z.object({ kind: z.literal('select') }),
      z.object({
        tableId: z.string().uuid(),
        tableName: z.string(),
        kind: z.literal('insert'),
      }),
      z.object({
        tableId: z.string().uuid(),
        tableName: z.string(),
        kind: z.literal('update'),
      }),
      z.object({
        tableId: z.string().uuid(),
        tableName: z.string(),
        kind: z.literal('delete'),
      }),
      z.object({
        tableId: z.string().uuid(),
        tableName: z.string(),
        columnId: z.string().uuid(),
        columnName: z.string(),
        to: z.string(),
        kind: z.literal('alterColumnType'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const ReactToChannelMessage = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  emoji: z.string(),
  action: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('add'), z.literal('remove')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('add')
    )
    .optional(),
});

export const ReactToChannelMessageResponse = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  emoji: z.string(),
  action: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('add'), z.literal('remove')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  count: z.number().int().gte(0),
});

export const ReadActivity = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }),
});

export const ReadActivityResponse = z.object({
  activities: z.array(
    z.object({
      actorId: z.string(),
      entityType: z.string(),
      entityId: z.string(),
      action: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({ type: z.literal('created') }),
          z.object({ type: z.literal('edited') }),
          z.object({ type: z.literal('opened') }),
          z.object({ type: z.literal('deleted') }),
          z.object({ type: z.literal('messaged') }),
          z.object({ type: z.literal('sent') }),
          z.object({ type: z.literal('responded') }),
          z.object({
            property: z.string(),
            propertyName: z.union([z.string(), z.null()]).optional(),
            propertyType: z.union([z.string(), z.null()]).optional(),
            from: z.any().optional(),
            fromLabels: z.union([z.array(z.string()), z.null()]).optional(),
            to: z.any().optional(),
            toLabels: z.union([z.array(z.string()), z.null()]).optional(),
            type: z.literal('propertyChanged'),
          }),
          z.object({
            participant: z.string(),
            type: z.literal('participantAdded'),
          }),
          z.object({
            participant: z.string(),
            type: z.literal('participantRemoved'),
          }),
          z.object({ callId: z.string(), type: z.literal('callStarted') }),
          z.object({ type: z.literal('taskAdded') }),
          z.object({ type: z.literal('taskRemoved') }),
          z.object({
            tag: z.string(),
            payload: z.any().optional(),
            type: z.literal('unknown'),
          }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      occurredAt: z.string().datetime({ offset: true }),
    })
  ),
  truncated: z.boolean(),
});

export const ReadAgentSession = z
  .object({
    sessionId: z.string().uuid(),
    turn: z.union([z.number().int().gte(0), z.null()]).optional(),
  })
  .strict();

export const SessionConversation = z.object({
  sessionId: z.string().uuid(),
  name: z.string(),
  ownerId: z.string(),
  botId: z.string().uuid(),
  archivedBy: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('user'),
          z.literal('pull_request_merged'),
          z.literal('runtime_closed'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  archivedAt: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  harness: z.string(),
  model: z.string(),
  status: z.string(),
  messages: z.array(
    z.object({
      turn: z.number().int().gte(0),
      role: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('user'), z.literal('agent')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      content: z.string(),
      failure: z.union([z.string(), z.null()]).optional(),
      truncated: z.boolean(),
    })
  ),
  totalMessages: z.number().int().gte(0),
  truncated: z.boolean(),
});

export const ReadAgentSessionSharing = z
  .object({ sessionId: z.string().uuid() })
  .strict();

export const SessionSharingResponse = z.object({
  owner: z.string(),
  linkScope: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('off'), z.literal('public'), z.literal('team')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  linkAccess: z
    .union([z.enum(['view', 'comment', 'edit', 'owner']), z.null()])
    .optional(),
  teamAccess: z
    .union([z.enum(['view', 'comment', 'edit', 'owner']), z.null()])
    .optional(),
  channels: z.array(
    z.object({
      channelId: z.string(),
      access: z.enum(['view', 'comment', 'edit', 'owner']),
    })
  ),
});

export const ReadBookingSettings = z
  .object({ teamId: z.union([z.string().uuid(), z.null()]).optional() })
  .strict();

export const BookingSettingsResult = z.object({
  profile: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    schedules: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        timeZone: z.string(),
        weekly: z.array(
          z.object({
            day: z.number().int().gte(0).lte(255),
            windows: z.array(z.object({ start: z.string(), end: z.string() })),
          })
        ),
        overrides: z.array(
          z.object({
            date: z.string().date(),
            windows: z.array(z.object({ start: z.string(), end: z.string() })),
          })
        ),
      })
    ),
    defaultScheduleId: z.union([z.string().uuid(), z.null()]).optional(),
    eventTypes: z.array(
      z.object({
        id: z.string().uuid(),
        title: z.string(),
        slug: z.string(),
        description: z.string(),
        durationMinutes: z.number().int().gte(0).lte(65535),
        location: z.string(),
        macroCall: z.boolean().optional(),
        googleMeet: z.boolean(),
        enabled: z.boolean(),
        scheduleId: z.string().uuid(),
        mode: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('individual'),
            z.literal('collective'),
            z.literal('roundRobin'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        hosts: z.array(z.string()),
        beforeMinutes: z.number().int().gte(0).lte(65535),
        afterMinutes: z.number().int().gte(0).lte(65535),
        noticeMinutes: z.number().int().gte(0).lte(65535),
        horizonDays: z.number().int().gte(0).lte(65535),
        intervalMinutes: z.number().int().gte(0).lte(65535),
        dailyLimit: z
          .union([z.number().int().gte(0).lte(65535), z.null()])
          .optional(),
        requiresConfirmation: z.boolean(),
        questions: z.array(
          z.object({
            id: z.string().uuid(),
            label: z.string(),
            required: z.boolean(),
          })
        ),
      })
    ),
    revision: z.number().int(),
  }),
  pageUrl: z.string(),
});

export const ReadCalendarEvent = z.object({
  eventId: z.string().uuid(),
  occurrenceKey: z.union([z.string(), z.null()]).optional(),
});

export const ReadCalendarEventResponse = z.object({
  access: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('on_your_calendar'),
      z.literal('on_visible_calendar'),
      z.literal('invited'),
      z.literal('snapshot'),
      z.literal('shared_with_you'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  calendarName: z.union([z.string(), z.null()]).optional(),
  eventId: z.union([z.string().uuid(), z.null()]).optional(),
  title: z.string(),
  start: z.string(),
  end: z.string(),
  isAllDay: z.boolean(),
  timeZone: z.union([z.string(), z.null()]).optional(),
  isRecurring: z.boolean(),
  recurrenceId: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  organizerEmail: z.union([z.string(), z.null()]).optional(),
  organizerName: z.union([z.string(), z.null()]).optional(),
  attendeeCount: z.number().int().gte(0),
  canRequestToJoin: z.boolean(),
  joinRequestStatus: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('pending'),
          z.literal('accepted'),
          z.literal('declined'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  summary: z.string(),
});

export const ReadCallRecord = z.object({ callId: z.string().uuid() });

export const ReadCallRecordResponse = z.object({
  callId: z.string().uuid(),
  summary: z.union([z.string(), z.null()]).optional(),
  transcript: z.array(
    z.object({
      speakerId: z.string(),
      diarizedSpeakerId: z.union([z.string(), z.null()]).optional(),
      content: z.string(),
      startedAt: z.string().datetime({ offset: true }),
      endedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ReadCanvas = z.object({
  document_id: z.string(),
  item_ids: z.union([z.array(z.string()), z.null()]).optional(),
  format: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('scene'), z.literal('overview')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
});

export const ReadChannelMessageContext = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  channelBefore: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  channelAfter: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  threadBefore: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  threadAfter: z
    .union([z.number().int().gte(0).lte(65535), z.null()])
    .optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ReadChannelMessageContextResponse = z.object({
  anchor: z.object({
    messageId: z.string().uuid(),
    channelId: z.string().uuid(),
    kind: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('topLevelMessage'), z.literal('threadReply')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    threadId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
  }),
  channelContext: z.object({
    before: z.array(
      z.object({
        id: z.string().uuid(),
        channelId: z.string().uuid(),
        senderId: z.string(),
        senderName: z.union([z.string(), z.null()]).optional(),
        content: z.string(),
        contentTruncated: z.boolean(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        editedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        thread: z.object({
          threadId: z.string().uuid(),
          replyCount: z.number().int(),
          latestReplyAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          preview: z
            .union([
              z.array(
                z.object({
                  id: z.string().uuid(),
                  threadId: z.string().uuid(),
                  senderId: z.string(),
                  senderName: z.union([z.string(), z.null()]).optional(),
                  content: z.string(),
                  contentTruncated: z.boolean(),
                  createdAt: z.string().datetime({ offset: true }),
                  updatedAt: z.string().datetime({ offset: true }),
                  editedAt: z
                    .union([z.string().datetime({ offset: true }), z.null()])
                    .optional(),
                  reactions: z.array(
                    z.object({ emoji: z.string(), users: z.array(z.string()) })
                  ),
                  attachments: z.array(
                    z.object({
                      id: z.string().uuid(),
                      entityType: z.string(),
                      entityId: z.string(),
                      createdAt: z.string().datetime({ offset: true }),
                    })
                  ),
                })
              ),
              z.null(),
            ])
            .optional(),
          omittedReplyCount: z.number().int(),
        }),
        reactions: z.array(
          z.object({ emoji: z.string(), users: z.array(z.string()) })
        ),
        attachments: z.array(
          z.object({
            id: z.string().uuid(),
            entityType: z.string(),
            entityId: z.string(),
            createdAt: z.string().datetime({ offset: true }),
          })
        ),
      })
    ),
    anchorOrParent: z.object({
      id: z.string().uuid(),
      channelId: z.string().uuid(),
      senderId: z.string(),
      senderName: z.union([z.string(), z.null()]).optional(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      thread: z.object({
        threadId: z.string().uuid(),
        replyCount: z.number().int(),
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        preview: z
          .union([
            z.array(
              z.object({
                id: z.string().uuid(),
                threadId: z.string().uuid(),
                senderId: z.string(),
                senderName: z.union([z.string(), z.null()]).optional(),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                attachments: z.array(
                  z.object({
                    id: z.string().uuid(),
                    entityType: z.string(),
                    entityId: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              })
            ),
            z.null(),
          ])
          .optional(),
        omittedReplyCount: z.number().int(),
      }),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    }),
    after: z.array(
      z.object({
        id: z.string().uuid(),
        channelId: z.string().uuid(),
        senderId: z.string(),
        senderName: z.union([z.string(), z.null()]).optional(),
        content: z.string(),
        contentTruncated: z.boolean(),
        createdAt: z.string().datetime({ offset: true }),
        updatedAt: z.string().datetime({ offset: true }),
        editedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        deletedAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        thread: z.object({
          threadId: z.string().uuid(),
          replyCount: z.number().int(),
          latestReplyAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          preview: z
            .union([
              z.array(
                z.object({
                  id: z.string().uuid(),
                  threadId: z.string().uuid(),
                  senderId: z.string(),
                  senderName: z.union([z.string(), z.null()]).optional(),
                  content: z.string(),
                  contentTruncated: z.boolean(),
                  createdAt: z.string().datetime({ offset: true }),
                  updatedAt: z.string().datetime({ offset: true }),
                  editedAt: z
                    .union([z.string().datetime({ offset: true }), z.null()])
                    .optional(),
                  reactions: z.array(
                    z.object({ emoji: z.string(), users: z.array(z.string()) })
                  ),
                  attachments: z.array(
                    z.object({
                      id: z.string().uuid(),
                      entityType: z.string(),
                      entityId: z.string(),
                      createdAt: z.string().datetime({ offset: true }),
                    })
                  ),
                })
              ),
              z.null(),
            ])
            .optional(),
          omittedReplyCount: z.number().int(),
        }),
        reactions: z.array(
          z.object({ emoji: z.string(), users: z.array(z.string()) })
        ),
        attachments: z.array(
          z.object({
            id: z.string().uuid(),
            entityType: z.string(),
            entityId: z.string(),
            createdAt: z.string().datetime({ offset: true }),
          })
        ),
      })
    ),
  }),
  threadContext: z
    .union([
      z.object({
        threadId: z.string().uuid(),
        parent: z.object({
          id: z.string().uuid(),
          channelId: z.string().uuid(),
          senderId: z.string(),
          senderName: z.union([z.string(), z.null()]).optional(),
          content: z.string(),
          contentTruncated: z.boolean(),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          thread: z.object({
            threadId: z.string().uuid(),
            replyCount: z.number().int(),
            latestReplyAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            preview: z
              .union([
                z.array(
                  z.object({
                    id: z.string().uuid(),
                    threadId: z.string().uuid(),
                    senderId: z.string(),
                    senderName: z.union([z.string(), z.null()]).optional(),
                    content: z.string(),
                    contentTruncated: z.boolean(),
                    createdAt: z.string().datetime({ offset: true }),
                    updatedAt: z.string().datetime({ offset: true }),
                    editedAt: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    reactions: z.array(
                      z.object({
                        emoji: z.string(),
                        users: z.array(z.string()),
                      })
                    ),
                    attachments: z.array(
                      z.object({
                        id: z.string().uuid(),
                        entityType: z.string(),
                        entityId: z.string(),
                        createdAt: z.string().datetime({ offset: true }),
                      })
                    ),
                  })
                ),
                z.null(),
              ])
              .optional(),
            omittedReplyCount: z.number().int(),
          }),
          reactions: z.array(
            z.object({ emoji: z.string(), users: z.array(z.string()) })
          ),
          attachments: z.array(
            z.object({
              id: z.string().uuid(),
              entityType: z.string(),
              entityId: z.string(),
              createdAt: z.string().datetime({ offset: true }),
            })
          ),
        }),
        repliesBefore: z.array(
          z.object({
            id: z.string().uuid(),
            threadId: z.string().uuid(),
            senderId: z.string(),
            senderName: z.union([z.string(), z.null()]).optional(),
            content: z.string(),
            contentTruncated: z.boolean(),
            createdAt: z.string().datetime({ offset: true }),
            updatedAt: z.string().datetime({ offset: true }),
            editedAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            reactions: z.array(
              z.object({ emoji: z.string(), users: z.array(z.string()) })
            ),
            attachments: z.array(
              z.object({
                id: z.string().uuid(),
                entityType: z.string(),
                entityId: z.string(),
                createdAt: z.string().datetime({ offset: true }),
              })
            ),
          })
        ),
        anchorReply: z
          .union([
            z.object({
              id: z.string().uuid(),
              threadId: z.string().uuid(),
              senderId: z.string(),
              senderName: z.union([z.string(), z.null()]).optional(),
              content: z.string(),
              contentTruncated: z.boolean(),
              createdAt: z.string().datetime({ offset: true }),
              updatedAt: z.string().datetime({ offset: true }),
              editedAt: z
                .union([z.string().datetime({ offset: true }), z.null()])
                .optional(),
              reactions: z.array(
                z.object({ emoji: z.string(), users: z.array(z.string()) })
              ),
              attachments: z.array(
                z.object({
                  id: z.string().uuid(),
                  entityType: z.string(),
                  entityId: z.string(),
                  createdAt: z.string().datetime({ offset: true }),
                })
              ),
            }),
            z.null(),
          ])
          .optional(),
        repliesAfter: z.array(
          z.object({
            id: z.string().uuid(),
            threadId: z.string().uuid(),
            senderId: z.string(),
            senderName: z.union([z.string(), z.null()]).optional(),
            content: z.string(),
            contentTruncated: z.boolean(),
            createdAt: z.string().datetime({ offset: true }),
            updatedAt: z.string().datetime({ offset: true }),
            editedAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            reactions: z.array(
              z.object({ emoji: z.string(), users: z.array(z.string()) })
            ),
            attachments: z.array(
              z.object({
                id: z.string().uuid(),
                entityType: z.string(),
                entityId: z.string(),
                createdAt: z.string().datetime({ offset: true }),
              })
            ),
          })
        ),
        omittedBefore: z.number().int().gte(0),
        omittedAfter: z.number().int().gte(0),
      }),
      z.null(),
    ])
    .optional(),
  omissions: z.array(
    z.object({
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ReadChannelMessages = z.object({
  channelId: z.string().uuid(),
  windowType: z.enum([
    'latest',
    'timeRange',
    'aroundMessage',
    'page',
    'messages',
  ]),
  from: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  to: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  messageId: z.union([z.string().uuid(), z.null()]).optional(),
  cursor: z.union([z.string(), z.null()]).optional(),
  direction: z.union([z.enum(['older', 'newer']), z.null()]).optional(),
  messageIds: z.array(z.string().uuid()).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  includeThreadPreviews: z.union([z.boolean(), z.null()]).optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ReadChannelMessagesResponse = z.object({
  channelId: z.string().uuid(),
  window: z.object({
    windowType: z.enum([
      'latest',
      'timeRange',
      'aroundMessage',
      'page',
      'messages',
    ]),
    from: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    to: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
    messageId: z.union([z.string().uuid(), z.null()]).optional(),
    direction: z.union([z.enum(['older', 'newer']), z.null()]).optional(),
    messageIds: z.array(z.string().uuid()),
  }),
  messages: z.array(
    z.object({
      id: z.string().uuid(),
      channelId: z.string().uuid(),
      senderId: z.string(),
      senderName: z.union([z.string(), z.null()]).optional(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      thread: z.object({
        threadId: z.string().uuid(),
        replyCount: z.number().int(),
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        preview: z
          .union([
            z.array(
              z.object({
                id: z.string().uuid(),
                threadId: z.string().uuid(),
                senderId: z.string(),
                senderName: z.union([z.string(), z.null()]).optional(),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                attachments: z.array(
                  z.object({
                    id: z.string().uuid(),
                    entityType: z.string(),
                    entityId: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              })
            ),
            z.null(),
          ])
          .optional(),
        omittedReplyCount: z.number().int(),
      }),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    })
  ),
  navigation: z.object({
    olderCursor: z.union([z.string(), z.null()]).optional(),
    newerCursor: z.union([z.string(), z.null()]).optional(),
    hasMoreOlder: z.boolean(),
    hasMoreNewer: z.boolean(),
  }),
  omissions: z.array(
    z.object({
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ReadChannelThread = z.object({
  channelId: z.string().uuid(),
  messageId: z.string().uuid(),
  windowType: z
    .union([z.enum(['allIfSmall', 'latest', 'aroundReply']), z.null()])
    .optional(),
  replyId: z.union([z.string().uuid(), z.null()]).optional(),
  before: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  after: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
  includeChannelContext: z.union([z.boolean(), z.null()]).optional(),
  maxCharsPerMessage: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const ReadChannelThreadResponse = z.object({
  anchor: z.object({
    messageId: z.string().uuid(),
    channelId: z.string().uuid(),
    kind: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('topLevelMessage'), z.literal('threadReply')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    threadId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
  }),
  thread: z.object({
    channelId: z.string().uuid(),
    threadId: z.string().uuid(),
    parent: z.object({
      id: z.string().uuid(),
      channelId: z.string().uuid(),
      senderId: z.string(),
      senderName: z.union([z.string(), z.null()]).optional(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      deletedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      thread: z.object({
        threadId: z.string().uuid(),
        replyCount: z.number().int(),
        latestReplyAt: z
          .union([z.string().datetime({ offset: true }), z.null()])
          .optional(),
        preview: z
          .union([
            z.array(
              z.object({
                id: z.string().uuid(),
                threadId: z.string().uuid(),
                senderId: z.string(),
                senderName: z.union([z.string(), z.null()]).optional(),
                content: z.string(),
                contentTruncated: z.boolean(),
                createdAt: z.string().datetime({ offset: true }),
                updatedAt: z.string().datetime({ offset: true }),
                editedAt: z
                  .union([z.string().datetime({ offset: true }), z.null()])
                  .optional(),
                reactions: z.array(
                  z.object({ emoji: z.string(), users: z.array(z.string()) })
                ),
                attachments: z.array(
                  z.object({
                    id: z.string().uuid(),
                    entityType: z.string(),
                    entityId: z.string(),
                    createdAt: z.string().datetime({ offset: true }),
                  })
                ),
              })
            ),
            z.null(),
          ])
          .optional(),
        omittedReplyCount: z.number().int(),
      }),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    }),
    replyCount: z.number().int().gte(0),
    latestReplyAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
  }),
  replies: z.array(
    z.object({
      id: z.string().uuid(),
      threadId: z.string().uuid(),
      senderId: z.string(),
      senderName: z.union([z.string(), z.null()]).optional(),
      content: z.string(),
      contentTruncated: z.boolean(),
      createdAt: z.string().datetime({ offset: true }),
      updatedAt: z.string().datetime({ offset: true }),
      editedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      reactions: z.array(
        z.object({ emoji: z.string(), users: z.array(z.string()) })
      ),
      attachments: z.array(
        z.object({
          id: z.string().uuid(),
          entityType: z.string(),
          entityId: z.string(),
          createdAt: z.string().datetime({ offset: true }),
        })
      ),
    })
  ),
  channelContext: z
    .union([
      z.array(
        z.object({
          id: z.string().uuid(),
          channelId: z.string().uuid(),
          senderId: z.string(),
          senderName: z.union([z.string(), z.null()]).optional(),
          content: z.string(),
          contentTruncated: z.boolean(),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          deletedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          thread: z.object({
            threadId: z.string().uuid(),
            replyCount: z.number().int(),
            latestReplyAt: z
              .union([z.string().datetime({ offset: true }), z.null()])
              .optional(),
            preview: z
              .union([
                z.array(
                  z.object({
                    id: z.string().uuid(),
                    threadId: z.string().uuid(),
                    senderId: z.string(),
                    senderName: z.union([z.string(), z.null()]).optional(),
                    content: z.string(),
                    contentTruncated: z.boolean(),
                    createdAt: z.string().datetime({ offset: true }),
                    updatedAt: z.string().datetime({ offset: true }),
                    editedAt: z
                      .union([z.string().datetime({ offset: true }), z.null()])
                      .optional(),
                    reactions: z.array(
                      z.object({
                        emoji: z.string(),
                        users: z.array(z.string()),
                      })
                    ),
                    attachments: z.array(
                      z.object({
                        id: z.string().uuid(),
                        entityType: z.string(),
                        entityId: z.string(),
                        createdAt: z.string().datetime({ offset: true }),
                      })
                    ),
                  })
                ),
                z.null(),
              ])
              .optional(),
            omittedReplyCount: z.number().int(),
          }),
          reactions: z.array(
            z.object({ emoji: z.string(), users: z.array(z.string()) })
          ),
          attachments: z.array(
            z.object({
              id: z.string().uuid(),
              entityType: z.string(),
              entityId: z.string(),
              createdAt: z.string().datetime({ offset: true }),
            })
          ),
        })
      ),
      z.null(),
    ])
    .optional(),
  omissions: z.array(
    z.object({
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('olderMessages'),
          z.literal('newerMessages'),
          z.literal('threadReplies'),
          z.literal('truncatedContent'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      messageId: z.union([z.string().uuid(), z.null()]).optional(),
      threadId: z.union([z.string().uuid(), z.null()]).optional(),
      count: z.union([z.number().int(), z.null()]).optional(),
      cursor: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ReadContent = z.object({ documentId: z.string().uuid() });

export const ReadContentResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({ text: z.string() }).strict(),
      z
        .object({
          markdown: z.array(
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  nodeId: z.string(),
                  content: z.string(),
                  tag: z.string(),
                  type: z.literal('generic'),
                }),
                z.object({ url: z.string(), type: z.literal('staticImage') }),
                z.object({ id: z.string(), type: z.literal('dssImage') }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            })
          ),
        })
        .strict(),
      z.object({ download: z.object({ url: z.string() }) }).strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  comments: z.array(
    z.object({
      id: z.string().uuid(),
      kind: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('inline'), z.literal('discussion')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      resolved: z.boolean(),
      anchor: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({
            sheetId: z.string(),
            sheetName: z.string(),
            range: z.string(),
            type: z.literal('spreadsheet'),
          }),
          z.object({ type: z.literal('document') }),
          z.object({
            markId: z.string().uuid(),
            markedText: z.union([z.string(), z.null()]).optional(),
            originalMarkedText: z.union([z.string(), z.null()]).optional(),
            removed: z.boolean().optional(),
            type: z.literal('text'),
          }),
          z.object({
            anchorId: z.string().uuid(),
            markedText: z.union([z.string(), z.null()]).optional(),
            type: z.literal('pdfHighlight'),
          }),
          z.object({ anchorId: z.string().uuid(), type: z.literal('pdfPin') }),
          z.object({
            pageId: z.string(),
            nodeId: z.union([z.string(), z.null()]).optional(),
            x: z.number(),
            y: z.number(),
            type: z.literal('fig'),
          }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      comments: z.array(
        z.object({
          id: z.string().uuid(),
          author: z.string(),
          authorName: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          createdAt: z.string().datetime({ offset: true }),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
        })
      ),
    })
  ),
});

export const ReadDesign = z.object({
  documentId: z.string(),
  pages: z.union([z.array(z.number().int().gte(0)), z.null()]).optional(),
});

export const ReadDesignResponse = z.object({ content: z.string() });

export const ReadDiscussion = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('initiative'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string(),
});

export const ReadDiscussionResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('initiative'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string(),
  threads: z.array(
    z.object({
      id: z.string().uuid(),
      resolved: z.boolean(),
      markedText: z.union([z.string(), z.null()]).optional(),
      comments: z.array(
        z.object({
          id: z.string().uuid(),
          author: z.string(),
          authorName: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          createdAt: z.string().datetime({ offset: true }),
          editedAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
        })
      ),
    })
  ),
});

export const ReadForm = z.object({
  formId: z.string().uuid(),
  view: z
    .intersection(
      z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('authoring'), z.literal('respondent')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.any().default('authoring')
    )
    .optional(),
  includeSummary: z.boolean().optional(),
});

export const ReadResult = z.any().superRefine((x, ctx) => {
  const schemas = [
    z.object({
      saved: z.object({
        form: z.object({
          id: z.string().uuid(),
          name: z.string(),
          description: z.string(),
          ownerId: z.string(),
          databaseId: z.string().uuid(),
          tableId: z.string().uuid(),
          submittedColumnId: z.union([z.string().uuid(), z.null()]).optional(),
          respondentColumnId: z.union([z.string().uuid(), z.null()]).optional(),
          audience: z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('members'), z.literal('public')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          tallyVisible: z.boolean(),
          responsesVisible: z.boolean(),
          anonymous: z.boolean(),
          status: z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('open'), z.literal('closed')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          closesAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          confirmationMessage: z.string(),
          branding: z.object({
            logoFileId: z.union([z.string().uuid(), z.null()]).optional(),
            coverFileId: z.union([z.string().uuid(), z.null()]).optional(),
            accentColor: z.union([z.string(), z.null()]).optional(),
          }),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          projectId: z.union([z.string(), z.null()]).optional(),
          isHiddenInDrive: z.boolean(),
        }),
        layout: z.object({
          sections: z.array(
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  questions: z.array(
                    z.object({
                      id: z.string().uuid(),
                      column: z.string().uuid(),
                      helpText: z.string(),
                      required: z.boolean(),
                      widget: z
                        .union([
                          z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('short'),
                              z.literal('paragraph'),
                              z.literal('datetime'),
                              z.literal('date'),
                              z.literal('url'),
                              z.literal('file'),
                              z.literal('choice'),
                              z.literal('dropdown'),
                              z.literal('checkboxes'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          z.null(),
                        ])
                        .optional(),
                    })
                  ),
                  kind: z.literal('questions'),
                }),
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  rules: z.object({
                    conjunction: z.any().superRefine((x, ctx) => {
                      const schemas = [z.literal('and'), z.literal('or')];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    conditions: z.array(
                      z.any().superRefine((x, ctx) => {
                        const schemas = [
                          z.intersection(
                            z.object({
                              column: z.string().uuid(),
                              test: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isEmpty'),
                                        z.literal('isNotEmpty'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    kind: z.literal('presence'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('is'),
                                        z.literal('isNot'),
                                        z.literal('contains'),
                                        z.literal('doesNotContain'),
                                        z.literal('startsWith'),
                                        z.literal('endsWith'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z.string(),
                                    kind: z.literal('text'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('is'),
                                        z.literal('isNot'),
                                        z.literal('greaterThan'),
                                        z.literal('greaterThanOrEqual'),
                                        z.literal('lessThan'),
                                        z.literal('lessThanOrEqual'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z.number(),
                                    kind: z.literal('number'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('before'),
                                        z.literal('after'),
                                        z.literal('onOrBefore'),
                                        z.literal('onOrAfter'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z
                                      .string()
                                      .datetime({ offset: true }),
                                    kind: z.literal('date'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('contains'),
                                        z.literal('doesNotContain'),
                                        z.literal('endsBefore'),
                                        z.literal('endsOnOrAfter'),
                                        z.literal('startsAfter'),
                                        z.literal('startsOnOrBefore'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    value: z
                                      .string()
                                      .datetime({ offset: true }),
                                    kind: z.literal('dateRange'),
                                  }),
                                  z.object({
                                    checked: z.boolean(),
                                    kind: z.literal('checkbox'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isAnyOf'),
                                        z.literal('isNoneOf'),
                                        z.literal('hasAny'),
                                        z.literal('hasAll'),
                                        z.literal('hasNone'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    options: z.array(z.string().uuid()),
                                    kind: z.literal('options'),
                                  }),
                                  z.object({
                                    operator: z.any().superRefine((x, ctx) => {
                                      const schemas = [
                                        z.literal('isAnyOf'),
                                        z.literal('isNoneOf'),
                                        z.literal('hasAny'),
                                        z.literal('hasAll'),
                                        z.literal('hasNone'),
                                      ];
                                      const errors = schemas.reduce<
                                        z.ZodError[]
                                      >(
                                        (errors, schema) =>
                                          ((result) =>
                                            result.error
                                              ? [...errors, result.error]
                                              : errors)(schema.safeParse(x)),
                                        []
                                      );
                                      if (
                                        schemas.length - errors.length !==
                                        1
                                      ) {
                                        ctx.addIssue({
                                          path: ctx.path,
                                          code: 'invalid_union',
                                          unionErrors: errors,
                                          message:
                                            'Invalid input: Should pass single schema',
                                        });
                                      }
                                    }),
                                    entities: z.array(z.string()),
                                    kind: z.literal('entities'),
                                  }),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                            }),
                            z.object({ kind: z.literal('condition') })
                          ),
                          z.intersection(
                            z.object({
                              conjunction: z.any().superRefine((x, ctx) => {
                                const schemas = [
                                  z.literal('and'),
                                  z.literal('or'),
                                ];
                                const errors = schemas.reduce<z.ZodError[]>(
                                  (errors, schema) =>
                                    ((result) =>
                                      result.error
                                        ? [...errors, result.error]
                                        : errors)(schema.safeParse(x)),
                                  []
                                );
                                if (schemas.length - errors.length !== 1) {
                                  ctx.addIssue({
                                    path: ctx.path,
                                    code: 'invalid_union',
                                    unionErrors: errors,
                                    message:
                                      'Invalid input: Should pass single schema',
                                  });
                                }
                              }),
                              conditions: z.any(),
                            }),
                            z.object({ kind: z.literal('group') })
                          ),
                        ];
                        const errors = schemas.reduce<z.ZodError[]>(
                          (errors, schema) =>
                            ((result) =>
                              result.error
                                ? [...errors, result.error]
                                : errors)(schema.safeParse(x)),
                          []
                        );
                        if (schemas.length - errors.length !== 1) {
                          ctx.addIssue({
                            path: ctx.path,
                            code: 'invalid_union',
                            unionErrors: errors,
                            message: 'Invalid input: Should pass single schema',
                          });
                        }
                      })
                    ),
                  }),
                  message: z.string(),
                  kind: z.literal('gate'),
                }),
                z.object({
                  id: z.string().uuid(),
                  title: z.string(),
                  description: z.string(),
                  target: z.object({
                    profileId: z.string().uuid(),
                    eventTypeId: z.string().uuid(),
                  }),
                  kind: z.literal('booking'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            })
          ),
        }),
        columns: z.array(
          z.object({
            id: z.string().uuid(),
            name: z.string(),
            kind: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({ type: z.literal('text') }),
                z.object({ type: z.literal('number') }),
                z.object({ type: z.literal('boolean') }),
                z.object({ type: z.literal('date') }),
                z.object({ type: z.literal('date_range') }),
                z.object({ type: z.literal('link') }),
                z.object({ multi: z.boolean(), type: z.literal('select') }),
                z.object({
                  multi: z.boolean(),
                  type: z.literal('select_number'),
                }),
                z.object({ type: z.literal('tag') }),
                z.object({
                  target: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('USER'),
                      z.literal('DOCUMENT'),
                      z.literal('TASK'),
                      z.literal('COMPANY'),
                      z.literal('CONTACT'),
                      z.literal('CALL_RECORD'),
                      z.literal('CHANNEL'),
                      z.literal('CHAT'),
                      z.literal('PROJECT'),
                      z.literal('THREAD'),
                      z.literal('CALENDAR_EVENT'),
                      z.literal('INITIATIVE'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  multi: z.boolean(),
                  type: z.literal('entity'),
                }),
                z.object({
                  database: z.string().uuid(),
                  table: z.string().uuid(),
                  type: z.literal('relation'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            options: z.array(
              z.object({
                id: z.string().uuid(),
                label: z.string(),
                color: z.union([z.string(), z.null()]).optional(),
              })
            ),
          })
        ),
        projected: z.boolean(),
        editorUrl: z.string(),
        respondentUrl: z.string(),
        acceptingResponses: z.boolean(),
        capabilities: z.object({
          presentationLabels: z.boolean(),
          conditionalColumnCleanup: z.boolean(),
          safeLinkedTypeChanges: z.boolean(),
          requiredBookingQualification: z.boolean(),
        }),
      }),
      summary: z
        .union([
          z.object({
            submitted: z.number().int().gte(0),
            stopped: z.number().int().gte(0),
            stoppedBySection: z.array(
              z.object({
                section: z.string().uuid(),
                count: z.number().int().gte(0),
              })
            ),
            rows: z.number().int().gte(0),
          }),
          z.null(),
        ])
        .optional(),
      view: z.literal('authoring'),
    }),
    z.object({
      detail: z.object({
        form: z.object({
          id: z.string().uuid(),
          name: z.string(),
          description: z.string(),
          ownerId: z.string(),
          databaseId: z.string().uuid(),
          tableId: z.string().uuid(),
          submittedColumnId: z.union([z.string().uuid(), z.null()]).optional(),
          respondentColumnId: z.union([z.string().uuid(), z.null()]).optional(),
          audience: z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('members'), z.literal('public')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          tallyVisible: z.boolean(),
          responsesVisible: z.boolean(),
          anonymous: z.boolean(),
          status: z.any().superRefine((x, ctx) => {
            const schemas = [z.literal('open'), z.literal('closed')];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          closesAt: z
            .union([z.string().datetime({ offset: true }), z.null()])
            .optional(),
          confirmationMessage: z.string(),
          branding: z.object({
            logoFileId: z.union([z.string().uuid(), z.null()]).optional(),
            coverFileId: z.union([z.string().uuid(), z.null()]).optional(),
            accentColor: z.union([z.string(), z.null()]).optional(),
          }),
          createdAt: z.string().datetime({ offset: true }),
          updatedAt: z.string().datetime({ offset: true }),
          projectId: z.union([z.string(), z.null()]).optional(),
          isHiddenInDrive: z.boolean(),
        }),
        access: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('view'),
            z.literal('edit'),
            z.literal('owner'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        tableGone: z.boolean(),
        sections: z.array(
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.object({
                id: z.string().uuid(),
                title: z.string(),
                description: z.string(),
                questions: z.array(
                  z.object({
                    id: z.string().uuid(),
                    column: z.string().uuid(),
                    title: z.string(),
                    kind: z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.object({ type: z.literal('text') }),
                        z.object({ type: z.literal('number') }),
                        z.object({ type: z.literal('boolean') }),
                        z.object({ type: z.literal('date') }),
                        z.object({ type: z.literal('date_range') }),
                        z.object({ type: z.literal('link') }),
                        z.object({
                          multi: z.boolean(),
                          type: z.literal('select'),
                        }),
                        z.object({
                          multi: z.boolean(),
                          type: z.literal('select_number'),
                        }),
                        z.object({ type: z.literal('tag') }),
                        z.object({
                          target: z.any().superRefine((x, ctx) => {
                            const schemas = [
                              z.literal('USER'),
                              z.literal('DOCUMENT'),
                              z.literal('TASK'),
                              z.literal('COMPANY'),
                              z.literal('CONTACT'),
                              z.literal('CALL_RECORD'),
                              z.literal('CHANNEL'),
                              z.literal('CHAT'),
                              z.literal('PROJECT'),
                              z.literal('THREAD'),
                              z.literal('CALENDAR_EVENT'),
                              z.literal('INITIATIVE'),
                            ];
                            const errors = schemas.reduce<z.ZodError[]>(
                              (errors, schema) =>
                                ((result) =>
                                  result.error
                                    ? [...errors, result.error]
                                    : errors)(schema.safeParse(x)),
                              []
                            );
                            if (schemas.length - errors.length !== 1) {
                              ctx.addIssue({
                                path: ctx.path,
                                code: 'invalid_union',
                                unionErrors: errors,
                                message:
                                  'Invalid input: Should pass single schema',
                              });
                            }
                          }),
                          multi: z.boolean(),
                          type: z.literal('entity'),
                        }),
                        z.object({
                          database: z.string().uuid(),
                          table: z.string().uuid(),
                          type: z.literal('relation'),
                        }),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    }),
                    options: z.array(
                      z.object({
                        id: z.string().uuid(),
                        label: z.string(),
                        color: z.union([z.string(), z.null()]).optional(),
                      })
                    ),
                    helpText: z.string(),
                    required: z.boolean(),
                    widget: z
                      .union([
                        z.any().superRefine((x, ctx) => {
                          const schemas = [
                            z.literal('short'),
                            z.literal('paragraph'),
                            z.literal('datetime'),
                            z.literal('date'),
                            z.literal('url'),
                            z.literal('file'),
                            z.literal('choice'),
                            z.literal('dropdown'),
                            z.literal('checkboxes'),
                          ];
                          const errors = schemas.reduce<z.ZodError[]>(
                            (errors, schema) =>
                              ((result) =>
                                result.error
                                  ? [...errors, result.error]
                                  : errors)(schema.safeParse(x)),
                            []
                          );
                          if (schemas.length - errors.length !== 1) {
                            ctx.addIssue({
                              path: ctx.path,
                              code: 'invalid_union',
                              unionErrors: errors,
                              message:
                                'Invalid input: Should pass single schema',
                            });
                          }
                        }),
                        z.null(),
                      ])
                      .optional(),
                  })
                ),
                kind: z.literal('questions'),
              }),
              z.object({
                id: z.string().uuid(),
                title: z.string(),
                description: z.string(),
                rules: z.object({
                  conjunction: z.any().superRefine((x, ctx) => {
                    const schemas = [z.literal('and'), z.literal('or')];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  conditions: z.array(
                    z.any().superRefine((x, ctx) => {
                      const schemas = [
                        z.intersection(
                          z.object({
                            column: z.string().uuid(),
                            test: z.any().superRefine((x, ctx) => {
                              const schemas = [
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('isEmpty'),
                                      z.literal('isNotEmpty'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  kind: z.literal('presence'),
                                }),
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('is'),
                                      z.literal('isNot'),
                                      z.literal('contains'),
                                      z.literal('doesNotContain'),
                                      z.literal('startsWith'),
                                      z.literal('endsWith'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  value: z.string(),
                                  kind: z.literal('text'),
                                }),
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('is'),
                                      z.literal('isNot'),
                                      z.literal('greaterThan'),
                                      z.literal('greaterThanOrEqual'),
                                      z.literal('lessThan'),
                                      z.literal('lessThanOrEqual'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  value: z.number(),
                                  kind: z.literal('number'),
                                }),
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('before'),
                                      z.literal('after'),
                                      z.literal('onOrBefore'),
                                      z.literal('onOrAfter'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  value: z.string().datetime({ offset: true }),
                                  kind: z.literal('date'),
                                }),
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('contains'),
                                      z.literal('doesNotContain'),
                                      z.literal('endsBefore'),
                                      z.literal('endsOnOrAfter'),
                                      z.literal('startsAfter'),
                                      z.literal('startsOnOrBefore'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  value: z.string().datetime({ offset: true }),
                                  kind: z.literal('dateRange'),
                                }),
                                z.object({
                                  checked: z.boolean(),
                                  kind: z.literal('checkbox'),
                                }),
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('isAnyOf'),
                                      z.literal('isNoneOf'),
                                      z.literal('hasAny'),
                                      z.literal('hasAll'),
                                      z.literal('hasNone'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  options: z.array(z.string().uuid()),
                                  kind: z.literal('options'),
                                }),
                                z.object({
                                  operator: z.any().superRefine((x, ctx) => {
                                    const schemas = [
                                      z.literal('isAnyOf'),
                                      z.literal('isNoneOf'),
                                      z.literal('hasAny'),
                                      z.literal('hasAll'),
                                      z.literal('hasNone'),
                                    ];
                                    const errors = schemas.reduce<z.ZodError[]>(
                                      (errors, schema) =>
                                        ((result) =>
                                          result.error
                                            ? [...errors, result.error]
                                            : errors)(schema.safeParse(x)),
                                      []
                                    );
                                    if (schemas.length - errors.length !== 1) {
                                      ctx.addIssue({
                                        path: ctx.path,
                                        code: 'invalid_union',
                                        unionErrors: errors,
                                        message:
                                          'Invalid input: Should pass single schema',
                                      });
                                    }
                                  }),
                                  entities: z.array(z.string()),
                                  kind: z.literal('entities'),
                                }),
                              ];
                              const errors = schemas.reduce<z.ZodError[]>(
                                (errors, schema) =>
                                  ((result) =>
                                    result.error
                                      ? [...errors, result.error]
                                      : errors)(schema.safeParse(x)),
                                []
                              );
                              if (schemas.length - errors.length !== 1) {
                                ctx.addIssue({
                                  path: ctx.path,
                                  code: 'invalid_union',
                                  unionErrors: errors,
                                  message:
                                    'Invalid input: Should pass single schema',
                                });
                              }
                            }),
                          }),
                          z.object({ kind: z.literal('condition') })
                        ),
                        z.intersection(
                          z.object({
                            conjunction: z.any().superRefine((x, ctx) => {
                              const schemas = [
                                z.literal('and'),
                                z.literal('or'),
                              ];
                              const errors = schemas.reduce<z.ZodError[]>(
                                (errors, schema) =>
                                  ((result) =>
                                    result.error
                                      ? [...errors, result.error]
                                      : errors)(schema.safeParse(x)),
                                []
                              );
                              if (schemas.length - errors.length !== 1) {
                                ctx.addIssue({
                                  path: ctx.path,
                                  code: 'invalid_union',
                                  unionErrors: errors,
                                  message:
                                    'Invalid input: Should pass single schema',
                                });
                              }
                            }),
                            conditions: z.any(),
                          }),
                          z.object({ kind: z.literal('group') })
                        ),
                      ];
                      const errors = schemas.reduce<z.ZodError[]>(
                        (errors, schema) =>
                          ((result) =>
                            result.error ? [...errors, result.error] : errors)(
                            schema.safeParse(x)
                          ),
                        []
                      );
                      if (schemas.length - errors.length !== 1) {
                        ctx.addIssue({
                          path: ctx.path,
                          code: 'invalid_union',
                          unionErrors: errors,
                          message: 'Invalid input: Should pass single schema',
                        });
                      }
                    })
                  ),
                }),
                message: z.string(),
                kind: z.literal('gate'),
              }),
              z.object({
                id: z.string().uuid(),
                title: z.string(),
                description: z.string(),
                target: z
                  .union([
                    z.object({
                      profileId: z.string().uuid(),
                      eventTypeId: z.string().uuid(),
                    }),
                    z.null(),
                  ])
                  .optional(),
                kind: z.literal('booking'),
              }),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          })
        ),
      }),
      respondentUrl: z.string(),
      acceptingResponses: z.boolean(),
      view: z.literal('respondent'),
    }),
  ];
  const errors = schemas.reduce<z.ZodError[]>(
    (errors, schema) =>
      ((result) => (result.error ? [...errors, result.error] : errors))(
        schema.safeParse(x)
      ),
    []
  );
  if (schemas.length - errors.length !== 1) {
    ctx.addIssue({
      path: ctx.path,
      code: 'invalid_union',
      unionErrors: errors,
      message: 'Invalid input: Should pass single schema',
    });
  }
});

export const ReadIllustratorDocument = z.object({ documentId: z.string() });

export const ReadIllustratorDocumentResponse = z.object({
  content: z.string(),
});

export const ReadInitiative = z.object({
  initiativeId: z.string().uuid(),
  taskCursor: z.union([z.string(), z.null()]).optional(),
  taskLimit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ProjectReadResult = z.object({
  project: z.object({
    initiativeId: z.string().uuid(),
    name: z.string(),
    ownerId: z.string(),
    memberIds: z.array(z.string()),
    taskIds: z.array(z.string()),
    taskCount: z.number().int().gte(0),
    tasksTruncated: z.boolean(),
    access: z.string(),
    teamAccess: z.union([z.string(), z.null()]).optional(),
    linkScope: z.union([z.string(), z.null()]).optional(),
    linkAccess: z.union([z.string(), z.null()]).optional(),
    channelShares: z.array(
      z.object({ channelId: z.string(), access: z.string() })
    ),
  }),
  properties: z.object({
    status: z.union([z.string().uuid(), z.null()]).optional(),
    priority: z.union([z.string().uuid(), z.null()]).optional(),
    assignees: z.array(z.string()),
    dueDate: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    completed: z.boolean(),
  }),
  description: z.string(),
  nextTaskCursor: z.union([z.string(), z.null()]).optional(),
});

export const ReadInitiativeActivity = z.object({
  initiativeId: z.string().uuid(),
  after: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  before: z.union([z.string().datetime({ offset: true }), z.null()]).optional(),
  cursor: z
    .union([
      z.object({
        occurredAt: z.string().datetime({ offset: true }),
        id: z.string().uuid(),
      }),
      z.null(),
    ])
    .optional(),
  limit: z.union([z.number().int().gte(0).lte(65535), z.null()]).optional(),
});

export const ProjectActivityResult = z.object({
  records: z.array(
    z.object({
      id: z.string().uuid(),
      actorId: z.string(),
      subjectId: z.string(),
      action: z.string(),
      actionPayload: z.any().optional(),
      occurredAt: z.string().datetime({ offset: true }),
    })
  ),
  truncated: z.boolean(),
  nextCursor: z
    .union([
      z.object({
        occurredAt: z.string().datetime({ offset: true }),
        id: z.string().uuid(),
      }),
      z.null(),
    ])
    .optional(),
});

export const ReadMemory = z.record(z.any());

export const ExplicitMemory = z.object({
  content: z.string(),
  revision: z.number().int(),
});

export const ReadMetadata = z.object({ documentId: z.string().uuid() });

export const ReadMetadataResponse = z.object({
  documentMetadata: z.object({
    documentId: z.string(),
    documentVersionId: z.number().int(),
    owner: z.string(),
    documentName: z.string(),
    fileType: z.union([z.string(), z.null()]).optional(),
    sha: z.union([z.string(), z.null()]).optional(),
    projectId: z.union([z.string(), z.null()]).optional(),
    projectName: z.union([z.string(), z.null()]).optional(),
    branchedFromId: z.union([z.string(), z.null()]).optional(),
    branchedFromVersionId: z.union([z.number().int(), z.null()]).optional(),
    documentFamilyId: z.union([z.number().int(), z.null()]).optional(),
    documentBom: z.any().optional(),
    modificationData: z.any().optional(),
    createdAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    updatedAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    deletedAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
    subType: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('task'),
            z.literal('snippet'),
            z.literal('skill'),
            z.literal('initiative_description'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    teamId: z.union([z.string(), z.null()]).optional(),
    teamTaskId: z.union([z.number().int(), z.null()]).optional(),
    content: z.object({
      state: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('unknown'),
          z.literal('pending'),
          z.literal('ready'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      location: z
        .union([
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('object_storage'),
              z.literal('sync_service'),
              z.literal('docx_bom_parts'),
              z.literal('converted_pdf'),
              z.literal('unknown'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          z.null(),
        ])
        .optional(),
    }),
    branchName: z.union([z.string(), z.null()]).optional(),
  }),
  userAccessLevel: z.enum(['view', 'comment', 'edit', 'owner']),
});

export const ReadPhotoshopDocument = z.object({ documentId: z.string() });

export const ReadPhotoshopDocumentResponse = z.object({ content: z.string() });

export const ReadPresentation = z.object({
  documentId: z.string(),
  slides: z.union([z.array(z.number().int().gte(0)), z.null()]).optional(),
});

export const ReadPresentationResponse = z.object({ content: z.string() });

export const ReadProject = z.object({ projectId: z.string().uuid() });

export const ReadProjectResponse = z.object({
  projectId: z.string().uuid(),
  projectName: z.string(),
  parentProjectId: z.union([z.string(), z.null()]).optional(),
  items: z.array(
    z.object({
      id: z.string(),
      itemType: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('document'),
          z.literal('project'),
          z.literal('chat'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      name: z.string(),
      fileType: z.union([z.string(), z.null()]).optional(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const ReadRoutine = z.object({ routineId: z.string().uuid() });

export const RoutineDetails = z.object({
  routine: z.object({
    id: z.string().uuid(),
    name: z.string(),
    enabled: z.boolean(),
    trigger: z.any(),
    task: z.any(),
    nextRunAt: z
      .union([z.string().datetime({ offset: true }), z.null()])
      .optional(),
  }),
  runs: z.array(z.any()),
});

export const ReadSkill = z.object({ documentId: z.string().uuid() });

export const ReadSkillResponse = z.object({
  documentId: z.string().uuid(),
  name: z.string(),
  content: z.string(),
});

export const ReadSpreadsheet = z.object({
  documentId: z.string(),
  sheetId: z.union([z.string(), z.null()]).optional(),
  ranges: z.union([z.array(z.string()), z.null()]).optional(),
  includeStyles: z.union([z.boolean(), z.null()]).optional(),
});

export const ReadWordDocument = z.object({
  documentId: z.string(),
  start: z.union([z.number().int().gte(0), z.null()]).optional(),
  count: z.union([z.number().int().gte(0), z.null()]).optional(),
});

export const RemoveFromPipeline = z.object({
  pipelineId: z.string().uuid(),
  entryIds: z.array(z.string().uuid()).optional(),
  recordIds: z.array(z.string().uuid()).optional(),
});

export const RemoveFromPipelineResponse = z.object({
  removed: z.array(
    z.object({
      entryId: z.string().uuid(),
      record: z.object({
        id: z.string().uuid(),
        name: z.union([z.string(), z.null()]).optional(),
        detail: z.union([z.string(), z.null()]).optional(),
      }),
      fields: z.record(z.any()),
    })
  ),
  version: z.union([z.number().int(), z.null()]).optional(),
  summary: z.string(),
});

export const RenameChannel = z.object({
  channelId: z.string().uuid(),
  name: z.string(),
});

export const RenameChannelResponse = z.object({
  channelId: z.string().uuid(),
  name: z.string(),
  previousName: z.union([z.string(), z.null()]).optional(),
  summary: z.string(),
});

export const RenameDocument = z.object({
  documentId: z.string().uuid(),
  documentName: z.string(),
});

export const RenameDocumentResponse = z.object({
  success: z.boolean(),
  documentId: z.string().uuid(),
  message: z.string(),
});

export const RenameItem = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('project'), z.literal('chat')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  name: z.string(),
});

export const RenameItemResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('project'), z.literal('chat')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  name: z.string(),
});

export const RenderCanvas = z.object({
  document_id: z.string(),
  item_ids: z.union([z.array(z.string()), z.null()]).optional(),
  crop: z
    .union([
      z.object({
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
      }),
      z.null(),
    ])
    .optional(),
});

export const CanvasRender = z.object({
  revision: z.string(),
  clock: z.record(z.number().int().gte(0)),
  crop: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  }),
  width: z.number().int().gte(0),
  height: z.number().int().gte(0),
  objects: z.array(
    z.object({
      id: z.string(),
      bounds: z.object({
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
      }),
    })
  ),
  unavailable: z.array(z.object({ id: z.string(), reason: z.string() })),
  textLayout: z
    .array(
      z.object({
        id: z.string(),
        bounds: z.object({
          x: z.number(),
          y: z.number(),
          width: z.number(),
          height: z.number(),
        }),
        overflow: z.boolean(),
      })
    )
    .optional(),
  modelMedia: z.array(
    z.object({ type: z.string(), mediaType: z.string(), data: z.string() })
  ),
});

export const ResolveComment = z.object({
  threadId: z.string().uuid(),
  resolved: z.boolean().optional(),
});

export const ResolveCommentResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('initiative'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string(),
  threadId: z.string().uuid(),
  resolved: z.boolean(),
});

export const RunRoutine = z.object({ routineId: z.string().uuid() });

export const RoutineRunStarted = z.object({
  routineId: z.string().uuid(),
  resource: z.any().optional(),
});

export const SaveDatabaseQuery = z.object({
  databaseId: z.union([z.string().uuid(), z.null()]).optional(),
  sql: z.string(),
  title: z.string(),
  displayMode: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('table'),
      z.literal('scalar'),
      z.literal('bar'),
      z.literal('line'),
      z.literal('area'),
      z.literal('scatter'),
      z.literal('pie'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  chart: z
    .union([
      z.object({
        x: z.string(),
        y: z.array(z.string()),
        title: z.union([z.string(), z.null()]).optional(),
        color: z.union([z.string(), z.null()]).optional(),
        stack: z.union([z.boolean(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
  prompt: z.union([z.string(), z.null()]).optional(),
});

export const SaveDatabaseQueryResponse = z.object({
  queryId: z.string().uuid(),
  markdown: z.string(),
});

export const SaveDatabaseView = z.object({
  databaseId: z.string().uuid(),
  tableId: z.string().uuid(),
  name: z.string(),
  filter: z
    .union([
      z.object({
        conjunction: z.any().superRefine((x, ctx) => {
          const schemas = [z.literal('and'), z.literal('or')];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        conditions: z.array(
          z.object({
            column: z.string().uuid(),
            test: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('isEmpty'),
                      z.literal('isNotEmpty'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  kind: z.literal('presence'),
                }),
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('is'),
                      z.literal('isNot'),
                      z.literal('contains'),
                      z.literal('doesNotContain'),
                      z.literal('startsWith'),
                      z.literal('endsWith'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  value: z.string(),
                  kind: z.literal('text'),
                }),
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('is'),
                      z.literal('isNot'),
                      z.literal('greaterThan'),
                      z.literal('greaterThanOrEqual'),
                      z.literal('lessThan'),
                      z.literal('lessThanOrEqual'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  value: z.number(),
                  kind: z.literal('number'),
                }),
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('before'),
                      z.literal('after'),
                      z.literal('onOrBefore'),
                      z.literal('onOrAfter'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  value: z.string().datetime({ offset: true }),
                  kind: z.literal('date'),
                }),
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('contains'),
                      z.literal('doesNotContain'),
                      z.literal('endsBefore'),
                      z.literal('endsOnOrAfter'),
                      z.literal('startsAfter'),
                      z.literal('startsOnOrBefore'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  value: z.string().datetime({ offset: true }),
                  kind: z.literal('dateRange'),
                }),
                z.object({ checked: z.boolean(), kind: z.literal('checkbox') }),
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('isAnyOf'),
                      z.literal('isNoneOf'),
                      z.literal('hasAny'),
                      z.literal('hasAll'),
                      z.literal('hasNone'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  options: z.array(z.string().uuid()),
                  kind: z.literal('options'),
                }),
                z.object({
                  operator: z.any().superRefine((x, ctx) => {
                    const schemas = [
                      z.literal('isAnyOf'),
                      z.literal('isNoneOf'),
                      z.literal('hasAny'),
                      z.literal('hasAll'),
                      z.literal('hasNone'),
                    ];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  entities: z.array(z.string()),
                  kind: z.literal('entities'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
          })
        ),
      }),
      z.null(),
    ])
    .optional(),
  sort: z
    .array(
      z.object({
        column: z.string().uuid(),
        direction: z.any().superRefine((x, ctx) => {
          const schemas = [z.literal('ascending'), z.literal('descending')];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
      })
    )
    .optional(),
  layout: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        columns: z.array(
          z.object({
            column: z.string().uuid(),
            width: z.union([z.number().int().gte(0), z.null()]).optional(),
          })
        ),
        kind: z.literal('table'),
      }),
      z.object({
        groupBy: z.string().uuid(),
        title: z.union([z.string().uuid(), z.null()]).optional(),
        lanes: z.array(
          z.object({
            key: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({ kind: z.literal('option'), id: z.string().uuid() }),
                z.object({ kind: z.literal('user'), id: z.string() }),
                z.object({ kind: z.literal('none') }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            hidden: z.boolean().optional(),
          })
        ),
        cardFields: z.array(z.string().uuid()),
        hideEmptyLanes: z.boolean(),
        kind: z.literal('board'),
      }),
      z.object({
        range: z.string().uuid(),
        title: z.union([z.string().uuid(), z.null()]).optional(),
        groupBy: z.union([z.string().uuid(), z.null()]).optional(),
        barFields: z.array(z.string().uuid()).optional(),
        colorBy: z.union([z.string().uuid(), z.null()]).optional(),
        kind: z.literal('timeline'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const SavedDatabaseView = z.object({
  view: z.any(),
  created: z.boolean(),
});

export const SearchSkills = z.object({
  name: z.string(),
  matchType: z
    .any()
    .superRefine((x, ctx) => {
      const schemas = [z.literal('partial'), z.literal('exact')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    })
    .optional(),
});

export const SearchSkillsResponse = z.object({
  results: z.array(
    z.object({
      documentId: z.string().uuid(),
      name: z.string(),
      updatedAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
    })
  ),
});

export const SearchTools = z.object({ query: z.string() });

export const SearchToolsResponse = z.object({
  results: z.array(z.object({ name: z.string(), description: z.string() })),
  additional_matches: z.array(
    z.object({ name: z.string(), description: z.string() })
  ),
});

export const SelfKnowledge = z.record(z.any());

export const SelfKnowledgeResponse = z.object({ about: z.string() });

export const SendChannelMessage = z.object({
  content: z.string(),
  channel_id: z.string().uuid(),
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
  attachments: z
    .array(
      z.object({
        id: z.string(),
        kind: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('image'),
            z.literal('video'),
            z.literal('document'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        width: z.union([z.number().int(), z.null()]).optional(),
        height: z.union([z.number().int(), z.null()]).optional(),
      })
    )
    .optional(),
});

export const SendChannelMessageResponse = z.object({
  channel_id: z.string().uuid(),
  message_id: z.string(),
});

export const SendConfirmedEmail = z.object({
  subject: z.string(),
  from: z.union([z.string(), z.null()]).optional(),
  body: z.string(),
  to: z.array(
    z.object({
      email: z.string(),
      name: z.union([z.string(), z.null()]).optional(),
    })
  ),
  cc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  bcc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  replyingToId: z.union([z.string().uuid(), z.null()]).optional(),
  includeSignature: z.union([z.boolean(), z.null()]).optional(),
  attachments: z.array(z.object({ documentId: z.string().uuid() })).optional(),
  userConfirmation: z.string(),
});

export const SendEmailResponse = z.any().superRefine((x, ctx) => {
  const schemas = [
    z.literal('userEdited'),
    z
      .object({
        sent: z.object({
          message_id: z.string().uuid(),
          thread_id: z.string().uuid(),
        }),
      })
      .strict(),
    z
      .object({ convertedToDraft: z.object({ draft_id: z.string().uuid() }) })
      .strict(),
  ];
  const errors = schemas.reduce<z.ZodError[]>(
    (errors, schema) =>
      ((result) => (result.error ? [...errors, result.error] : errors))(
        schema.safeParse(x)
      ),
    []
  );
  if (schemas.length - errors.length !== 1) {
    ctx.addIssue({
      path: ctx.path,
      code: 'invalid_union',
      unionErrors: errors,
      message: 'Invalid input: Should pass single schema',
    });
  }
});

export const SendEmail = z.object({
  subject: z.string(),
  from: z.union([z.string(), z.null()]).optional(),
  body: z.string(),
  to: z.array(
    z.object({
      email: z.string(),
      name: z.union([z.string(), z.null()]).optional(),
    })
  ),
  cc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  bcc: z
    .array(
      z.object({
        email: z.string(),
        name: z.union([z.string(), z.null()]).optional(),
      })
    )
    .optional(),
  replyingToId: z.union([z.string().uuid(), z.null()]).optional(),
  includeSignature: z.union([z.boolean(), z.null()]).optional(),
  attachments: z.array(z.object({ documentId: z.string().uuid() })).optional(),
});

export const UserToolResponseForSendEmailResponse = z
  .any()
  .superRefine((x, ctx) => {
    const schemas = [
      z.literal('PendingUserExecution'),
      z.literal('Rejected'),
      z
        .object({
          UserAction: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('userEdited'),
              z
                .object({
                  sent: z.object({
                    message_id: z.string().uuid(),
                    thread_id: z.string().uuid(),
                  }),
                })
                .strict(),
              z
                .object({
                  convertedToDraft: z.object({ draft_id: z.string().uuid() }),
                })
                .strict(),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        })
        .strict(),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  });

export const SetEntityProperty = z.object({
  entity_id: z.string(),
  entity_type: z.enum([
    'document',
    'initiative',
    'project',
    'thread',
    'channel',
    'call',
    'user',
    'company',
    'chat',
  ]),
  property_definition_id: z.string().uuid(),
  boolean_value: z.union([z.boolean(), z.null()]).optional(),
  date_value: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
  date_range_value: z
    .union([
      z.object({
        start: z.string().datetime({ offset: true }),
        end: z.string().datetime({ offset: true }),
      }),
      z.null(),
    ])
    .optional(),
  number_value: z.union([z.number(), z.null()]).optional(),
  string_value: z.union([z.string(), z.null()]).optional(),
  option_id: z.union([z.string().uuid(), z.null()]).optional(),
  option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  add_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  remove_option_ids: z.union([z.array(z.string().uuid()), z.null()]).optional(),
  entity_ref: z
    .union([
      z.object({
        entityType: z.enum([
          'document',
          'task',
          'initiative',
          'project',
          'thread',
          'channel',
          'call',
          'user',
          'company',
          'contact',
          'chat',
        ]),
        entityId: z.string(),
      }),
      z.null(),
    ])
    .optional(),
  entity_refs: z
    .union([
      z.array(
        z.object({
          entityType: z.enum([
            'document',
            'task',
            'initiative',
            'project',
            'thread',
            'channel',
            'call',
            'user',
            'company',
            'contact',
            'chat',
          ]),
          entityId: z.string(),
        })
      ),
      z.null(),
    ])
    .optional(),
  link_url: z.union([z.string(), z.null()]).optional(),
  link_urls: z.union([z.array(z.string()), z.null()]).optional(),
});

export const SetEntityPropertyResponse = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const SetFavorite = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('project'),
      z.literal('channel'),
      z.literal('email'),
      z.literal('call'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  favorite: z.boolean(),
});

export const SetFavoriteResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('project'),
      z.literal('channel'),
      z.literal('email'),
      z.literal('call'),
      z.literal('crm_company'),
      z.literal('crm_contact'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  favorite: z.boolean(),
});

export const SetFormAccess = z.object({
  formId: z.string().uuid(),
  draft: z
    .object({
      audience: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('members'), z.literal('public')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      status: z.any().superRefine((x, ctx) => {
        const schemas = [z.literal('open'), z.literal('closed')];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      closesAt: z
        .union([z.string().datetime({ offset: true }), z.null()])
        .optional(),
      tallyVisible: z.boolean(),
      responsesVisible: z.union([z.boolean(), z.null()]).optional(),
      anonymous: z.union([z.boolean(), z.null()]).optional(),
      channelGrants: z
        .array(
          z.any().superRefine((x, ctx) => {
            const schemas = [
              z
                .object({
                  channelId: z.string().uuid(),
                  access: z.any().superRefine((x, ctx) => {
                    const schemas = [z.literal('view'), z.literal('edit')];
                    const errors = schemas.reduce<z.ZodError[]>(
                      (errors, schema) =>
                        ((result) =>
                          result.error ? [...errors, result.error] : errors)(
                          schema.safeParse(x)
                        ),
                      []
                    );
                    if (schemas.length - errors.length !== 1) {
                      ctx.addIssue({
                        path: ctx.path,
                        code: 'invalid_union',
                        unionErrors: errors,
                        message: 'Invalid input: Should pass single schema',
                      });
                    }
                  }),
                  operation: z.literal('upsert'),
                })
                .strict(),
              z
                .object({
                  channelId: z.string().uuid(),
                  operation: z.literal('remove'),
                })
                .strict(),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          })
        )
        .optional(),
    })
    .strict(),
});

export const SetSenderPolicy = z.object({
  sender_email: z.string(),
  policy: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('signal'),
      z.literal('noise'),
      z.literal('block'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  thread_id: z.union([z.string().uuid(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
});

export const SetSenderPolicyResponse = z.object({
  senderEmail: z.string(),
  policy: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('signal'),
      z.literal('noise'),
      z.literal('block'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  inbox: z.string(),
  summary: z.string(),
});

export const ShareWithChannel = z.object({
  channelId: z.string().uuid(),
  entityId: z.string(),
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('calendar_event'),
      z.literal('document'),
      z.literal('project'),
      z.literal('database'),
      z.literal('form'),
      z.literal('call'),
      z.literal('thread'),
      z.literal('agent_session'),
      z.literal('external_chat'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const ShareWithChannelResponse = z.object({
  channelId: z.string().uuid(),
  entityId: z.string(),
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('calendar_event'),
      z.literal('document'),
      z.literal('project'),
      z.literal('database'),
      z.literal('form'),
      z.literal('call'),
      z.literal('thread'),
      z.literal('agent_session'),
      z.literal('external_chat'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  summary: z.string(),
});

export const Subagent = z.object({ task: z.string() });

export const SubagentResponse = z.object({ result: z.string() });

export const TextEditorCodeExecution = z.object({ input: z.string() });

export const TextEditorCodeExecutionResponse = z.object({
  tool_use_id: z.string(),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          file_type: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
        }),
        z.object({ type: z.literal('text_editor_code_execution_view_result') })
      ),
      z.intersection(
        z.object({
          file_type: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_create_result'),
        })
      ),
      z.intersection(
        z.object({
          file_type: z.union([z.string(), z.null()]).optional(),
          content: z.union([z.string(), z.null()]).optional(),
          numLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          startLine: z.union([z.number().int().gte(0), z.null()]).optional(),
          totalLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          is_file_update: z.union([z.boolean(), z.null()]).optional(),
          oldStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          oldLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          newStart: z.union([z.number().int().gte(0), z.null()]).optional(),
          newLines: z.union([z.number().int().gte(0), z.null()]).optional(),
          lines: z.union([z.array(z.string()), z.null()]).optional(),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_str_replace_result'),
        })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('unavailable'),
              z.literal('execution_time_exceeded'),
              z.literal('container_expired'),
              z.literal('invalid_input'),
              z.literal('too_many_requests'),
              z.literal('file_not_found'),
              z.literal('string_not_found'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({
          type: z.literal('text_editor_code_execution_tool_result_error'),
        })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const UpdateAgentSessionSharing = z
  .object({
    sessionId: z.string().uuid(),
    linkScope: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('off'),
            z.literal('public'),
            z.literal('team'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    linkAccess: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('off'),
            z.literal('view'),
            z.literal('comment'),
            z.literal('edit'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    teamAccess: z
      .union([
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('off'),
            z.literal('view'),
            z.literal('comment'),
            z.literal('edit'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        z.null(),
      ])
      .optional(),
    channels: z
      .union([
        z.array(
          z.object({
            channelId: z.string().uuid(),
            access: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('off'),
                z.literal('view'),
                z.literal('comment'),
                z.literal('edit'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
          })
        ),
        z.null(),
      ])
      .optional(),
  })
  .strict();

export const UpdateBookingSettings = z
  .object({
    teamId: z.union([z.string().uuid(), z.null()]).optional(),
    profile: z.object({
      id: z.string().uuid(),
      name: z.string(),
      description: z.string(),
      schedules: z.array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          timeZone: z.string(),
          weekly: z.array(
            z.object({
              day: z.number().int().gte(0).lte(255),
              windows: z.array(
                z.object({ start: z.string(), end: z.string() })
              ),
            })
          ),
          overrides: z.array(
            z.object({
              date: z.string().date(),
              windows: z.array(
                z.object({ start: z.string(), end: z.string() })
              ),
            })
          ),
        })
      ),
      defaultScheduleId: z.union([z.string().uuid(), z.null()]).optional(),
      eventTypes: z.array(
        z.object({
          id: z.string().uuid(),
          title: z.string(),
          slug: z.string(),
          description: z.string(),
          durationMinutes: z.number().int().gte(0).lte(65535),
          location: z.string(),
          macroCall: z.boolean().optional(),
          googleMeet: z.boolean(),
          enabled: z.boolean(),
          scheduleId: z.string().uuid(),
          mode: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('individual'),
              z.literal('collective'),
              z.literal('roundRobin'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          hosts: z.array(z.string()),
          beforeMinutes: z.number().int().gte(0).lte(65535),
          afterMinutes: z.number().int().gte(0).lte(65535),
          noticeMinutes: z.number().int().gte(0).lte(65535),
          horizonDays: z.number().int().gte(0).lte(65535),
          intervalMinutes: z.number().int().gte(0).lte(65535),
          dailyLimit: z
            .union([z.number().int().gte(0).lte(65535), z.null()])
            .optional(),
          requiresConfirmation: z.boolean(),
          questions: z.array(
            z.object({
              id: z.string().uuid(),
              label: z.string(),
              required: z.boolean(),
            })
          ),
        })
      ),
      revision: z.number().int(),
    }),
    userConfirmation: z.string(),
  })
  .strict();

export const UpdateCalendarEvent = z.object({
  eventId: z.string().uuid(),
  calendarId: z.union([z.string().uuid(), z.null()]).optional(),
  inbox: z.union([z.string(), z.null()]).optional(),
  scope: z.any().superRefine((x, ctx) => {
    const schemas = [z.literal('all'), z.literal('this_event')];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  recurrenceId: z.union([z.string(), z.null()]).optional(),
  title: z.union([z.string(), z.null()]).optional(),
  description: z.union([z.string(), z.null()]).optional(),
  location: z.union([z.string(), z.null()]).optional(),
  time: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.object({
            startsAt: z.string().datetime({ offset: true }),
            endsAt: z.string().datetime({ offset: true }),
            timeZone: z.union([z.string(), z.null()]).optional(),
            kind: z.literal('timed'),
          }),
          z.object({
            startDate: z.string().date(),
            endDate: z.string().date(),
            kind: z.literal('allDay'),
          }),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  attendees: z
    .union([
      z.array(
        z.object({ email: z.string(), isOptional: z.boolean().optional() })
      ),
      z.null(),
    ])
    .optional(),
  recurrenceLines: z.union([z.array(z.string()), z.null()]).optional(),
  conference: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('provider_default'),
          z.literal('microsoft_teams'),
          z.literal('google_meet'),
          z.literal('remove'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  reminders: z
    .union([
      z.object({
        useDefault: z.boolean(),
        overrides: z
          .array(
            z.object({ method: z.string(), minutes: z.number().int().gte(0) })
          )
          .optional(),
      }),
      z.null(),
    ])
    .optional(),
  rsvp: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('accepted'),
          z.literal('declined'),
          z.literal('tentative'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  outOfOffice: z
    .union([
      z.object({
        autoDeclineMode: z
          .union([
            z.any().superRefine((x, ctx) => {
              const schemas = [
                z.literal('decline_none'),
                z.literal('decline_all'),
                z.literal('decline_new_only'),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            z.null(),
          ])
          .optional(),
        declineMessage: z.union([z.string(), z.null()]).optional(),
      }),
      z.null(),
    ])
    .optional(),
  guestPolicy: z
    .union([
      z.object({
        canModify: z.boolean(),
        canInviteOthers: z.boolean(),
        canSeeOtherGuests: z.boolean(),
      }),
      z.null(),
    ])
    .optional(),
});

export const UpdateCallRecord = z.object({
  callId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  shareWithTeam: z.union([z.boolean(), z.null()]).optional(),
});

export const UpdateCallRecordResponse = z.object({
  callId: z.string().uuid(),
  summary: z.string(),
});

export const UpdateInitiative = z.object({
  initiativeId: z.string().uuid(),
  name: z.union([z.string(), z.null()]).optional(),
  memberIds: z.union([z.array(z.string()), z.null()]).optional(),
});

export const UpdateInitiativeSharing = z.object({
  initiativeId: z.string().uuid(),
  teamAccess: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('off'),
          z.literal('view'),
          z.literal('comment'),
          z.literal('edit'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  linkScope: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('off'),
          z.literal('public'),
          z.literal('team'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  linkAccess: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('off'),
          z.literal('view'),
          z.literal('comment'),
          z.literal('edit'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  channels: z
    .union([
      z.array(
        z.object({
          channelId: z.string().uuid(),
          access: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('off'),
              z.literal('view'),
              z.literal('comment'),
              z.literal('edit'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        })
      ),
      z.null(),
    ])
    .optional(),
});

export const UpdateMemory = z.object({
  revision: z.number().int(),
  edit: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        line: z.number().int().gte(0),
        content: z.string(),
        action: z.literal('insert'),
      }),
      z.object({
        line: z.number().int().gte(0),
        content: z.string(),
        action: z.literal('replace'),
      }),
      z.object({ line: z.number().int().gte(0), action: z.literal('delete') }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const UpdatePipelineEntries = z.object({
  pipelineId: z.string().uuid(),
  updates: z.array(
    z.object({
      entryId: z.union([z.string().uuid(), z.null()]).optional(),
      recordId: z.union([z.string().uuid(), z.null()]).optional(),
      set: z.array(
        z.object({
          field: z.string(),
          value: z.union([z.string(), z.null()]).optional(),
          values: z.union([z.array(z.string()), z.null()]).optional(),
        })
      ),
    })
  ),
  baseVersion: z.union([z.number().int(), z.null()]).optional(),
});

export const UpdatePipelineEntriesResponse = z.object({
  updated: z.array(
    z.object({
      entryId: z.string().uuid(),
      record: z.object({
        id: z.string().uuid(),
        name: z.union([z.string(), z.null()]).optional(),
        detail: z.union([z.string(), z.null()]).optional(),
      }),
      changes: z.array(
        z.object({ field: z.string(), before: z.any(), after: z.any() })
      ),
    })
  ),
  version: z.union([z.number().int(), z.null()]).optional(),
  summary: z.string(),
});

export const UpdateRoutine = z.object({
  routineId: z.string().uuid(),
  change: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({ enabled: z.boolean(), type: z.literal('enabled') }),
      z.object({
        configuration: z.object({
          name: z.string(),
          instructions: z.string(),
          target: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.object({ model: z.string(), type: z.literal('model') }),
              z.object({
                agentId: z.string().uuid(),
                type: z.literal('agent'),
              }),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          schedule: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.object({
                at: z.string().datetime({ offset: true }),
                type: z.literal('once'),
              }),
              z.object({
                expression: z.string(),
                timezone: z.string(),
                type: z.literal('cron'),
              }),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
          requiredApps: z
            .array(z.object({ appSlug: z.string(), name: z.string() }))
            .optional(),
        }),
        type: z.literal('configuration'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const UpdateSharing = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('project'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  linkScope: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('off'),
          z.literal('public'),
          z.literal('team'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  linkAccess: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('off'),
          z.literal('view'),
          z.literal('comment'),
          z.literal('edit'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  teamAccess: z
    .union([
      z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('off'),
          z.literal('view'),
          z.literal('comment'),
          z.literal('edit'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      z.null(),
    ])
    .optional(),
  channels: z
    .union([
      z.array(
        z.object({
          channelId: z.string().uuid(),
          access: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('off'),
              z.literal('view'),
              z.literal('comment'),
              z.literal('edit'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        })
      ),
      z.null(),
    ])
    .optional(),
});

export const UpdateSharingResponse = z.object({
  entityType: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('document'),
      z.literal('project'),
      z.literal('chat'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  entityId: z.string().uuid(),
  message: z.string(),
});

export const UpdateThreadLabels = z.object({
  thread_id: z.string().uuid(),
  label_id: z.string().uuid(),
  add: z.boolean(),
});

export const UpdateThreadLabelsResponse = z.object({
  successfulCount: z.number().int().gte(0),
  failedCount: z.number().int().gte(0),
  summary: z.string(),
});

export const UploadFile = z.object({
  fileName: z.string(),
  contentBase64: z.string(),
  projectId: z.union([z.string().uuid(), z.null()]).optional(),
});

export const UploadFileResponse = z.object({
  documentId: z.string(),
  fileName: z.string(),
  sizeBytes: z.number().int().gte(0),
});

export const WebFetch = z.object({ input: z.string() });

export const WebFetchResponse = z.object({
  tool_use_id: z.string(),
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.intersection(
        z.object({
          url: z.string(),
          content: z.object({
            source: z.any().superRefine((x, ctx) => {
              const schemas = [
                z.object({
                  media_type: z.string(),
                  data: z.string(),
                  type: z.literal('text'),
                }),
                z.object({
                  media_type: z.string(),
                  data: z.string(),
                  type: z.literal('base64'),
                }),
              ];
              const errors = schemas.reduce<z.ZodError[]>(
                (errors, schema) =>
                  ((result) =>
                    result.error ? [...errors, result.error] : errors)(
                    schema.safeParse(x)
                  ),
                []
              );
              if (schemas.length - errors.length !== 1) {
                ctx.addIssue({
                  path: ctx.path,
                  code: 'invalid_union',
                  unionErrors: errors,
                  message: 'Invalid input: Should pass single schema',
                });
              }
            }),
            title: z.union([z.string(), z.null()]).optional(),
            citations: z
              .union([z.object({ enabled: z.boolean() }), z.null()])
              .optional(),
          }),
          retrieved_at: z.string(),
        }),
        z.object({ type: z.literal('web_fetch_result') })
      ),
      z.intersection(
        z.object({
          error_code: z.any().superRefine((x, ctx) => {
            const schemas = [
              z.literal('invalid_input'),
              z.literal('url_too_long'),
              z.literal('url_not_allowed'),
              z.literal('url_not_accessible'),
              z.literal('too_many_requests'),
              z.literal('unsupported_content_type'),
              z.literal('max_uses_exceeded'),
              z.literal('unavailable'),
            ];
            const errors = schemas.reduce<z.ZodError[]>(
              (errors, schema) =>
                ((result) =>
                  result.error ? [...errors, result.error] : errors)(
                  schema.safeParse(x)
                ),
              []
            );
            if (schemas.length - errors.length !== 1) {
              ctx.addIssue({
                path: ctx.path,
                code: 'invalid_union',
                unionErrors: errors,
                message: 'Invalid input: Should pass single schema',
              });
            }
          }),
        }),
        z.object({ type: z.literal('web_fetch_tool_result_error') })
      ),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});

export const WebSearch = z.object({ input: z.string() });

export const WebSearchResponse = z.object({
  content: z.union([
    z.array(
      z.object({
        title: z.string(),
        url: z.string(),
        encrypted_content: z.union([z.string(), z.null()]).optional(),
        page_age: z.union([z.string(), z.null()]).optional(),
        type: z.literal('web_search_result'),
      })
    ),
    z.object({ type: z.string(), error_code: z.string() }),
  ]),
  tool_use_id: z.string(),
});

export const DescribeCodeTools = z
  .object({
    task: z.union([z.string(), z.null()]).optional(),
    names: z.array(z.string()).optional(),
    topics: z
      .array(
        z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('search'),
            z.literal('ai'),
            z.literal('templates'),
            z.literal('examples'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        })
      )
      .optional(),
  })
  .strict();

export const CodeToolsDescription = z.object({
  task_documentation: z
    .union([
      z.object({
        status: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('selected'),
            z.literal('unconfigured'),
            z.literal('cancelled'),
            z.literal('provider_error'),
            z.literal('timeout'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
        entities: z.array(
          z.object({
            name: z.string(),
            description: z.string(),
            index: z.union([z.string(), z.null()]).optional(),
          })
        ),
        omitted: z.array(z.string()),
      }),
      z.null(),
    ])
    .optional(),
  sdk_guide: z.union([z.string(), z.null()]).optional(),
  guides: z.array(
    z.object({
      topic: z.any().superRefine((x, ctx) => {
        const schemas = [
          z.literal('search'),
          z.literal('ai'),
          z.literal('templates'),
          z.literal('examples'),
        ];
        const errors = schemas.reduce<z.ZodError[]>(
          (errors, schema) =>
            ((result) => (result.error ? [...errors, result.error] : errors))(
              schema.safeParse(x)
            ),
          []
        );
        if (schemas.length - errors.length !== 1) {
          ctx.addIssue({
            path: ctx.path,
            code: 'invalid_union',
            unionErrors: errors,
            message: 'Invalid input: Should pass single schema',
          });
        }
      }),
      markdown: z.string(),
    })
  ),
  instructions: z.string(),
  tools: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
      index: z.union([z.string(), z.null()]).optional(),
    })
  ),
});

export const ExecuteCode = z
  .object({
    execution_id: z.string(),
    acting_as: z.any().superRefine((x, ctx) => {
      const schemas = [z.literal('requester'), z.literal('owner')];
      const errors = schemas.reduce<z.ZodError[]>(
        (errors, schema) =>
          ((result) => (result.error ? [...errors, result.error] : errors))(
            schema.safeParse(x)
          ),
        []
      );
      if (schemas.length - errors.length !== 1) {
        ctx.addIssue({
          path: ctx.path,
          code: 'invalid_union',
          unionErrors: errors,
          message: 'Invalid input: Should pass single schema',
        });
      }
    }),
    turn_id: z.string().uuid(),
    source: z.string(),
    timeout_ms: z.number().int().gte(0).optional(),
  })
  .strict();

export const ExecutionReceipt = z.object({
  executionId: z.string(),
  status: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.literal('running'),
      z.literal('succeeded'),
      z.literal('failed'),
      z.literal('cancelled'),
      z.literal('timed_out'),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
  activity: z
    .array(
      z.object({
        id: z.number().int().gte(0),
        label: z.string(),
        status: z.any().superRefine((x, ctx) => {
          const schemas = [
            z.literal('running'),
            z.literal('completed'),
            z.literal('failed'),
            z.literal('unknown'),
          ];
          const errors = schemas.reduce<z.ZodError[]>(
            (errors, schema) =>
              ((result) => (result.error ? [...errors, result.error] : errors))(
                schema.safeParse(x)
              ),
            []
          );
          if (schemas.length - errors.length !== 1) {
            ctx.addIssue({
              path: ctx.path,
              code: 'invalid_union',
              unionErrors: errors,
              message: 'Invalid input: Should pass single schema',
            });
          }
        }),
      })
    )
    .optional(),
  result: z.any().optional(),
  error: z.union([z.string(), z.null()]).optional(),
});

export const ReadCodeDocs = z.object({ symbols: z.array(z.string()) }).strict();

export const SymbolReferences = z.object({
  references: z.array(z.object({ symbol: z.string(), reference: z.string() })),
  omitted: z.array(z.string()),
  unknown: z.array(z.string()),
});

export const ReadChat = z.object({ chatId: z.string() });

export const ReadChatResponse = z.object({
  chatId: z.string(),
  title: z.string(),
  messages: z.array(
    z.object({
      role: z.string(),
      content: z.string(),
      attachmentIds: z.array(z.string()),
    })
  ),
});

export const ReadThread = z.object({
  contentType: z.enum([
    'channel',
    'channel-message',
    'chat-thread',
    'chat-message',
    'project',
  ]),
  ids: z.array(z.string()),
  messagesSince: z
    .union([z.string().datetime({ offset: true }), z.null()])
    .optional(),
});

export const ReadResponse = z.object({
  content: z.any().superRefine((x, ctx) => {
    const schemas = [
      z.object({
        channel_id: z.string(),
        channel_name: z.union([z.string(), z.null()]).optional(),
        transcript: z.string(),
        type: z.literal('channel'),
      }),
      z.object({
        conversation: z.array(
          z.object({
            chat_id: z.string(),
            title: z.string(),
            messages: z.array(
              z.object({
                content: z.string(),
                date: z.string().datetime({ offset: true }),
                attachmentIds: z.array(z.string()),
              })
            ),
          })
        ),
        type: z.literal('chat'),
      }),
      z.object({
        formatted_preview: z.string(),
        type: z.literal('itemPreviews'),
      }),
    ];
    const errors = schemas.reduce<z.ZodError[]>(
      (errors, schema) =>
        ((result) => (result.error ? [...errors, result.error] : errors))(
          schema.safeParse(x)
        ),
      []
    );
    if (schemas.length - errors.length !== 1) {
      ctx.addIssue({
        path: ctx.path,
        code: 'invalid_union',
        unionErrors: errors,
        message: 'Invalid input: Should pass single schema',
      });
    }
  }),
});
